<template>
    <main class="timesheet-page" :class="{'timesheet-page--compact': !reviewMode && !isHistoricalSheet, 'timesheet-page--review': reviewMode}">
        <timesheet-edit-problems :api="gridApi" />
        <timesheet-cell-picker :api="gridApi" />
        <header class="timesheet-page__header">
            <div class="timesheet-page__title">
                <div class="timesheet-page__heading">
                    <h1>{{ reviewMode ? 'Проверка табелей' : 'Табель' }}</h1>
                    <b-badge v-if="sheet && !reviewMode" :variant="statusVariant" class="timesheet-status-badge timesheet-page__status">
                        {{ statusLabel }}
                    </b-badge>
                </div>
                <timesheet-sync-status v-if="!reviewMode" :api="gridApi" />
            </div>
            <div class="timesheet-page__header-actions">
                <b-button
                    v-if="sheet && !reviewMode"
                    class="timesheet-header-action timesheet-header-action--share"
                    size="sm"
                    variant="outline-primary"
                    :disabled="sharingTimesheetLink"
                    title="Скопировать или отправить ссылку на этот табель"
                    aria-label="Ссылка на табель"
                    @click="shareTimesheetLink"
                >
                    <b-spinner v-if="sharingTimesheetLink" small></b-spinner>
                    <i v-else class="fas fa-link" aria-hidden="true"></i>
                </b-button>
                <b-button
                    v-if="bootstrapData.permissions && bootstrapData.permissions.can_view_review"
                    class="timesheet-header-action timesheet-header-action--review"
                    size="sm"
                    :variant="reviewMode ? 'primary' : 'outline-primary'"
                    @click="toggleReviewMode"
                >
                    <i class="fas fa-clipboard-check mr-1" aria-hidden="true"></i>
                    {{ reviewMode ? 'К табелям' : bootstrapData.permissions.can_review ? 'Проверка ОК' : 'Свод табелей' }}
                </b-button>
                <b-button
                    v-if="bootstrapData.permissions && (
                        bootstrapData.permissions.can_view_payroll
                        || bootstrapData.permissions.can_manage_payroll
                        || bootstrapData.permissions.can_pay_salary
                    )"
                    class="timesheet-header-action timesheet-header-action--payroll"
                    size="sm"
                    variant="outline-success"
                    :to="payrollPeriodLink"
                >
                    <i class="fas fa-money-check-alt mr-1" aria-hidden="true"></i>
                    Зарплата
                </b-button>
                <b-dropdown v-if="bootstrapData.permissions && (bootstrapData.permissions.can_manage_settings || bootstrapData.permissions.can_import_history || bootstrapData.permissions.can_resolve_history_people)"
                    text="Ещё" size="sm" variant="outline-secondary" right toggle-class="timesheet-header-more">
                    <b-dropdown-item v-if="bootstrapData.permissions.can_manage_settings" to="/timesheets/settings"><i class="fas fa-cog mr-2" aria-hidden="true"></i>Настройки табелей</b-dropdown-item>
                    <b-dropdown-item v-if="bootstrapData.permissions.can_import_history" to="/timesheets/import"><i class="fas fa-file-import mr-2" aria-hidden="true"></i>Перенос истории</b-dropdown-item>
                    <b-dropdown-item v-if="bootstrapData.permissions.can_resolve_history_people" to="/timesheets/history-people"><i class="fas fa-users mr-2" aria-hidden="true"></i>Сотрудники архива</b-dropdown-item>
                </b-dropdown>
            </div>
        </header>

        <b-alert v-if="errorMessage" show variant="danger" dismissible @dismissed="errorMessage = ''">
            {{ errorMessage }}
        </b-alert>
        <b-alert v-if="successMessage" show variant="success" dismissible @dismissed="successMessage = ''">
            {{ successMessage }}
        </b-alert>

        <WorkPeriodBar :month="periodMonth" :mode="periodModeLabel" :context="reviewMode || isHistoricalSheet ? periodObjectLabel : ''"
            :loading="periodTransition || periodRefreshing || reviewLoading || reviewGridLoading" :disabled="periodNavigationLocked" :error="periodNavigationError"
            @change="changePeriodMonth" @refresh="refreshPeriod">
            <b-button v-if="periodOrigin === 'payroll'" size="sm" :to="{path: '/payroll', query: {month: periodMonth}}">К зарплате</b-button>
            <b-button v-if="sheet && periodOrigin === 'review' && !reviewMode" size="sm" :disabled="periodNavigationLocked" @click="toggleReviewMode">К своду</b-button>
        </WorkPeriodBar>
        <b-alert v-if="reviewFocusMessage && sheet && !reviewMode" show variant="warning">{{ reviewFocusMessage }}</b-alert>
        <b-alert v-if="plannedArrivalLinkMessage" show variant="info" dismissible @dismissed="plannedArrivalLinkMessage = ''">{{ plannedArrivalLinkMessage }}</b-alert>
        <b-alert v-if="periodMissing && !periodTransition" show variant="info">Табель за этот месяц ещё не начат.<template v-if="$route.query.arrival_key"> Чтобы подтвердить прибытие, сначала начните месяц в нужном табеле.</template></b-alert>
        <TimesheetSubmissionDeadline v-if="sheet && !reviewMode && !periodTransition"
            :key="sheet.timesheet.id" :value="sheet.submission_deadline" :timesheet-id="sheet.timesheet.id" compact
            @change="applySubmissionDeadline" @access-lost="clearCurrentTimesheet" />
        <section v-if="reviewMode && !periodTransition" class="timesheet-review" :aria-busy="periodRefreshing ? 'true' : 'false'" :inert="periodRefreshing ? '' : null">
            <b-alert v-if="(periodSummary && periodSummary.scope === 'accessible') || (reviewGrid && reviewGrid.scope === 'accessible')" show variant="info">Данные, итоги и замечания рассчитаны только по доступным вам объектам.</b-alert>
            <div class="review-view-tabs" aria-label="Вид проверки">
                <div class="review-view-tabs__choices" role="group" aria-label="Представление табелей">
                    <button type="button" :aria-pressed="reviewViewState.view === 'grid' ? 'true' : 'false'" :disabled="periodNavigationLocked" @click="changeReviewView('grid')">Свод по сотрудникам</button>
                    <button type="button" :aria-pressed="reviewViewState.view === 'days' ? 'true' : 'false'" :disabled="periodNavigationLocked" @click="changeReviewView('days')">Свод по дням</button>
                    <button type="button" :aria-pressed="reviewViewState.view === 'readiness' ? 'true' : 'false'" :disabled="periodNavigationLocked" @click="changeReviewView('readiness')">{{ bootstrapData.permissions.can_review ? 'Готовность комплекта' : 'Статусы подачи' }}</button>
                </div>
                <div v-if="reviewViewState.view !== 'readiness' && !isHistoricalReviewPeriod" class="review-drafts-control">
                <button type="button" role="switch" class="review-drafts-switch"
                    :aria-checked="reviewViewState.includeDrafts ? 'true' : 'false'" aria-label="Включить черновики"
                    :disabled="periodNavigationLocked" @click="changeReviewDrafts(!reviewViewState.includeDrafts)">
                    <span class="review-drafts-switch__track" aria-hidden="true"></span>
                    <span>Черновики <strong>{{ reviewViewState.includeDrafts ? 'Включены' : 'Выключены' }}</strong></span>
                </button>
                <TimesheetReviewDraftsHelp :visit-counted="reviewDraftsHelpCounted" @visit="reviewDraftsHelpCounted = true" />
                </div>
            </div>
            <template v-if="reviewViewState.view !== 'readiness'">
                <p v-if="reviewGridLoading && !reviewGrid" role="status">Загружаем свод табелей…</p>
                <b-alert v-if="reviewGridError" show variant="danger">{{ reviewGridError }} <b-button size="sm" @click="loadReviewGrid">Повторить загрузку</b-button></b-alert>
                <TimesheetReviewGrid v-if="reviewGrid" ref="reviewGrid" :key="`${periodMonth}:${reviewViewState.kind}:${reviewViewState.view}`" :view="reviewViewState.view"
                    :aria-busy="reviewGridLoading ? 'true' : 'false'" :inert="reviewGridLoading ? '' : null"
                    :grid="reviewGrid" :generated-at="reviewGridGeneratedAt" :filters="reviewViewState.filters" :position="reviewViewState" :source-link="reviewGridSourceLink"
                    @drafts="changeReviewDrafts" @reset-filters="resetReviewFilters" @filters="changeReviewFilters" @position="saveReviewView" @open="saveReviewView()" @open-employee="openEmployeeInspector" />
            </template>
            <section v-else class="review-readiness" aria-label="Готовность комплекта">
            <div v-if="periodSummary" class="review-readiness__overview">
                <div class="review-readiness__progress">
                    <span class="review-readiness__eyebrow">{{ isHistoricalReviewPeriod ? 'Архив за месяц' : 'Комплект за месяц' }}</span>
                    <strong v-if="isHistoricalReviewPeriod">{{ periodSummary.expected_count }} <span>исходных табелей</span></strong>
                    <strong v-else>{{ periodSummary.approved_count }} <span>из {{ periodSummary.expected_count }} табелей готовы</span></strong>
                    <p v-if="periodSummary.blocker_count" class="review-readiness__blocker-count">{{ periodSummary.blocker_count }} {{ pluralize(periodSummary.blocker_count, 'причина', 'причины', 'причин') }} мешают передаче</p>
                    <p v-else-if="periodSummary.can_handoff && !isHistoricalReviewPeriod">Комплект проверен и готов к передаче</p>
                    <p v-else-if="!isHistoricalReviewPeriod && !periodSummary.expected_count && !periodSummary.package_outside_view_count">Нет табелей для передачи</p>
                    <p v-else-if="isHistoricalReviewPeriod">Исходные файлы сохранены раздельно · только чтение</p>
                </div>
                <div class="review-readiness__metrics" aria-label="Состояние объектов">
                    <div><strong>{{ reviewReadinessCounts.all }}</strong><span>объектов</span></div>
                    <div :class="{'needs-action': reviewReadinessCounts.actionable}"><strong>{{ reviewReadinessCounts.actionable }}</strong><span>требуют действий</span></div>
                    <div><strong>{{ reviewReadinessCounts.ready }}</strong><span>{{ isHistoricalReviewPeriod ? 'с табелями' : 'готовы' }}</span></div>
                    <div><strong>{{ reviewReadinessCounts.quiet }}</strong><span>без данных / исключены</span></div>
                </div>
                <div class="review-readiness__handoff">
                <b-button
                    v-if="periodSummary.permissions.can_review && !isHistoricalReviewPeriod"
                    variant="success"
                    :disabled="!periodSummary.can_handoff || handoffPending"
                    :title="periodSummary.can_handoff
                        ? 'Зафиксировать комплект и передать расчётчику зарплаты'
                        : 'Сначала устраните все причины по объектам'"
                    @click="handoffReviewPeriod"
                >
                    <b-spinner v-if="handoffPending" small></b-spinner>
                    <i v-else class="fas fa-share-square mr-1" aria-hidden="true"></i>
                    Передать на расчёт ЗП
                </b-button>
                </div>
            </div>
            <details class="review-readiness__scope"><summary>Какие объекты входят в список</summary>
                <p>Показаны объекты, действовавшие хотя бы часть выбранного месяца. Наличие отчётов не влияет на этот список.</p>
                <p v-if="periodSummary && periodSummary.activity_current_fallback_count">Для объектов без истории переключения «Действующий» используется текущее состояние. Таких объектов: {{ periodSummary.activity_current_fallback_count }}.</p>
                <p v-if="!isHistoricalReviewPeriod">Здесь показана текущая сверка со сменными отчётами. Замечания к поданным версиям — в своде по сотрудникам.</p>
            </details>
            <b-alert v-if="periodSummary && bootstrapData.permissions.can_review && (periodSummary.package_outside_view_count || periodSummary.package_outside_view_blocker_count)" show variant="info">
                В общем комплекте учитываются также табели объектов вне этого списка: {{ periodSummary.package_outside_view_count }}.
                Причин, мешающих передаче по объектам вне списка: {{ periodSummary.package_outside_view_blocker_count }}.
                Их табели доступны в своде по сотрудникам и через выбор объекта.
            </b-alert>
            <div v-if="!reviewLoading && periodSummary && !periodSummary.items.length" class="timesheet-review__empty">
                Нет доступных объектов, действовавших в выбранном месяце.
            </div>
            <div class="review-readiness__filters">
                <div class="review-readiness__filter-fields">
                    <label><span>Объект</span><b-form-input v-model="reviewObjectSearch" type="search" placeholder="Найти по названию" aria-label="Поиск по объекту" /></label>
                    <label><span>Статус подачи</span><b-form-select v-model="reviewStatusFilter" aria-label="Статус подачи" :options="reviewStatusOptions" /></label>
                    <button type="button" class="review-readiness__attention-toggle" :aria-pressed="reviewAttentionOnly ? 'true' : 'false'" @click="reviewAttentionOnly = !reviewAttentionOnly"><i class="fas fa-exclamation-circle" aria-hidden="true"></i> Требуют действий <strong>{{ reviewReadinessCounts.actionable }}</strong></button>
                </div>
                <div v-if="reviewReadinessFilterCount" class="review-readiness__active-filters" aria-label="Включены фильтры готовности">
                    <strong><i class="fas fa-filter" aria-hidden="true"></i> Включены фильтры: {{ reviewReadinessFilterCount }}</strong>
                    <button v-if="reviewObjectSearch.trim()" type="button" @click="reviewObjectSearch = ''" :aria-label="'Снять поиск: ' + reviewObjectSearch">{{ reviewObjectSearch }} ×</button>
                    <button v-if="reviewStatusFilter" type="button" @click="reviewStatusFilter = ''" :aria-label="'Снять статус: ' + reviewReadinessStatusLabel">{{ reviewReadinessStatusLabel }} ×</button>
                    <button v-if="reviewAttentionOnly" type="button" @click="reviewAttentionOnly = false" aria-label="Снять фильтр: требуют действий">Требуют действий ×</button>
                    <button type="button" class="review-readiness__reset" @click="reviewObjectSearch = ''; reviewStatusFilter = ''; reviewAttentionOnly = false">Сбросить все</button>
                </div>
            </div>
            <div class="review-readiness__list-caption"><strong>{{ filteredReviewObjects.length }} из {{ reviewReadinessCounts.all }} объектов</strong><span>Сначала требующие действий · исключённые в конце</span></div>
            <p v-if="reviewLoading && !periodSummary" role="status">Загружаем готовность комплекта…</p>
            <p v-if="periodSummary && periodSummary.items.length && !filteredReviewObjects.length">Нет объектов по выбранным фильтрам.</p>
            <div class="timesheet-review__list">
                <section v-for="section in reviewReadinessSections" :key="section.key" class="review-readiness__section" :class="'review-readiness__section--' + section.tone">
                <header class="review-readiness__section-heading"><h2>{{ section.label }}</h2><span>{{ section.groups.length }}</span></header>
                <div
                    v-for="group in section.groups" :key="`review-object-${group.building_id}`" class="timesheet-review__object"
                >
                    <strong>{{ group.object_name }}</strong>
                    <span v-if="!group.is_active" class="d-inline-flex align-items-center ml-2">
                        <b-badge variant="secondary">Объект перенесён в архив</b-badge>
                        <b-button
                            v-b-tooltip.hover.focus
                            variant="link"
                            size="sm"
                            class="p-0 ml-1"
                            aria-label="Почему архивный объект показан в своде"
                            title="Сейчас в рабочем процессе объекта выключен признак «Действующий». Объект остаётся в своде, потому что был действующим хотя бы часть выбранного месяца. Перенос в архив сам по себе не означает, что все табели поданы и проверены."
                        ><i class="far fa-question-circle" aria-hidden="true"></i></b-button>
                    </span>
                    <div class="review-readiness__owners" title="Текущие ответственные объекта">
                        <span>Прораб:
                            <EmployeeLink v-if="group.foreman" :user-id="group.foreman.user_id" :fallback="group.foreman.name" compact open-in-panel />
                            <span v-else class="text-muted">Не назначен</span>
                        </span>
                        <span>Куратор:
                            <EmployeeLink v-if="group.curator" :user-id="group.curator.user_id" :fallback="group.curator.name" compact open-in-panel />
                            <span v-else class="text-muted">Не назначен</span>
                        </span>
                    </div>
                <article v-for="item in group.items"
                    :key="`period-object-${item.building_id}-${item.timesheet_id || 0}`"
                    :class="{'has-blockers': item.blockers && item.blockers.length}"
                >
                    <div>

                        <strong v-if="item.name">{{ item.name }}</strong>
                        <a v-if="historicalSourceUrl(item)" :href="historicalSourceUrl(item)" target="_blank" rel="noopener noreferrer">Исходная таблица</a>
                        <span v-if="item.excluded_by_settings">{{ packageExclusionText(item) }}</span>
                        <span v-else-if="item.excluded_by_review">Исключён проверяющим из комплекта этого месяца. Данные табеля не входят в расчёт.</span>
                        <span v-else-if="item.ignored_empty && item.timesheet_id && !item.required_submission_missing">{{ !item.expected_by_reports && item.status === 'draft'
                            ? 'Нет сменных отчётов и заполненных данных. Черновик не блокирует передачу и не входит в расчёт.'
                            : 'Пустой табель — не блокирует расчёт.' }}</span>
                        <span v-else-if="item.needs_package_decision && item.report_reconciliation && item.report_reconciliation.required">Сменных отчётов нет, но в черновике есть заполненные данные. Требуется решение проверяющего.</span>
                        <span v-else-if="item.required_submission_missing">Обязательному объекту нужен хотя бы один поданный и утверждённый табель.</span>
                        <span v-else-if="item.report_reconciliation">
                            {{ reportSummaryLabel(item.report_reconciliation) }}
                        </span>
                        <span v-else-if="!item.excluded_by_settings && item.status === 'no_data'">{{ isHistoricalReviewPeriod
                            ? 'За выбранный месяц нет сохранённого исторического табеля по этому объекту.'
                            : 'За выбранный месяц нет табеля и сменных отчётов. Аннулированные записи не учитываются.' }}
                            Объект показан, потому что был действующим в этом месяце.
                        </span>
                        <span v-else-if="!item.excluded_by_settings && !item.timesheet_id && item.status !== 'no_data'">{{ item.expected_by_reports
                            ? 'Есть отчёты, табель ещё не подан'
                            : 'Обязательный табель ещё не подан' }}</span>
                        <ul v-if="item.blockers && item.blockers.length" class="timesheet-review__blockers">
                            <li v-for="(blocker, index) in item.blockers" :key="`${item.building_id}-blocker-${index}`">
                                {{ blocker }}
                            </li>
                        </ul>
                        <div v-if="!item.excluded_by_settings && item.draft_content && (item.needs_package_decision || item.excluded_by_review)" class="small">
                            Дней с записями: {{ item.draft_content.filled_days }}; записей сотрудников: {{ item.draft_content.entry_count }}.
                            КТУ/основания: {{ item.draft_content.has_ktu ? 'есть' : 'нет' }};
                            характеристики: {{ item.draft_content.has_characteristics ? 'есть' : 'нет' }}.
                        </div>
                        <div v-if="!item.excluded_by_settings && item.package_decision && item.package_decision.action === 'exclude'" class="small mt-1">
                            {{ item.package_decision.actor_name }} · {{ packageDecisionTime(item.package_decision.occurred_at) }}.
                            Причина: {{ item.package_decision.reason }}
                            <strong v-if="item.package_decision_stale" class="d-block text-danger">Решение устарело: требуется повторная проверка.</strong>
                        </div>
                        <div v-if="item.required_by_settings && !item.excluded_by_settings && (item.needs_package_decision || item.excluded_by_review)" class="small mt-1">
                            Исключение этого черновика не отменяет требование подать и утвердить хотя бы один табель по обязательному объекту.
                        </div>
                    </div>
                    <div class="timesheet-review__state">
                        <b-badge v-if="item.excluded_by_settings" class="timesheet-status-badge" variant="secondary">Исключён настройками</b-badge>
                        <b-badge v-if="item.excluded_by_review" variant="secondary">Исключён проверяющим</b-badge>
                        <b-badge v-if="!item.excluded_by_settings || item.timesheet_id" class="timesheet-status-badge" :variant="reviewStatusVariant(item.status)">
                            {{ statusText(item.status) }}
                        </b-badge>
                        <b-badge class="timesheet-status-badge" v-if="item.required_by_settings && !item.excluded_by_settings" variant="info">Обязательный табель</b-badge>
                        <b-button
                            v-if="item.timesheet_id"
                            size="sm"
                            variant="outline-primary"
                            :href="reviewSheetLink(item.timesheet_id)"
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Открыть табель в новой вкладке"
                        >
                            Открыть табель
                        </b-button>
                        <b-button v-if="item.can_exclude_from_package" size="sm" variant="outline-secondary"
                            @click="openPackageDecision(item, 'exclude')">Исключить из комплекта за {{ periodMonth }}</b-button>
                        <b-button v-if="item.can_cancel_package_decision" size="sm" variant="outline-secondary"
                            @click="openPackageDecision(item, 'cancel')">Отменить исключение</b-button>
                    </div>
                </article>
                </div>
                </section>
            </div>
            </section>
        </section>

        <section v-else-if="!reviewMode" class="timesheet-selector" :class="{'timesheet-selector--multiple': selectedObjectMultiple}">
            <div class="timesheet-selector__object-field">
                <label for="timesheet-object-input"><span>Объект</span></label>
                <div class="timesheet-object-picker" @focusout="handleObjectPickerFocusOut">
                    <b-form-input
                        id="timesheet-object-input"
                        ref="objectPickerInput"
                        v-model="objectQuery"
                        autocomplete="off"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-controls="timesheet-object-options"
                        :aria-expanded="objectPickerOpen ? 'true' : 'false'"
                        placeholder="Выберите объект — начните вводить название"
                        :disabled="loading"
                        @focus="openObjectPicker"
                        @click="openObjectPicker"
                        @input="handleObjectQueryInput"
                        @keydown.esc="objectPickerOpen = false"
                        @keydown.down.prevent="objectPickerOpen = true"
                    ></b-form-input>
                    <button
                        type="button"
                        class="timesheet-object-picker__toggle"
                        :aria-label="objectPickerOpen ? 'Закрыть список объектов' : 'Открыть список объектов'"
                        :disabled="loading"
                        @mousedown.prevent
                        @click="toggleObjectPicker"
                    >
                        <i :class="objectPickerOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" aria-hidden="true"></i>
                    </button>
                    <div
                        v-if="objectPickerOpen"
                        id="timesheet-object-options"
                        class="timesheet-object-picker__menu"
                        role="listbox"
                    >
                        <button
                            v-for="option in filteredObjectInputOptions"
                            :key="`object-option-${option.value}`"
                            type="button"
                            class="timesheet-object-picker__option"
                            :class="{'is-selected': Number(buildingId) === Number(option.value)}"
                            role="option"
                            :aria-selected="Number(buildingId) === Number(option.value) ? 'true' : 'false'"
                            @mousedown.prevent
                            @click="selectPeriodObject(option)"
                        >
                            <strong>{{ option.text }}</strong>
                            <small v-if="option.hint && option.hint !== option.text">{{ option.hint }}</small>
                        </button>
                        <div v-if="!filteredObjectInputOptions.length" class="timesheet-object-picker__empty">
                            Ничего не найдено. Измените запрос.
                        </div>
                    </div>
                </div>
            </div>
            <b-button
                variant="primary"
                v-if="!selectedObjectMultiple && periodMissing && !periodHistoricalObjects.length && editableObjects.some(object => Number(object.building_id) === Number(buildingId) && object.can_edit)"
                :disabled="!buildingId || !periodMonth || loading"
                @click="openTimesheet"
            >
                <b-spinner v-if="loading" small></b-spinner>
                <i v-else class="fas fa-calendar-check mr-1" aria-hidden="true"></i>
                {{ periodMissing ? 'Начать месяц' : 'Открыть табель' }}
            </b-button>
            <TimesheetStreams ref="namedTimesheets" v-if="selectedObjectMultiple && periodMonth && !isHistoricalSheet && !periodHistoricalObjects.length" :building-id="Number(buildingId)"
                :month="periodMonth" :can-create="selectedObjectMultiple.can_edit"
                :current-stream-id="selectedPeriodStreamId" :disabled="periodNavigationLocked || periodTransition"
                @select="selectPeriodStream"
                @busy="streamMutationPending = $event"
                @open="openNamedTimesheet" @updated="sheet && openNamedTimesheet(sheet.timesheet.id)" />
        </section>

        <b-alert v-if="loaded && !availableObjects.length" show variant="warning">
            Нет объектов, доступных для табелирования или просмотра истории за этот месяц.
        </b-alert>

        <b-alert v-if="employeeFocusMessage && !periodTransition" show variant="warning" role="alert">{{ employeeFocusMessage }}</b-alert>
        <TimesheetHistoryView v-if="isHistoricalSheet && !reviewMode && !periodTransition" :sheet="sheet" :sources="historicalSheets" :focused-line-ids="employeeFocusedLineIds"
            :disabled="periodNavigationLocked" @select="openNamedTimesheet" @open-employee="openEmployeeInspector" />

        <template v-if="sheet && !isHistoricalSheet && !reviewMode && !periodTransition">
            <TimesheetTextQualityNotice :issues="textQualityIssues" :editable="canEdit" @fix="openTextQualityIssue" />
            <section
                v-if="unresolvedLines.length"
                class="timesheet-unresolved-blocker"
                role="alert"
                aria-live="assertive"
            >
                <div class="timesheet-unresolved-blocker__title">
                    <i class="fas fa-user-times" aria-hidden="true"></i>
                    <div>
                        <strong>ТАБЕЛЬ НЕЛЬЗЯ ОТПРАВИТЬ: есть люди без карточки сотрудника</strong>
                        <span>
                            Это кадровый инцидент. Сопоставьте каждую ручную строку с карточкой;
                            часы и КТУ не пропадут.
                        </span>
                    </div>
                </div>
                <div class="timesheet-unresolved-blocker__people">
                    <article v-for="line in unresolvedLines" :key="`unresolved-blocker-${line.id}`">
                        <div>
                            <strong>{{ line.employee_name_snapshot }}</strong>
                            <span v-if="line.resolution_suggestion">
                                Возможно, это <b>{{ line.resolution_suggestion.employee_name }}</b>
                            </span>
                            <span v-else>Нужно найти и подтвердить карточку сотрудника</span>
                        </div>
                        <b-button
                            variant="danger"
                            class="timesheet-unresolved-attention"
                            v-timesheet-attention
                            :disabled="!canResolveUnresolved"
                            @click="openUnresolvedResolver(line)"
                        >
                            <i class="fas fa-link mr-1" aria-hidden="true"></i>
                            Сопоставить сейчас
                        </b-button>
                    </article>
                </div>
                <small v-if="!canResolveUnresolved">
                    Табель сейчас нельзя редактировать. Откройте режим исправления, чтобы выполнить сопоставление.
                </small>
            </section>

            <section
                v-if="activeUnresolvedMerges.length"
                class="timesheet-unresolved-undo"
                aria-live="polite"
            >
                <article v-for="merge in activeUnresolvedMerges" :key="`unresolved-merge-${merge.audit_event_id}`">
                    <span>
                        Объединено: <b>{{ merge.unresolved_name }}</b> → <b>{{ merge.employee_name }}</b>
                    </span>
                    <b-button
                        size="sm"
                        variant="outline-secondary"
                        :disabled="!canResolveUnresolved || unresolvedUndoPendingId !== null"
                        @click="undoUnresolvedMerge(merge)"
                    >
                        <b-spinner v-if="unresolvedUndoPendingId === merge.audit_event_id" small></b-spinner>
                        <span v-else>Отменить объединение</span>
                    </b-button>
                </article>
            </section>

            <TimesheetEmptyLines :key="'empty-top-' + sheet.timesheet.id" :sheet="sheet" :lines="emptySubmissionLines" :can-edit="canEdit" :prepare="flushTimesheetEdits" :reload="reloadAfterEmptyLines" @busy="rosterExclusionPending = $event" @checked="setEmptyLines" @locate="locateEmptyLine" />
            <b-alert v-if="!reportReconciliationRequired" show variant="secondary">
                Сверка с ежесменными отчётами для этого объекта не требуется.
            </b-alert>
            <b-alert
                v-if="reportMismatchDays.length"
                show
                variant="danger"
                class="timesheet-report-blocker"
            >
                <details>
                <summary><strong>Табель нельзя отправить: расхождения с отчётами за {{ reportMismatchDays.length }}
                    {{ pluralize(reportMismatchDays.length, 'день', 'дня', 'дней') }}.</strong> Подробнее</summary>
                <p>Количество рабочих не совпадает с ежесменными отчётами. ИТР в сверку не входят. Исправьте табель либо аннулируйте неверный отчёт и подайте новый.</p>
                <ul>
                    <li v-for="day in reportMismatchDays" :key="`mismatch-${day.date}`">
                        <strong>{{ shortDate(day.date) }}</strong>:
                        по отчётам {{ day.report_workers }}, в табеле {{ day.timesheet_workers }}
                        <template v-if="day.report_ids && day.report_ids.length">
                            · отчёты № <TimesheetReportLinks :ids="day.report_ids" />
                        </template>
                    </li>
                </ul>
                </details>
            </b-alert>

            <section
                v-if="absenceSuggestions.length || absenceDiscrepancies.length || absenceWarningGroups.length"
                class="timesheet-absence-assistant"
                aria-live="polite"
            >
                <div class="timesheet-absence-assistant__summary">
                    <div>
                        <i class="fas fa-user-clock" aria-hidden="true"></i>
                        <span>
                            <strong v-if="absenceSuggestions.length">
                                Кадрами зарегистрировано {{ absenceSuggestions.length }}
                                {{ pluralize(absenceSuggestions.length, 'отметка', 'отметки', 'отметок') }}
                                для {{ absencePreviewGroups.length }}
                                {{ pluralize(absencePreviewGroups.length, 'сотрудника', 'сотрудников', 'сотрудников') }}
                            </strong>
                            <strong v-else>Кадровые отсутствия сопоставлены</strong>
                            <small v-if="absenceDiscrepancies.length">
                                {{ absenceDiscrepancies.length }}
                                {{ pluralize(absenceDiscrepancies.length, 'расхождение', 'расхождения', 'расхождений') }}
                            </small>
                        </span>
                    </div>
                    <div class="timesheet-absence-assistant__actions">
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            @click="absencePreviewOpen = !absencePreviewOpen"
                        >
                            {{ absencePreviewOpen ? 'Скрыть' : 'Просмотреть' }}
                        </b-button>
                        <b-button
                            v-if="canEdit && absenceSuggestions.length"
                            size="sm"
                            variant="primary"
                            :disabled="absenceApplying"
                            @click="applyAbsenceSuggestions()"
                        >
                            <b-spinner v-if="absenceApplying" small></b-spinner>
                            <span v-else>Проставить {{ absenceSuggestions.length }}</span>
                        </b-button>
                    </div>
                </div>
                <div v-if="absencePreviewOpen" class="timesheet-absence-assistant__preview">
                    <article
                        v-for="group in absencePreviewGroups"
                        :key="`absence-preview-${group.line.id}`"
                    >
                        <strong>{{ group.line.employee_name_snapshot }}</strong>
                        <span>{{ group.days.map(absenceDayLabel).join(', ') }}</span>
                    </article>
                    <article
                        v-for="item in absenceDiscrepancies"
                        :key="`absence-discrepancy-${item.line.id}-${item.day.work_date}`"
                        class="is-warning"
                    >
                        <strong>{{ item.line.employee_name_snapshot }} · {{ shortDate(item.day.work_date) }}</strong>
                        <span>{{ item.day.message || absenceStateLabel(item.day) }}</span>
                        <div
                            v-if="canResolveAbsenceDiscrepancy(item.day)"
                            class="timesheet-absence-assistant__resolution"
                        >
                            <b-button
                                size="sm"
                                variant="outline-primary"
                                :disabled="absenceResolvingKey === absenceDiscrepancyKey(item)"
                                @click="resolveAbsenceDiscrepancy(item, 'sync')"
                            >
                                Синхронизировать
                            </b-button>
                            <b-button
                                size="sm"
                                variant="outline-secondary"
                                :disabled="absenceResolvingKey === absenceDiscrepancyKey(item)"
                                @click="resolveAbsenceDiscrepancy(item, 'keep')"
                            >
                                Оставить как есть
                            </b-button>
                        </div>
                    </article>
                    <article
                        v-for="group in absenceWarningGroups"
                        :key="`absence-warning-${group.line.id}`"
                        class="is-warning"
                    >
                        <strong>{{ group.line.employee_name_snapshot }}</strong>
                        <span>{{ group.messages.join(' ') }}</span>
                    </article>
                </div>
            </section>

            <div v-if="absenceUndo" class="timesheet-absence-undo" role="status">
                <span>
                    Проставлено {{ absenceUndo.saved }}
                    {{ pluralize(absenceUndo.saved, 'отметка', 'отметки', 'отметок') }}
                </span>
                <b-button
                    size="sm"
                    variant="link"
                    :disabled="absenceApplying || !canEdit"
                    @click="undoAbsenceSuggestions"
                >
                    Отменить
                </b-button>
            </div>

            <b-button
                v-if="canEdit"
                class="timesheet-mobile-add-person"
                variant="primary"
                @click="openAddPerson"
            >
                <i class="fas fa-user-plus" aria-hidden="true"></i>
                Добавить сотрудника
            </b-button>

            <timesheet-assessment-status :api="gridApi" />

            <timesheet-copy-names v-if="isCompactViewport && !isMobileFullMonthView" class="mb-2" :names="filteredLines.map(line => line.employee_name_snapshot)" />

            <div class="timesheet-mobile-view-switch" role="group" aria-label="Режим заполнения табеля">
                <b-button
                    :variant="mobileMode === 'day' ? 'primary' : 'outline-primary'"
                    @click="mobileMode = 'day'"
                >
                    <i class="fas fa-calendar-day" aria-hidden="true"></i>
                    По дням
                </b-button>
                <b-button
                    :variant="mobileMode === 'month' ? 'primary' : 'outline-primary'"
                    @click="mobileMode = 'month'"
                >
                    <i class="fas fa-calendar-alt" aria-hidden="true"></i>
                    Месяц
                </b-button>
            </div>

            <div
                v-if="mobileMode === 'month'"
                class="timesheet-mobile-month-layout-switch"
                role="group"
                aria-label="Вид месяца на телефоне"
            >
                <button
                    type="button"
                    :class="{'is-active': mobileMonthLayout === 'weeks'}"
                    :aria-pressed="mobileMonthLayout === 'weeks' ? 'true' : 'false'"
                    @click="setMobileMonthLayout('weeks')"
                >
                    <i class="fas fa-calendar-week" aria-hidden="true"></i>
                    <span>
                        <strong>По неделям</strong>
                        <small>Удобно заполнять</small>
                    </span>
                </button>
                <button
                    type="button"
                    :class="{'is-active': mobileMonthLayout === 'full'}"
                    :aria-pressed="mobileMonthLayout === 'full' ? 'true' : 'false'"
                    @click="setMobileMonthLayout('full')"
                >
                    <i class="fas fa-table" aria-hidden="true"></i>
                    <span>
                        <strong>Весь месяц</strong>
                        <small>Все дни 1–31</small>
                    </span>
                </button>
            </div>

            <timesheet-employee-search v-if="isCompactViewport" :api="gridApi" />
            <div v-if="isCompactViewport && employeeSearchActive && !employeeSearchPending && !filteredLines.length"
                class="timesheet-employee-search-empty" role="status">
                В этом табеле сотрудник не найден.
                <button type="button" @click="resetEmployeeSearch">Показать всех</button>
            </div>

            <datalist id="timesheet-cell-suggestions">
                <option
                    v-for="suggestion in cellSuggestionOptions"
                    :key="`cell-suggestion-${suggestion.value}`"
                    :value="suggestion.value"
                    :label="suggestion.label"
                ></option>
            </datalist>

            <button
                id="timesheet-legend-button"
                type="button"
                class="timesheet-legend-button"
                aria-label="Открыть условные обозначения табеля"
            >
                <i class="fas fa-list-ul" aria-hidden="true"></i>
                <span>Обозначения</span>
            </button>
            <button
                v-if="isMobileFullMonthView"
                type="button"
                class="timesheet-mobile-month-context-fab"
                :class="{
                    'is-active': mobileNamesPinned,
                    'is-previewing': mobileNamesPreviewing
                }"
                :aria-label="mobileNamesPinned
                    ? 'ФИО закреплены. Нажмите, чтобы открепить; удерживайте для прозрачного просмотра'
                    : 'Нажмите, чтобы закрепить ФИО; удерживайте, чтобы временно показать их'"
                :aria-pressed="mobileNamesPinned ? 'true' : 'false'"
                :title="mobileNamesPinned ? 'Открепить ФИО' : 'Закрепить ФИО'"
                @pointerdown="startMobileNamesPreview"
                @pointerup="finishMobileNamesPreview"
                @pointercancel="finishMobileNamesPreview"
                @contextmenu.prevent
                @click="toggleMobileNamesContext"
            >
                <i class="fas fa-eye" aria-hidden="true"></i>
                <i
                    v-if="mobileNamesPinned && !mobileNamesPreviewing"
                    class="fas fa-thumbtack timesheet-mobile-month-context-fab__pin"
                    aria-hidden="true"
                ></i>
            </button>
            <aside
                v-if="isMobileFullMonthView && mobileNamesHintVisible"
                class="timesheet-mobile-month-context-hint"
                role="status"
            >
                <button
                    type="button"
                    aria-label="Больше не показывать эту подсказку"
                    @click="hideMobileNamesHint(true)"
                >×</button>
                <strong>ФИО в полном месяце</strong>
                <span>
                    Нажмите на глаз — закрепить или отпустить ФИО.
                    Удерживайте — временно показать столбец полупрозрачно.
                </span>
            </aside>
            <b-popover
                target="timesheet-legend-button"
                triggers="click blur"
                placement="auto"
                boundary="viewport"
                title="Условные обозначения"
                custom-class="timesheet-legend-popover"
            >
                <div class="timesheet-legend">
                    <div class="timesheet-legend__examples">
                        <div><code>8</code><span>8 часов работы</span></div>
                        <div><code>8 Д</code><span>8 часов + дорога</span></div>
                        <div><code>Л</code><span>простой из-за литерного рейса; только без часов</span></div>
                        <div><code>Дт</code><span>часы в другом табеле, рассчитываются системой</span></div>
                    </div>
                    <div
                        v-for="group in legendCodeGroups"
                        :key="`legend-${group.key}`"
                        class="timesheet-legend__group"
                    >
                        <strong>{{ group.label }}</strong>
                        <div v-for="code in group.codes" :key="`legend-code-${code.id}`">
                            <code>{{ code.code }}</code>
                            <span>{{ code.name }}</span>
                        </div>
                    </div>
                    <small>Можно вводить с пробелом или через слеш: <code>8 Д</code> и <code>8/Д</code>.</small>
                </div>
            </b-popover>

            <section
                v-if="isCompactViewport && mobileMode === 'day'"
                class="timesheet-day-view"
                :class="{'is-mobile-active': mobileMode === 'day'}"
            >
                <div class="timesheet-daybar">
                    <b-button variant="outline-secondary" size="sm" @click="moveDay(-1)">
                        <i class="fas fa-chevron-left" aria-hidden="true"></i>
                        <span class="sr-only">Предыдущий день</span>
                    </b-button>
                    <label>
                        <span>Рабочий день</span>
                        <b-form-input
                            v-model="selectedDate"
                            type="date"
                            :min="period.starts_on"
                            :max="period.ends_on"
                            @change="handleSelectedDateChange"
                        ></b-form-input>
                    </label>
                    <b-button variant="outline-secondary" size="sm" @click="moveDay(1)">
                        <i class="fas fa-chevron-right" aria-hidden="true"></i>
                        <span class="sr-only">Следующий день</span>
                    </b-button>
                    <div class="timesheet-daybar__bulk" v-if="canEdit">
                        <strong>{{ shortDate(selectedDate) }}</strong>
                        <span>{{ bulkRecipientLabel }}</span>
                        <b-form-input
                            v-model="bulkValue"
                            type="text"
                            inputmode="decimal"
                            list="timesheet-cell-suggestions"
                            autocomplete="off"
                            placeholder="8"
                            :aria-label="employeeSearchActive ? 'Отметка для найденных сотрудников' : 'Отметка для всех сотрудников'"
                        ></b-form-input>
                        <b-button variant="outline-primary" size="sm" :disabled="bulkFillDisabled" @click="applyBulkValue(captureTimesheetBulkAction())">
                            <i class="fas fa-check" aria-hidden="true"></i>
                            <span class="sr-only">{{ employeeSearchActive ? 'Проставить найденным сотрудникам' : 'Проставить всем' }}</span>
                        </b-button>
                        <span v-if="bulkError" role="alert" class="text-danger">{{ bulkError }}</span>
                        <timesheet-bulk-result :result="bulkResult" />
                    </div>
                    <b-button
                        v-if="canEdit"
                        class="timesheet-daybar__add"
                        variant="primary"
                        size="sm"
                        aria-label="Добавить сотрудника"
                        @click="openAddPerson"
                    >
                        <i class="fas fa-plus" aria-hidden="true"></i>
                        <span>Сотрудник</span>
                    </b-button>
                </div>

                <div class="timesheet-mobile-days" role="tablist" aria-label="Дни недели">
                    <button
                        v-for="day in selectedMobileWeek.days"
                        :key="`day-switch-${day.date}`"
                        type="button"
                        role="tab"
                        :aria-selected="day.date === selectedDate ? 'true' : 'false'"
                        :class="{
                            'is-active': day.date === selectedDate,
                            'review-source-day': day.date === reviewFocusedDate,
                            'is-weekend': day.is_weekend
                        }"
                        @click="openDay(day.date)"
                    >
                        <span>{{ day.weekday }}</span>
                        <strong>{{ day.day }}</strong>
                        <small>{{ minutesLabel(dayMinutes(day.date)) }}ч</small>
                    </button>
                </div>

                <div
                    class="timesheet-day-reconciliation"
                    :class="{'is-mismatch': !selectedDayReconciliation.matches}"
                >
                    <span v-if="reportReconciliationRequired">По отчётам <strong>{{ selectedDayReconciliation.report_workers }}</strong></span>
                    <i v-if="reportReconciliationRequired" class="fas fa-exchange-alt" aria-hidden="true"></i>
                    <span>В табеле <strong>{{ selectedDayReconciliation.timesheet_workers }}</strong></span>
                    <small v-if="reportReconciliationRequired && selectedDayReconciliation.report_ids.length">
                        отчёты № <TimesheetReportLinks :ids="selectedDayReconciliation.report_ids" />
                    </small>
                    <small v-if="!selectedDayReconciliation.matches">Нужно выровнять до отправки</small>
                </div>

                <section
                    class="timesheet-roster"
                    aria-live="polite"
                    data-timesheet-keyboard-grid
                >
                    <template v-for="(line, lineIndex) in filteredLines">
                        <h2
                            v-if="lineGroupStartsAt(lineIndex)"
                            :key="`day-group-${lineGroupKey(line)}`"
                            class="timesheet-line-group-heading"
                        >
                            {{ lineGroupLabel(line) }}
                        </h2>
                    <article
                        :key="`day-line-${line.id}`"
                        :data-review-line-id="line.id"
                        class="timesheet-person"
                        :class="{
                            'review-source-row': Number(line.id) === reviewFocusedLineId,
                            'employee-focused-row': isEmployeeFocused(line),
                            'timesheet-person--external': externalForDate(line).length,
                            'timesheet-person--unresolved': line.unresolved_person_id
                        }"
                    >
                        <div class="timesheet-person__identity">
                            <div class="timesheet-person__avatar" aria-hidden="true">
                                {{ employeeInitials(line) }}
                            </div>
                            <div class="timesheet-person__name">
                                <div>
                                    <button
                                        v-if="line.employee_id"
                                        type="button"
                                        class="timesheet-employee-link"
                                        :title="assignmentPersonTitle(line, selectedDate)"
                                        @click="openEmployeeInspector(line)"
                                    >
                                        {{ lineEmployeeName(line) }}
                                    </button>
                                    <strong v-else>{{ lineEmployeeName(line) }}</strong>
                                    <button type="button" class="timesheet-select-month" :aria-label="`Выделить весь месяц: ${line.employee_name_snapshot}`" title="Выделить весь месяц" @click.stop="selectEmployeeMonth(line, $event)"><i class="far fa-calendar-alt" aria-hidden="true"></i></button>
                                    <button
                                        v-if="line.unresolved_person_id"
                                        type="button"
                                        class="timesheet-unresolved-line-action"
                                        v-timesheet-attention
                                        :disabled="!canResolveUnresolved"
                                        @click.stop="openUnresolvedResolver(line)"
                                    >
                                        <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                                        Нет карточки — сопоставить
                                    </button>
                                    <timesheet-row-actions v-if="canExcludeRosterLine(line)" :name="line.employee_name_snapshot || lineEmployeeName(line)" :disabled="rosterExclusionPending || editPendingCount > 0" @exclude="openRosterExclusion(line)" />
                                    <span
                                        v-if="lineIssueGroups(line).length"
                                        class="timesheet-issue-rail"
                                        aria-label="Проблемы по сотруднику"
                                    >
                                        <button
                                            v-for="group in visibleLineIssueGroups(line)"
                                            :key="`day-person-issue-${line.id}-${group.key}`"
                                            type="button"
                                            class="timesheet-issue-icon"
                                            :class="issueCueClasses(group)"
                                            :aria-label="issueGroupAriaLabel(group)"
                                            v-b-tooltip.hover.focus.top="hasSourceIssue([group]) ? '' : issueGroupTooltip(group)"
                                            @mouseenter="showSourcePopover($event, line, [group])"
                                            @focus="showSourcePopover($event, line, [group])"
                                            @keydown.down.prevent="showSourcePopover($event, line, [group], true)"
                                            @keydown.esc.stop="closeSourcePopover()"
                                            @click.stop="openIssueGroup(line, group)"
                                        >
                                            <i :class="group.icon" aria-hidden="true"></i>
                                            <small v-if="group.count > 1">{{ group.count }}</small>
                                        </button>
                                        <button
                                            v-if="lineIssueGroups(line).length > 3"
                                            type="button"
                                            class="timesheet-issue-icon timesheet-issue-icon--more"
                                            :aria-label="`Ещё ${lineIssueGroups(line).length - 3} вида проблем`"
                                            v-b-tooltip.hover.focus.top="lineIssueOverflowTooltip(line)"
                                            @click.stop="openIssueGroup(line, lineIssueGroups(line)[3])"
                                        >
                                            +{{ lineIssueGroups(line).length - 3 }}
                                        </button>
                                    </span>
                                </div>
                                <small v-if="line.can_remove_manual">
                                    Добавлен вручную<span v-if="line.manual_add_reason">: {{ line.manual_add_reason }}</span>
                                </small>
                                <button
                                    v-if="canRemoveManualLine(line)"
                                    type="button"
                                    class="timesheet-manual-line-remove"
                                    :disabled="removingLineId !== null"
                                    @click.stop="removeManualLine(line)"
                                >
                                    <b-spinner v-if="removingLineId === Number(line.id)" small></b-spinner>
                                    <i v-else class="fas fa-trash-alt" aria-hidden="true"></i>
                                    Удалить из табеля
                                </button>
                            </div>
                        </div>

                        <timesheet-assessment-link
                            :incomplete="Boolean(assessmentIssuesForLine(line, false).length)"
                            :status="mobileAssessmentStatus(line)"
                            @click="openAssessment(line)"
                        ></timesheet-assessment-link>

                        <div
                            class="timesheet-person__entry"
                            :data-line-id="line.id"
                            :data-work-date="selectedDate"
                            :class="[presenceCellClass(entryPresenceKey(line.id, selectedDate)), reviewSourceCellClass(line, selectedDate)]"
                            :style="presenceCellStyle(entryPresenceKey(line.id, selectedDate))"
                        >
                            <label>
                                <span>Отметка за день</span>
                                <div class="timesheet-mobile-combobox">
                                    <timesheet-entry-input v-if="canEdit"
                                        :api="gridApi" :line="line" :date="selectedDate"
                                        class="form-control"
                                        placeholder="Например: 8, Д, Л или О"
                                    />
                                    <timesheet-read-cell v-else :api="gridApi" :line="line" :date="selectedDate" />
                                    <button
                                        type="button"
                                        class="timesheet-mobile-combobox__toggle"
                                        aria-label="Показать варианты отметки"
                                        :disabled="!canEdit"
                                        @mousedown.prevent
                                        @click="openCellSuggestions(line, selectedDate)"
                                    >
                                        <i class="fas fa-chevron-down" aria-hidden="true"></i>
                                    </button>
                                </div>
                            </label>
                            <b-spinner
                                v-if="editStates[cellKey(line.id, selectedDate)] && editStates[cellKey(line.id, selectedDate)].pending"
                                small
                            ></b-spinner>
                            <span
                                v-if="remoteEditorFor(entryPresenceKey(line.id, selectedDate))"
                                class="timesheet-presence-label"
                            >
                                {{ remoteEditorLabel(entryPresenceKey(line.id, selectedDate)) }}
                            </span>
                        </div>

                        <div class="timesheet-person__day-context">
                            <span
                                v-if="assignmentDayFor(line, selectedDate)"
                                class="timesheet-assignment-chip"
                                :title="assignmentDayTitle(line, selectedDate)"
                            >
                                <i class="fas fa-link" aria-hidden="true"></i>
                                Назначен на объект
                            </span>
                            <button
                                v-if="absenceDayFor(line, selectedDate) && absenceDayFor(line, selectedDate).state === 'suggestion'"
                                type="button"
                                class="timesheet-absence-suggestion-chip"
                                :disabled="!canEdit || absenceApplying"
                                @click="applyAbsenceSuggestions(line, selectedDate)"
                            >
                                Кадры: {{ absenceDayFor(line, selectedDate).code }} · Проставить
                            </button>
                            <span
                                v-else-if="absenceNeedsAttention(absenceDayFor(line, selectedDate))"
                                class="timesheet-absence-discrepancy-chip"
                            >
                                <i class="fas fa-info-circle" aria-hidden="true"></i>
                                {{ absenceDayFor(line, selectedDate).message || absenceStateLabel(absenceDayFor(line, selectedDate)) }}
                            </span>
                        </div>

                        <div
                            v-if="externalForDate(line).length"
                            class="timesheet-external-info"
                        >
                            <i class="fas fa-info-circle" aria-hidden="true"></i>
                            <span>
                                <strong>Часы распределены между табелями</strong>
                                <small v-if="localWorkedMinutes(line, selectedDate) > 0">
                                    В этом табеле: {{ minutesLabel(localWorkedMinutes(line, selectedDate)) }} ч
                                    + в других табелях: {{ minutesLabel(externalWorkedMinutes(line, selectedDate)) }} ч
                                    = всего {{ minutesLabel(combinedWorkedMinutes(line, selectedDate)) }} ч.
                                </small>
                                <small v-else>
                                    В других табелях уже указано
                                    {{ minutesLabel(externalWorkedMinutes(line, selectedDate)) }} ч.
                                    Здесь можно дополнительно указать часы.
                                </small>
                                <timesheet-conflict-sources :entries="externalForDate(line)" />
                            </span>
                        </div>

                        <div
                            v-for="incident in incidentsForDate(line)"
                            :key="incident.id"
                            class="timesheet-incident"
                            :class="`timesheet-incident--${incident.severity}`"
                        >
                            <i class="fas fa-flag" aria-hidden="true"></i>
                            <span>
                                <strong>{{ incident.title }}</strong>
                                <small v-if="incident.action_hint">{{ incident.action_hint }}</small>
                                <timesheet-conflict-sources :entries="incident.related_entries || []" />
                                <b-button
                                    v-if="isPrimaryAssignmentAction(line, incident)"
                                    class="timesheet-incident__action"
                                    size="sm"
                                    variant="warning"
                                    :disabled="assigningIncidentId !== null"
                                    @click.stop="assignEmployeeFromIncident(line, incident)"
                                >
                                    <b-spinner
                                        v-if="assigningIncidentId === Number(incident.id)"
                                        small
                                    ></b-spinner>
                                    <i v-else class="fas fa-user-plus" aria-hidden="true"></i>
                                    {{ assignmentActionLabel(incident) }}
                                </b-button>
                            </span>
                        </div>
                    </article>
                    </template>
                </section>
            </section>

            <section
                v-if="!isCompactViewport || isMobileFullMonthView"
                class="timesheet-month-view timesheet-month-view--desktop"
                :class="{
                    'timesheet-month-view--expanded': tableExpanded,
                    'is-mobile-full-active': isMobileFullMonthView,
                    'timesheet-month-view--names-unpinned': isMobileFullMonthView
                        && !mobileNamesPinned
                        && !mobileNamesPreviewing,
                    'timesheet-month-view--names-previewing': mobileNamesPreviewing
                }"
            >
                <div class="timesheet-month-toolbar">
                    <div v-if="tableExpanded" class="timesheet-expanded-context">
                        <strong>{{ periodMonth }}</strong><span>{{ periodObjectLabel }}</span>
                        <timesheet-sync-status :api="gridApi" />
                        <timesheet-employee-search v-if="isMobileFullMonthView" :api="gridApi" compact />
                    </div>
                    <b-button class="timesheet-expand-button" variant="outline-primary" size="sm"
                        :aria-pressed="tableExpanded ? 'true' : 'false'" @click="setTableExpanded(!tableExpanded)">
                        <i :class="tableExpanded ? 'fas fa-compress-alt' : 'fas fa-expand-alt'" aria-hidden="true"></i>
                        {{ tableExpanded ? 'Вернуть обычный вид' : 'На весь экран' }}
                    </b-button>
                    <details class="timesheet-input-help">
                        <summary>Как заполнять</summary>
                        <p>Состав пополняется автоматически по назначениям и плану. Если сотрудника нет, используйте
                            «Добавить сотрудника». Убрать сотрудника можно только из строки без часов, кодов и комментариев.
                            Это исключение из текущего табеля; назначения и кадровый статус не меняются.</p>
                        <p>Вводите часы или код прямо в ячейку. Enter — вниз, Tab — вправо, стрелки — соседние ячейки.
                            Двойной клик или Alt+↓ — выбор отметки. Диапазон — протянуть мышью или Shift+клик.
                            Недели с «+ открыть» свёрнуты: нажмите на шапку, чтобы увидеть дни.
                            На телефоне листайте дни вправо; нажмите на день, чтобы открыть его крупно.</p>
                    </details>
                    <timesheet-copy-names :names="filteredLines.map(line => line.employee_name_snapshot)" />
                    <div
                        v-if="!isMobileFullMonthView"
                        class="timesheet-month-toolbar__weeks"
                        role="group"
                        aria-label="Управление неделями"
                    >
                        <b-button variant="outline-secondary" size="sm" @click="collapseAllWeeks">
                            <i class="fas fa-compress-alt" aria-hidden="true"></i>
                            Всё свернуть
                        </b-button>
                        <b-button variant="outline-secondary" size="sm" @click="expandAllWeeks">
                            <i class="fas fa-expand-alt" aria-hidden="true"></i>
                            Всё развернуть
                        </b-button>
                        <b-button
                            v-if="characteristicFields.length"
                            :variant="showDesktopAssessments ? 'secondary' : 'outline-secondary'"
                            size="sm"
                            @click="showDesktopAssessments = !showDesktopAssessments"
                        >
                            <i class="fas fa-user-check" aria-hidden="true"></i>
                            {{ showDesktopAssessments ? 'Скрыть характеристики' : 'Характеристики' }}
                        </b-button>
                    </div>
                    <div v-if="canEdit" class="timesheet-month-toolbar__bulk">
                        <b-form-input
                            v-model="selectedDate"
                            class="timesheet-month-toolbar__bulk-date"
                            type="date"
                            :min="period.starts_on"
                            :max="period.ends_on"
                            aria-label="День массового заполнения"
                            @change="handleSelectedDateChange"
                        ></b-form-input>
                        <span class="timesheet-month-toolbar__bulk-copy">{{ bulkRecipientLabel }}</span>
                        <b-form-input
                            v-model="bulkValue"
                            class="timesheet-month-toolbar__bulk-value"
                            type="text"
                            inputmode="decimal"
                            list="timesheet-cell-suggestions"
                            autocomplete="off"
                            placeholder="8 или Д"
                            :aria-label="employeeSearchActive ? 'Отметка для найденных сотрудников за день' : 'Отметка для всех сотрудников за день'"
                        ></b-form-input>
                        <b-button variant="outline-primary" size="sm" :disabled="bulkFillDisabled" @click="applyBulkValue(captureTimesheetBulkAction())">
                            Заполнить день
                        </b-button>
                        <span v-if="bulkError" role="alert" class="text-danger">{{ bulkError }}</span>
                        <timesheet-bulk-result :result="bulkResult" />
                    </div>
                </div>

                <timesheet-grid-frame :api="gridApi">
                <div ref="monthScroll" class="timesheet-month-scroll"
                    :style="tableViewportHeight ? {maxHeight: `${tableViewportHeight}px`} : null" @scroll.passive="onMonthGridScroll">
                    <table
                        class="timesheet-month-table"
                        :class="{
                            'timesheet-month-table--without-ktu': !ktuEnabled,
                            'timesheet-month-table--without-reports': !reportReconciliationRequired
                        }"
                        data-timesheet-keyboard-grid
                    >
                        <thead>
                            <tr class="timesheet-month-table__week-row">
                                <th rowspan="2" class="timesheet-month-table__number">№</th>
                                <th
                                    v-if="ktuEnabled && !isMobileFullMonthView"
                                    rowspan="2"
                                    class="timesheet-month-table__ktu"
                                >
                                    <span
                                        class="timesheet-ktu-heading"
                                        title="Шкала 0–5. 4 — норма; количество дней не снижает КТУ."
                                        v-b-tooltip.hover.focus.top
                                    >
                                        КТУ <i class="fas fa-info-circle" aria-hidden="true"></i>
                                    </span>
                                </th>
                                <th rowspan="2" class="timesheet-month-table__person">
                                    <template v-if="isCompactViewport">Сотрудник</template>
                                    <timesheet-employee-search v-else :api="gridApi" compact />
                                </th>
                                <th
                                    v-if="ktuEnabled && isMobileFullMonthView"
                                    rowspan="2"
                                    class="timesheet-month-table__ktu timesheet-month-table__ktu--mobile-scroll"
                                >
                                    <span
                                        class="timesheet-ktu-heading"
                                        title="Шкала 0–5. 4 — норма; количество дней не снижает КТУ."
                                        v-b-tooltip.hover.focus.top
                                    >
                                        КТУ <i class="fas fa-info-circle" aria-hidden="true"></i>
                                    </span>
                                </th>
                                <th
                                    v-for="week in monthWeeks"
                                    :key="`week-head-${week.key}`"
                                    :colspan="isWeekCollapsed(week.key) ? 1 : week.days.length"
                                    class="timesheet-month-table__week"
                                    :class="{'is-collapsed': isWeekCollapsed(week.key)}"
                                >
                                    <button
                                        type="button"
                                        :title="isMobileFullMonthView
                                            ? `${week.label}: ${week.range}`
                                            : isWeekCollapsed(week.key)
                                            ? `Развернуть ${week.label.toLocaleLowerCase('ru-RU')}`
                                            : `Свернуть ${week.label.toLocaleLowerCase('ru-RU')}`"
                                        @click="handleMonthWeekClick(week.key)"
                                    >
                                        <template v-if="isMobileFullMonthView">
                                            {{ week.label }} · {{ week.range }}
                                        </template>
                                        <template v-else-if="isWeekCollapsed(week.key)">
                                            <i class="fas fa-plus-circle" aria-hidden="true"></i>
                                            <strong>{{ week.label }}</strong>
                                            <small>открыть</small>
                                        </template>
                                        <template v-else>
                                            <i class="fas fa-chevron-down" aria-hidden="true"></i>
                                            {{ week.label }} · {{ week.range }}
                                        </template>
                                    </button>
                                </th>
                                <th
                                    :colspan="showMonthlyTotals ? 3 : 1"
                                    class="timesheet-month-table__totals-group"
                                    :class="{'is-collapsed': !showMonthlyTotals}"
                                >
                                    <button type="button" @click="showMonthlyTotals = !showMonthlyTotals">
                                        <i
                                            :class="showMonthlyTotals ? 'fas fa-chevron-down' : 'fas fa-plus-circle'"
                                            aria-hidden="true"
                                        ></i>
                                        Итоги месяца
                                        <small v-if="!showMonthlyTotals">открыть</small>
                                    </button>
                                </th>
                                <th rowspan="2" class="timesheet-month-table__assessment">Хар.</th>
                                <th
                                    v-for="field in visibleCharacteristicFields"
                                    :key="`head-${field.key}`"
                                    rowspan="2"
                                    class="timesheet-month-table__characteristic"
                                    :class="{'is-text': field.type === 'text'}"
                                >
                                    {{ field.label }}
                                </th>
                            </tr>
                            <tr class="timesheet-month-table__day-row">
                                <th
                                    v-for="column in visibleMonthColumns"
                                    :key="`column-head-${column.key}`"
                                    :data-cursor-date="column.type === 'day' ? column.date : null"
                                    :class="{
                                        'is-weekend': column.type === 'day' && column.is_weekend,
                                        'is-selected': column.type === 'day' && column.date === selectedDate,
                                        'review-source-day': column.type === 'day' && column.date === reviewFocusedDate,
                                        'is-week-summary': column.type === 'week'
                                    }"
                                >
                                    <button
                                        v-if="column.type === 'day'"
                                        type="button"
                                        @click="openDay(column.date)"
                                    >
                                        <strong>{{ column.day }}</strong>
                                        <span>{{ column.weekday }}</span>
                                    </button>
                                    <button
                                        v-else
                                        type="button"
                                        title="Развернуть неделю и показать дни"
                                        @click="toggleWeek(column.week_key)"
                                    >
                                        <strong><i class="fas fa-expand-alt" aria-hidden="true"></i></strong>
                                        <span>дни</span>
                                    </button>
                                </th>
                                <template v-if="showMonthlyTotals">
                                    <th class="timesheet-month-table__total">Дней</th>
                                    <th class="timesheet-month-table__total">Часов</th>
                                    <th class="timesheet-month-table__total">Среднее</th>
                                </template>
                                <th v-else class="timesheet-month-table__total is-collapsed-total">
                                    <button type="button" @click="showMonthlyTotals = true">
                                        <strong>Σ</strong>
                                        <span>месяц</span>
                                    </button>
                                </th>
                            </tr>
                            <tr v-for="(row, index) in reportCountRows" :key="row.field"
                                class="timesheet-reconciliation-row" :style="{'--reconciliation-row': index}">
                                <th :colspan="ktuEnabled ? 3 : 2" class="timesheet-month-table__report-label" scope="row">
                                    <button type="button" class="timesheet-report-detail-button" @click="openReportDetails()"
                                        title="Численность рабочих без ИТР. В свёрнутой неделе — сумма за дни (чел.-дн.).">
                                        {{ row.label }}
                                    </button>
                                </th>
                                <th v-for="column in visibleMonthColumns" :key="row.field + column.key"
                                    :class="reportColumnClass(column)" :title="reportColumnTitle(column)">
                                    <button type="button" class="timesheet-report-detail-button"
                                        :aria-label="row.label + '. ' + reportColumnTitle(column) + '. Открыть сверку'"
                                        @click="openReportDetails(column)">
                                        {{ reportColumnDisplayCount(column, row.field) }}
                                    </button>
                                </th>
                                <th v-if="index === 0" :colspan="serviceRowTailColspan" :rowspan="reportReconciliationRequired ? 3 : 1"
                                    class="timesheet-month-table__report-status">
                                    <span v-if="!reportReconciliationRequired">Сверка не требуется</span>
                                    <strong v-else-if="reportMismatchDays.length">Расхождение: {{ reportMismatchDays.length }}
                                        {{ pluralize(reportMismatchDays.length, 'день', 'дня', 'дней') }}</strong>
                                    <span v-else>Совпадает</span>
                                </th>
                            </tr>
                            <tr v-if="reportReconciliationRequired" class="timesheet-reconciliation-row timesheet-reconciliation-row--numbers"
                                :style="{'--reconciliation-row': 2}">
                                <th :colspan="ktuEnabled ? 3 : 2" class="timesheet-month-table__report-label" scope="row">Отчёты №</th>
                                <th v-for="column in visibleMonthColumns" :key="'report-links-' + column.key"
                                    :class="{'is-week-summary': column.type === 'week'}">
                                    <TimesheetReportLinks v-if="column.type === 'day'" :ids="reportColumnIds(column)" stacked compact />
                                    <button v-else type="button" class="timesheet-report-detail-button"
                                        title="Количество отчётов за неделю. Нажмите, чтобы развернуть неделю"
                                        :aria-label="`Отчётов за неделю: ${reportColumnIds(column).length}. Развернуть неделю`"
                                        :aria-expanded="false"
                                        @click="toggleWeek(column.week_key)">
                                        {{ reportColumnIds(column).length }} отч.
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <timesheet-month-rows ref="monthRows" :api="gridApi" :lines="filteredLines">
                            <tr v-if="!isCompactViewport && employeeSearchActive && !employeeSearchPending && !filteredLines.length" key="search-empty">
                                <td :colspan="monthTableColumnCount" class="timesheet-employee-search-empty" role="status">
                                    В этом табеле сотрудник не найден.
                                    <button type="button" @click="resetEmployeeSearch">Показать всех</button>
                                </td>
                            </tr>
                            <tr v-if="canEdit" key="add-person" class="timesheet-month-table__add-row">
                                <td class="timesheet-month-table__number">
                                    <button type="button" aria-label="Добавить сотрудника" @click="openAddPerson">
                                        <i class="fas fa-plus" aria-hidden="true"></i>
                                    </button>
                                </td>
                                <td v-if="ktuEnabled" class="timesheet-month-table__ktu"></td>
                                <td :colspan="monthAddRowColspan" class="timesheet-month-table__add-cell">
                                    <button type="button" @click="openAddPerson">
                                        <i class="fas fa-plus" aria-hidden="true"></i>
                                        Добавить сотрудника
                                    </button>
                                </td>
                            </tr>
                        </timesheet-month-rows>
                        <tfoot>
                            <tr>
                                <th class="timesheet-month-table__number"></th>
                                <th
                                    v-if="ktuEnabled && !isMobileFullMonthView"
                                    class="timesheet-month-table__ktu"
                                ></th>
                                <th class="timesheet-month-table__person">
                                    {{ employeeSearchActive ? 'Всего по табелю' : isMobileFullMonthView ? 'Σ часов' : 'Всего часов' }}
                                </th>
                                <th
                                    v-if="ktuEnabled && isMobileFullMonthView"
                                    class="timesheet-month-table__ktu timesheet-month-table__ktu--mobile-scroll"
                                ></th>
                                <td
                                    v-for="column in visibleMonthColumns"
                                    :key="`total-${column.key}`"
                                    :class="{
                                        'is-weekend': column.type === 'day' && column.is_weekend,
                                        'is-week-summary': column.type === 'week'
                                    }"
                                >
                                    {{ column.type === 'day'
                                        ? minutesLabel(dayMinutes(column.date))
                                        : minutesLabel(weekMinutes(column.week)) }}
                                </td>
                                <template v-if="showMonthlyTotals">
                                    <td class="timesheet-month-table__total"></td>
                                    <td class="timesheet-month-table__total">{{ monthTotalHours }}</td>
                                    <td class="timesheet-month-table__total"></td>
                                </template>
                                <td v-else class="timesheet-month-table__total is-collapsed-total">
                                    {{ monthTotalHours }}
                                </td>
                                <td class="timesheet-month-table__assessment"></td>
                                <td
                                    v-for="field in visibleCharacteristicFields"
                                    :key="`foot-${field.key}`"
                                    class="timesheet-month-table__characteristic"
                                ></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                </timesheet-grid-frame>
            </section>

            <section
                v-if="isCompactViewport && mobileMode === 'month' && mobileMonthLayout === 'weeks'"
                class="timesheet-mobile-month"
                :class="{'is-mobile-active': mobileMode === 'month' && mobileMonthLayout === 'weeks'}"
            >
                <header class="timesheet-mobile-month__header">
                    <div>
                        <strong>Месяц по неделям</strong>
                        <span>Часы — в сетке, характеристика — один раз за месяц.</span>
                    </div>
                    <b-button v-if="canEdit" variant="primary" size="sm" @click="openAddPerson">
                        <i class="fas fa-plus" aria-hidden="true"></i>
                        Добавить сотрудника
                    </b-button>
                </header>
                <div class="timesheet-mobile-weeks" role="tablist" aria-label="Недели месяца">
                    <button
                        v-for="week in monthWeeks"
                        :key="`mobile-week-${week.key}`"
                        type="button"
                        role="tab"
                        :aria-selected="selectedMobileWeek.key === week.key ? 'true' : 'false'"
                        :class="{'is-active': selectedMobileWeek.key === week.key}"
                        @click="chooseMobileWeek(week.key)"
                    >
                        <strong>{{ week.label }}</strong>
                        <span>{{ week.range }}</span>
                    </button>
                </div>

                <section class="timesheet-mobile-reconciliation" aria-label="Сверка рабочих с отчётами">
                    <header>
                        <strong>Количество рабочих</strong>
                        <span>Нажмите на день, чтобы открыть его</span>
                    </header>
                    <div
                        class="timesheet-mobile-reconciliation__grid"
                        :style="{
                            gridTemplateColumns: `minmax(68px, 1.65fr) repeat(${selectedMobileWeek.days.length}, minmax(30px, 1fr))`
                        }"
                    >
                        <span class="timesheet-mobile-reconciliation__corner">День</span>
                        <button
                            v-for="day in selectedMobileWeek.days"
                            :key="`reconciliation-day-${day.date}`"
                            type="button"
                            class="timesheet-mobile-reconciliation__day"
                            :class="{
                                'is-weekend': day.is_weekend,
                                'is-mismatch': !reconciliationDay(day.date).matches
                            }"
                            :aria-label="`Открыть ${shortDate(day.date)}`"
                            @click="openDay(day.date)"
                        >
                            <span>{{ day.weekday }}</span>
                            <strong>{{ day.day }}</strong>
                        </button>
                        <template v-if="reportReconciliationRequired">
                        <strong class="timesheet-mobile-reconciliation__label">По отчётам</strong>
                        <button
                            v-for="day in selectedMobileWeek.days"
                            :key="`reconciliation-report-${day.date}`"
                            type="button"
                            class="timesheet-mobile-reconciliation__value"
                            :class="{'is-mismatch': !reconciliationDay(day.date).matches}"
                            :aria-label="`${shortDate(day.date)}: по отчётам ${reconciliationDay(day.date).report_workers}`"
                            @click="openDay(day.date)"
                        >
                            {{ reconciliationDay(day.date).report_workers }}
                        </button>
                        </template>
                        <strong class="timesheet-mobile-reconciliation__label">В табеле</strong>
                        <button
                            v-for="day in selectedMobileWeek.days"
                            :key="`reconciliation-sheet-${day.date}`"
                            type="button"
                            class="timesheet-mobile-reconciliation__value"
                            :class="{'is-mismatch': !reconciliationDay(day.date).matches}"
                            :aria-label="`${shortDate(day.date)}: в табеле ${reconciliationDay(day.date).timesheet_workers}`"
                            @click="openDay(day.date)"
                        >
                            {{ reconciliationDay(day.date).timesheet_workers }}
                        </button>
                        <template v-if="reportReconciliationRequired">
                            <strong class="timesheet-mobile-reconciliation__label timesheet-report-label">Отчёты №</strong>
                            <div v-for="day in selectedMobileWeek.days" :key="`mobile-report-links-${day.date}`"
                                 class="timesheet-mobile-report-cell">
                                <TimesheetReportLinks :ids="reconciliationDay(day.date).report_ids" stacked />
                            </div>
                        </template>
                    </div>
                </section>

                <div class="timesheet-mobile-month__roster" data-timesheet-keyboard-grid>
                    <template v-for="(line, lineIndex) in filteredLines">
                    <h2
                        v-if="lineGroupStartsAt(lineIndex)"
                        :key="`mobile-month-group-${lineGroupKey(line)}`"
                        class="timesheet-line-group-heading"
                    >
                        {{ lineGroupLabel(line) }}
                    </h2>
                    <article
                        :key="`mobile-month-${line.id}`"
                        :data-review-line-id="line.id"
                        class="timesheet-mobile-person"
                        :class="{'timesheet-person--unresolved': line.unresolved_person_id, 'review-source-row': Number(line.id) === reviewFocusedLineId, 'employee-focused-row': isEmployeeFocused(line)}"
                    >
                        <header>
                            <div class="timesheet-person__avatar" aria-hidden="true">
                                {{ employeeInitials(line) }}
                            </div>
                            <div>
                                <div class="timesheet-person-name-row">
                                    <button
                                        v-if="line.employee_id"
                                        type="button"
                                        class="timesheet-employee-link"
                                        :title="assignmentPersonTitle(line)"
                                        @click="openEmployeeInspector(line)"
                                    >
                                        {{ lineEmployeeName(line) }}
                                    </button>
                                    <strong v-else>{{ lineEmployeeName(line) }}</strong>
                                    <button type="button" class="timesheet-select-month" :aria-label="`Выделить весь месяц: ${line.employee_name_snapshot}`" title="Выделить весь месяц" @click.stop="selectEmployeeMonth(line, $event)"><i class="far fa-calendar-alt" aria-hidden="true"></i></button>
                                    <span
                                        v-if="lineIssueGroups(line).length"
                                        class="timesheet-issue-rail"
                                        aria-label="Проблемы по сотруднику"
                                    >
                                        <button
                                            v-for="group in visibleLineIssueGroups(line)"
                                            :key="`mobile-person-issue-${line.id}-${group.key}`"
                                            type="button"
                                            class="timesheet-issue-icon"
                                            :class="issueCueClasses(group)"
                                            :aria-label="issueGroupAriaLabel(group)"
                                            v-b-tooltip.hover.focus.top="hasSourceIssue([group]) ? '' : issueGroupTooltip(group)"
                                            @mouseenter="showSourcePopover($event, line, [group])"
                                            @focus="showSourcePopover($event, line, [group])"
                                            @keydown.down.prevent="showSourcePopover($event, line, [group], true)"
                                            @keydown.esc.stop="closeSourcePopover()"
                                            @click.stop="openIssueGroup(line, group)"
                                        >
                                            <i :class="group.icon" aria-hidden="true"></i>
                                            <small v-if="group.count > 1">{{ group.count }}</small>
                                        </button>
                                    </span>
                                </div>
                                <timesheet-row-actions v-if="canExcludeRosterLine(line)" :name="line.employee_name_snapshot || lineEmployeeName(line)" :disabled="rosterExclusionPending || editPendingCount > 0" @exclude="openRosterExclusion(line)" />
                                <span>
                                    {{ minutesLabel(weekLineMinutes(line, selectedMobileWeek)) }} ч за неделю ·
                                    {{ minutesLabel(lineMonthMinutes(line)) }} ч за месяц
                                </span>
                            </div>
                        </header>
                        <button
                            v-if="line.unresolved_person_id"
                            type="button"
                            class="timesheet-unresolved-line-action timesheet-unresolved-line-action--mobile"
                            v-timesheet-attention
                            :disabled="!canResolveUnresolved"
                            @click="openUnresolvedResolver(line)"
                        >
                            <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                            Нет карточки сотрудника — сопоставить сейчас
                        </button>
                        <button
                            v-if="canRemoveManualLine(line)"
                            type="button"
                            class="timesheet-manual-line-remove timesheet-manual-line-remove--mobile"
                            :disabled="removingLineId !== null"
                            @click.stop="removeManualLine(line)"
                        >
                            <b-spinner v-if="removingLineId === Number(line.id)" small></b-spinner>
                            <i v-else class="fas fa-trash-alt" aria-hidden="true"></i>
                            Удалить из табеля
                        </button>
                        <timesheet-assessment-link
                            :incomplete="Boolean(assessmentIssuesForLine(line, false).length)"
                            :status="mobileAssessmentStatus(line)"
                            @click="openAssessment(line)"
                        ></timesheet-assessment-link>
                        <div class="timesheet-mobile-week-grid">
                            <label
                                v-for="day in selectedMobileWeek.days"
                                :key="`${line.id}-mobile-${day.date}`"
                                :data-line-id="line.id"
                                :data-work-date="day.date"
                                :class="[
                                    reviewSourceCellClass(line, day.date),
                                    {
                                        'is-weekend': day.is_weekend,
                                        'has-external': hasExternalEntry(line, day.date),
                                        'has-issue': dateIncidentsFor(line, day.date).length,
                                        'has-remote-editor': remoteEditorFor(entryPresenceKey(line.id, day.date))
                                    },
                                    assignmentCellClasses(line, day.date)
                                ]"
                                :style="presenceCellStyle(entryPresenceKey(line.id, day.date))"
                                :title="monthCellTitle(line, day.date)"
                            >
                                <span>{{ day.weekday }}</span>
                                <strong>{{ day.day }}</strong>
                                <timesheet-entry-input v-if="canEdit" :api="gridApi" :line="line" :date="day.date" class="timesheet-keyboard-cell" :placeholder="monthCellPlaceholder(line, day.date)" :title="monthCellTitle(line, day.date)" />
                                <button
                                    v-if="absenceDayFor(line, day.date) && absenceDayFor(line, day.date).state === 'suggestion'"
                                    type="button"
                                    class="timesheet-mobile-absence-suggestion"
                                    :disabled="absenceApplying"
                                    :aria-label="`Проставить ${absenceDayFor(line, day.date).code} за ${day.date}`"
                                    @click.prevent.stop="applyAbsenceSuggestions(line, day.date)"
                                >
                                    {{ absenceDayFor(line, day.date).code }}
                                </button>
                                <timesheet-read-cell v-else-if="!canEdit" :api="gridApi" :line="line" :date="day.date" />
                                <span v-else class="timesheet-mobile-week-grid__value">
                                    {{ monthCellDisplay(line, day.date) || '—' }}
                                </span>
                                <span
                                    v-if="hasExternalEntry(line, day.date) && localWorkedMinutes(line, day.date) > 0"
                                    class="timesheet-mobile-external-badge"
                                    title="Есть часы в другом табеле"
                                >
                                    Дт
                                </span>
                                <span
                                    v-if="remoteEditorFor(entryPresenceKey(line.id, day.date))"
                                    class="timesheet-presence-label"
                                >
                                    {{ remoteEditorLabel(entryPresenceKey(line.id, day.date)) }}
                                </span>
                            </label>
                        </div>
                        <div v-if="lineLevelIncidents(line).length" class="timesheet-mobile-person__warning">
                            <i class="fas fa-flag" aria-hidden="true"></i>
                            <span>
                                {{ lineLevelIncidents(line).map(incidentText).join(' · ') }}
                                <b-button
                                    v-if="assignableLineIncident(line)"
                                    class="timesheet-incident__action"
                                    size="sm"
                                    variant="warning"
                                    :disabled="assigningIncidentId !== null"
                                    @click="assignEmployeeFromIncident(
                                        line,
                                        assignableLineIncident(line)
                                    )"
                                >
                                    <i class="fas fa-user-plus" aria-hidden="true"></i>
                                    {{ assignmentActionLabel(assignableLineIncident(line)) }}
                                </b-button>
                            </span>
                        </div>
                    </article>
                    </template>
                </div>
            </section>

            <section
                ref="plannedArrivalsSection"
                class="timesheet-planned-roster"
                :class="{'is-open': plannedArrivalsExpanded}"
            >
                <button
                    type="button"
                    class="timesheet-planned-roster__toggle"
                    :aria-expanded="plannedArrivalsExpanded ? 'true' : 'false'"
                    aria-controls="timesheet-planned-arrivals-list"
                    @click="plannedArrivalsExpanded = !plannedArrivalsExpanded"
                >
                    <span class="timesheet-planned-roster__icon" aria-hidden="true">
                        <i class="fas fa-user-clock"></i>
                    </span>
                    <span class="timesheet-planned-roster__heading">
                        <strong>Запланированы на ваш объект</strong>
                        <small>
                            <template v-if="plannedArrivals.length">
                                {{ plannedArrivals.length }} чел. ещё не подтверждены по фактической дате прибытия
                            </template>
                            <template v-else>Все запланированные прибытия подтверждены</template>
                        </small>
                    </span>
                    <b-badge
                        v-if="overduePlannedArrivalsCount"
                        variant="warning"
                        class="timesheet-status-badge timesheet-planned-roster__overdue"
                    >
                        Просрочено: {{ overduePlannedArrivalsCount }}
                    </b-badge>
                    <span class="timesheet-planned-roster__toggle-label">
                        {{ plannedArrivalsExpanded ? 'Скрыть список' : 'Показать список' }}
                        <i
                            class="fas"
                            :class="plannedArrivalsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
                            aria-hidden="true"
                        ></i>
                    </span>
                </button>
                <div
                    v-show="plannedArrivalsExpanded"
                    id="timesheet-planned-arrivals-list"
                    class="timesheet-planned-roster__list"
                >
                    <div v-if="!plannedArrivals.length" class="timesheet-planned-roster__empty">
                        <i class="fas fa-check-circle" aria-hidden="true"></i>
                        <span>
                            <strong>Никого не нужно подтверждать</strong>
                            <small>Новые запланированные сотрудники появятся здесь автоматически.</small>
                        </span>
                    </div>
                    <article
                        v-for="arrival in plannedArrivals"
                        :key="`planned-arrival-${arrival.key || arrival.id}`"
                        class="timesheet-planned-roster__person"
                        :class="{'is-overdue': plannedArrivalIsOverdue(arrival)}"
                    >
                        <div class="timesheet-person__avatar" aria-hidden="true">
                            {{ plannedArrivalInitials(arrival) }}
                        </div>
                        <div class="timesheet-planned-roster__identity">
                            <strong>{{ arrival.employee_name }}</strong>
                            <small v-if="arrival.target_type === 'object_group'">
                                Запланирован на группу, в которую входит этот объект
                            </small>
                            <span v-if="arrivalAlreadyInTimesheet(arrival)">
                                Уже есть строка в табеле, но прибытие ещё не подтверждено
                            </span>
                            <span v-else>Появится в табеле после подтверждения прибытия</span>
                            <small
                                v-if="arrival.confirmation_hint"
                                class="timesheet-planned-roster__confirmation-hint"
                            >
                                {{ arrival.confirmation_hint }}
                            </small>
                            <small v-if="arrival.note">Примечание к плану: {{ arrival.note }}</small>
                        </div>
                        <div class="timesheet-planned-roster__date">
                            <span>Запланирован с</span>
                            <strong>{{ shortDate(arrival.planned_for) }}</strong>
                            <b-badge class="timesheet-status-badge" v-if="plannedArrivalIsOverdue(arrival)" variant="warning">
                                Дата уже наступила
                            </b-badge>
                        </div>
                        <div class="timesheet-planned-arrival-action">
                            <b-button
                                v-if="canEdit && arrival.can_confirm"
                                variant="success"
                                :disabled="plannedMovePendingId !== null || editPendingCount > 0"
                                @click="confirmPlannedArrival(arrival)"
                            >
                                <b-spinner
                                    v-if="plannedMovePendingId === (arrival.key || arrival.id)"
                                    small
                                ></b-spinner>
                                <template v-else>
                                    <i class="fas fa-check" aria-hidden="true"></i>
                                    Подтвердить прибытие
                                </template>
                            </b-button>
                            <small v-else-if="!arrival.can_confirm">
                                {{ arrival.confirmation_reason }}
                            </small>
                            <small v-else>Откройте редактирование табеля для подтверждения.</small>
                        </div>
                    </article>
                </div>
            </section>

            <section class="timesheet-summary">
                <div>
                    <strong>{{ currentObject.name }}</strong>
                    <span v-if="currentObject.contract_name">{{ currentObject.contract_name }}</span>
                </div>
                <div>
                    <strong>{{ displayMode === 'day' ? dayTotalHours : monthTotalHours }}</strong>
                    <span>{{ displayMode === 'day' ? 'часов за день' : 'часов за месяц' }}</span>
                </div>
                <div>
                    <strong>{{ sheet.lines.length }}</strong>
                    <span>человек в табеле</span>
                </div>
                <div v-b-tooltip.hover.focus.top="monthPersonDaysTooltip" class="timesheet-summary__details" tabindex="0">
                    <strong>{{ monthPersonDays.all.calculated }}</strong>
                    <span>чел.-дн. за месяц</span>
                </div>
                <div>
                    <strong>{{ displayMode === 'day' ? currentDayIncidentCount : openIncidentCount }}</strong>
                    <span>инцидентов</span>
                </div>
            </section>
            <p class="small text-muted mt-2">
                Полностью пустые строки не требуют КТУ и характеристики. Перед отправкой заполните фактические данные или уберите лишние пустые строки из табеля.
            </p>

            <timesheet-conflict-popover v-if="sourcePopover" :key="sourcePopover.key" :target="sourcePopover.target"
                                        :days="sourcePopoverDays" :focus-content="sourcePopover.focusContent" @close="closeSourcePopover" />
            <timesheet-roster-exclusion-dialog v-model="rosterExclusionVisible" :line="rosterExclusionLine" :pending="rosterExclusionPending" :removed="rosterExclusionDone" :error="rosterExclusionError" @submit="submitRosterExclusion" />
            <b-modal :visible="Boolean(manualRemoval)" title="Удалить сотрудника из табеля?"
                     modal-class="timesheet-dialog" centered scrollable
                     :hide-header-close="removingLineId !== null" :no-close-on-backdrop="removingLineId !== null"
                     :no-close-on-esc="removingLineId !== null" @hide="closeManualRemoval">
                <template v-if="manualRemoval">
                    <p v-if="manualRemoval.removed" class="mb-0" role="status">
                        <strong>{{ manualRemoval.name }}</strong> удалён из табеля.
                    </p>
                    <template v-else>
                        <p>Удалить <strong>{{ manualRemoval.name }}</strong> из табеля?</p>
                        <p class="mb-0">Дневных отметок нет. КТУ и характеристики этой строки тоже удалятся.
                            Несохранённые правки этой строки также удалятся. Отменить удаление нельзя.</p>
                    </template>
                </template>
                <template #modal-footer>
                    <div class="w-100">
                        <div v-if="manualRemovalError" class="alert alert-danger mb-2" role="alert">{{ manualRemovalError }}</div>
                        <div v-if="manualRemovalNotice" class="alert alert-warning mb-2" role="alert">{{ manualRemovalNotice }}</div>
                        <p v-if="removingLineId !== null" class="mb-2" role="status">
                            <b-spinner small class="mr-1" /> {{ manualRemoval && manualRemoval.removed ? 'Обновляем табель…' : 'Удаляем строку…' }}
                        </p>
                        <div class="d-flex flex-wrap justify-content-end">
                            <b-button variant="outline-secondary" class="m-1" :disabled="removingLineId !== null" @click="closeManualRemoval">
                                {{ manualRemoval && manualRemoval.removed ? 'Закрыть' : 'Не удалять' }}
                            </b-button>
                            <b-button v-if="manualRemoval && !manualRemoval.removed" variant="danger" class="m-1"
                                      :disabled="removingLineId !== null" @click="confirmManualRemoval">Да, удалить из табеля</b-button>
                            <b-button v-else-if="manualRemovalNotice" variant="primary" class="m-1"
                                      :disabled="removingLineId !== null" @click="retryManualRemovalRefresh">Обновить табель</b-button>
                        </div>
                    </div>
                </template>
            </b-modal>
            <b-modal :visible="Boolean(conflictSelection)" title="Отметки в разных табелях" ok-only ok-title="Закрыть" scrollable no-enforce-focus
                     @hidden="conflictSelection = null">
                <section v-for="day in conflictDetails" :key="day.date" class="mb-3">
                    <strong>{{ day.name }} · {{ shortDate(day.date) }}</strong>
                    <p>В этом табеле сохранено: {{ day.local }}.</p>
                    <p v-for="(message, index) in day.messages" :key="index">{{ message }}</p>
                    <timesheet-conflict-sources :entries="day.entries" @open-author="conflictSelection = null" />
                    <p v-if="!day.entries.length">В других табелях отметок сейчас нет. Если предупреждение осталось, обновите табель.</p>
                </section>
            </b-modal>
            <b-modal
                modal-class="timesheet-dialog"
                v-if="canEdit"
                v-model="addPersonModalVisible"
                title="Добавить сотрудника в табель"
                size="lg"
                scrollable
                :hide-header-close="addingPerson"
                :no-close-on-backdrop="addingPerson"
                :no-close-on-esc="addingPerson"
                @hidden="handleAddPersonModalHidden"
            >
            <div v-if="personAdditionResult" class="timesheet-add-person__receipt" role="status">
                <i class="fas fa-check-circle text-success mr-2" aria-hidden="true"></i>
                <strong>{{ personAdditionResult.message }}</strong>
            </div>
            <fieldset v-else :disabled="addingPerson" class="border-0 p-0 m-0">
            <section v-if="reservePersonForAddition" ref="reservePersonDetails" tabindex="-1" aria-label="Выбранный сотрудник в резерве" class="timesheet-reserve-selection">
                <div class="timesheet-reserve-selection__person">
                    <EmployeeLink :employee-id="reservePersonForAddition.employee_id" :fallback="reservePersonForAddition.full_name" />
                    <b-badge variant="warning">В резерве</b-badge>
                </div>
                <p>Вы выбрали сотрудника в резерве. Он ещё не добавлен в табель.</p>
                <p>Можно добавить строку сейчас. Для подтверждения прибытия сотрудника сначала нужно вернуть в действующие с указанием трудового периода.</p>
                <TimesheetEmployeeCardContacts :timesheet-id="currentTimesheetId()" reserve-return />
            </section>
            <section v-else class="timesheet-add-person timesheet-add-person--modal">
                <label for="timesheet-person-search" class="mb-0 font-weight-bold">Фамилия, имя или отчество</label>
                <div class="timesheet-add-person__search">
                    <b-form-input
                        id="timesheet-person-search"
                        ref="personSearchInput"
                        v-model.trim="peopleQuery"
                        placeholder="Фамилия, имя или отчество"
                        autofocus
                        @keyup.enter="searchPeopleNow"
                    ></b-form-input>
                </div>
                <div class="timesheet-add-person__search-status" role="status" aria-live="polite">
                    <template v-if="peopleQuery.length < 2">
                        Введите хотя бы две буквы — поиск начнётся сам.
                    </template>
                    <template v-else-if="peopleSearching">
                        <b-spinner small></b-spinner>
                        Ищем среди всех сотрудников…
                    </template>
                    <template v-else-if="peopleSearchError">
                        {{ peopleSearchError }}
                    </template>
                    <template v-else-if="peopleSearchCompletedQuery === peopleQuery.trim()">
                        <template v-if="plannedMatchesForAdd.length || regularPeopleResults.length">
                            Выберите сотрудника из списка.
                        </template>
                        <template v-else-if="peopleResults.existing_unresolved">
                            Найдена ранее добавленная строка. Выберите действие ниже.
                        </template>
                        <template v-else>
                            Сотрудник не найден. Проверьте фамилию или добавьте человека без карточки.
                        </template>
                    </template>
                </div>

                <section v-if="plannedMatchesForAdd.length" class="timesheet-add-person__planned">
                    <header>
                        <span>
                            <i class="fas fa-user-clock" aria-hidden="true"></i>
                            <strong>Сначала — запланированные на ваш объект</strong>
                        </span>
                        <small>Для них не нужна причина ручного добавления</small>
                    </header>
                    <div v-if="plannedMatchesForAdd.length" class="timesheet-add-person__planned-list">
                        <button
                            v-for="arrival in plannedMatchesForAdd"
                            :key="`add-planned-${arrival.key || arrival.id}`"
                            type="button"
                            :disabled="!arrival.can_confirm || plannedMovePendingId !== null || editPendingCount > 0"
                            @click="confirmPlannedArrivalFromAdd(arrival)"
                        >
                            <span class="timesheet-add-person__planned-person">
                                <strong>{{ arrival.employee_name }}</strong>
                                <small>
                                    <template v-if="arrival.target_type === 'object_group'">Запланирован на группу вашего объекта с</template>
                                    <template v-else>Запланирован на ваш объект с</template>
                                    <b>{{ shortDate(arrival.planned_for) }}</b>
                                </small>
                                <small v-if="arrivalAlreadyInTimesheet(arrival)">
                                    Строка уже есть — осталось подтвердить прибытие
                                </small>
                            </span>
                            <b-badge class="timesheet-status-badge" v-if="plannedArrivalIsOverdue(arrival)" variant="warning">
                                Дата наступила
                            </b-badge>
                            <span v-if="arrival.can_confirm" class="timesheet-add-person__planned-action">
                                Подтвердить прибытие
                                <i class="fas fa-chevron-right" aria-hidden="true"></i>
                            </span>
                            <small v-else>{{ arrival.confirmation_reason }}</small>
                        </button>
                    </div>
                </section>

                <div v-if="regularPeopleResults.length" class="timesheet-add-person__results">
                    <div class="timesheet-add-person__results-heading">
                        <strong>Найденные сотрудники</strong>
                        <span>Действующих добавим сразу; для резервных покажем пояснение.</span>
                    </div>
                    <button
                        v-for="person in regularPeopleResults"
                        :key="person.employee_id"
                        type="button"
                        :aria-label="person.lifecycle_status === 'reserve' ? `Выбрать ${person.full_name} — в резерве` : `Добавить ${person.full_name} в табель`"
                        :disabled="person.already_in_timesheet || addingPerson"
                        @click="addEmployee(person)"
                    >
                        <span class="timesheet-add-person__result-person">
                            <strong>{{ person.full_name }}</strong>
                            <small v-if="person.excluded_from_timesheet">Убран из этого табеля — нажмите, чтобы вернуть</small>
                            <small>{{ person.position || lifecycleLabel(person.lifecycle_status) }}</small>
                            <b-badge v-if="person.lifecycle_status === 'reserve'" variant="warning">В резерве</b-badge>
                        </span>
                        <span
                            v-if="!person.already_in_timesheet"
                            class="timesheet-add-person__result-action"
                        >
                            <template v-if="addingEmployeeId === Number(person.employee_id)">
                                <b-spinner small label="Добавление сотрудника"></b-spinner>
                                Добавляем…
                            </template>
                            <template v-else-if="person.lifecycle_status === 'reserve'">Выбрать <i class="fas fa-chevron-right" aria-hidden="true"></i></template>
                            <template v-else>
                                <i class="fas fa-plus" aria-hidden="true"></i>
                                Добавить в табель
                            </template>
                        </span>
                        <b-badge class="timesheet-status-badge" v-if="person.already_in_timesheet" variant="info">
                            Уже есть в этом табеле
                        </b-badge>
                    </button>
                </div>

                <details class="timesheet-add-person__reason-disclosure">
                    <summary>Причина ручного добавления <span class="text-muted">(необязательно)</span></summary>
                    <label class="timesheet-add-person__reason mt-2">
                        <span class="sr-only">Причина ручного добавления</span>
                        <DiscussionEditor v-model="addReason" placeholder="Например: подмена, перевод ещё не оформлен" />
                        <small>Если причину указать, она сохранится в строке и истории.</small>
                    </label>
                </details>

                <b-alert v-if="peopleResults.existing_unresolved" show variant="warning" class="mt-3">
                    <strong>Этот человек уже добавлен без карточки сотрудника.</strong>
                    <p class="mb-2">
                        «{{ peopleResults.existing_unresolved.full_name }}» уже есть
                        <template v-if="peopleResults.existing_unresolved.in_this_timesheet">
                            в этом табеле. Повторно добавлять его не нужно.
                        </template>
                        <template v-else>
                            в другом табеле. Добавляйте его ещё раз только если карточки сотрудника по-прежнему нет.
                        </template>
                    </p>
                    <b-button
                        v-if="peopleResults.existing_unresolved.in_this_timesheet"
                        size="sm"
                        variant="warning"
                        @click="openExistingUnresolvedFromSearch"
                    >
                        Открыть строку и выбрать сотрудника
                    </b-button>
                </b-alert>

                <b-button
                    v-if="peopleQuery.length >= 2
                        && !peopleSearching
                        && peopleSearchCompletedQuery === peopleQuery.trim()"
                    variant="link"
                    class="px-0"
                    @click="showUnresolvedForm = !showUnresolvedForm"
                >
                    Не нашли сотрудника? Добавить без карточки
                </b-button>

                <div v-if="showUnresolvedForm" class="timesheet-add-person__manual">
                    <b-form-input
                        v-model.trim="unresolvedForm.full_name"
                        placeholder="Фамилия Имя Отчество"
                    ></b-form-input>
                    <b-form-input
                        v-model.trim="unresolvedForm.phone"
                        placeholder="Телефон, если известен"
                    ></b-form-input>
                    <b-button
                        variant="warning"
                        :disabled="addingPerson"
                        @click="addUnresolved(false)"
                    >
                        Проверить и добавить
                    </b-button>
                </div>

                <b-alert v-if="matchCandidates" show variant="warning" class="mt-3">
                    <strong>Проверьте: возможно, этот сотрудник уже есть.</strong>
                    <ul v-if="matchCandidates.employees && matchCandidates.employees.length">
                        <li v-for="candidate in matchCandidates.employees" :key="candidate.employee_id">
                            {{ candidate.full_name }} — {{ candidate.match_reasons.join(', ') }}
                            <b-button size="sm" variant="outline-primary" @click="addEmployee(candidate)">
                                Выбрать
                            </b-button>
                        </li>
                    </ul>
                    <p v-if="matchCandidates.unresolved_people && matchCandidates.unresolved_people.length">
                        Человека с таким именем уже добавляли без карточки сотрудника.
                    </p>
                    <b-button size="sm" variant="warning" @click="addUnresolved(true)">
                        Это другой человек — добавить без карточки
                    </b-button>
                </b-alert>
            </section>
            </fieldset>
            <template #modal-footer>
                <div class="w-100">
                    <b-alert v-if="addPersonError" show variant="danger" class="mb-2" role="alert">{{ addPersonError }}</b-alert>
                    <b-alert v-if="personRefreshNotice" show variant="warning" class="mb-2" role="alert">{{ personRefreshNotice }}</b-alert>
                    <div class="d-flex flex-wrap align-items-center justify-content-end">
                        <template v-if="reservePersonForAddition && !personAdditionResult">
                            <b-button variant="outline-secondary" class="mr-auto my-1" :disabled="addingPerson" @click="backToPersonSearch">Назад к поиску</b-button>
                            <b-button variant="primary" class="ml-2 my-1" :disabled="addingPerson" @click="confirmReservePersonAddition">{{ addingPerson ? 'Добавляем…' : 'Добавить строку' }}</b-button>
                        </template>
                        <span v-if="addingPerson" class="mr-auto pr-2" role="status">
                            <b-spinner small class="mr-1"></b-spinner>
                            {{ personAdditionResult ? 'Обновляем табель…' : 'Добавляем…' }}
                        </span>
                        <b-button v-if="personAdditionResult" variant="primary" class="mr-2 my-1"
                            :disabled="addingPerson" @click="retryPersonAdditionRefresh">Обновить табель</b-button>
                        <b-button v-if="!reservePersonForAddition || personAdditionResult" variant="outline-secondary" class="my-1" :disabled="addingPerson"
                            @click="resetPersonForm">Закрыть</b-button>
                    </div>
                </div>
            </template>
            </b-modal>

            <b-modal
                modal-class="timesheet-dialog"
                v-if="canEdit || canResolveUnresolved"
                v-model="plannedArrivalModalVisible"
                title="Подтверждение прибытия"
                centered
                scrollable
                :hide-header-close="plannedMovePendingId !== null"
                :no-close-on-backdrop="plannedMovePendingId !== null"
                :no-close-on-esc="plannedMovePendingId !== null"
                @hidden="resetPlannedArrivalConfirmation"
            >
                <template v-if="plannedArrivalModalItem">
                    <p v-if="hoursArrivalRequirement" class="text-muted">
                        Подтверждение {{ hoursArrivalPosition }} из {{ hoursArrivalTotal }}.
                        {{ hoursArrivalOperation === 'resolution'
                            ? 'Строка будет связана после завершения подтверждений. Сохранённые отметки останутся.'
                            : 'Все отметки сохранятся после завершения подтверждений.' }}
                    </p>
                    <p>
                        Подтвердить, что
                        <strong>{{ plannedArrivalModalItem.employee_name }}</strong>
                        {{ plannedArrivalModalItem.target_type === 'object_group'
                            ? 'прибыл в группу объектов?' : 'прибыл на объект?' }}
                    </p>
                    <p><strong>Объект табеля:</strong> {{ currentObject.name }}</p>
                    <b-form-group
                        label="Фактическая дата прибытия"
                        label-for="planned-arrival-effective-date"
                    >
                        <b-form-input
                            id="planned-arrival-effective-date"
                            v-model="plannedArrivalEffectiveDate"
                            type="date"
                            :min="hoursArrivalRequirement ? null : period.starts_on"
                            :max="plannedArrivalDateMax"
                            :disabled="plannedMovePendingId !== null || Boolean(hoursArrivalRequirement && hoursArrivalRequirement.existing_confirmation)"
                            @input="plannedArrivalConfirmationWarning = null; arrivalDiscrepancyAccepted = false"
                        ></b-form-input>
                        <small v-if="hoursArrivalRequirement && hoursArrivalRequirement.suggested_arrival_date" class="d-block text-muted mt-2">
                            По отметкам в строке предлагаем {{ shortDate(hoursArrivalRequirement.suggested_arrival_date) }}:
                            это первая работа или простой для этого приезда. Проверьте дату и при необходимости измените её.
                        </small>
                        <small v-if="plannedArrivalModalItem.confirmation_hint" class="text-muted">
                            Подтверждаем план на выбранную дату. Существующее назначение не изменится.
                        </small>
                        <small v-else-if="!hoursArrivalRequirement" class="text-muted">
                            {{ plannedArrivalModalItem.target_type === 'object_group'
                                ? 'С этой даты сотрудник будет фактически назначен на группу, в которую входит объект табеля.'
                                : 'С этой даты сотрудник будет фактически назначен на объект.' }}
                            Дата должна входить в период табеля и не может быть будущей.
                        </small>
                    </b-form-group>
                    <b-alert
                        v-if="plannedArrivalModalItem.confirmation_hint"
                        show
                        variant="info"
                    >
                        {{ plannedArrivalModalItem.confirmation_hint }}
                        Никаких назначений снимать не будем.
                    </b-alert>
                    <b-alert
                        v-if="plannedArrivalPreviousPlace"
                        show
                        variant="warning"
                    >
                        {{ plannedArrivalPreviousPlace }}
                    </b-alert>
                    <b-alert
                        v-if="plannedArrivalConfirmationWarning"
                        show
                        variant="warning"
                        class="timesheet-planned-arrival-warning"
                    >
                        <strong>Нужно подтвердить изменения</strong>
                        <span v-if="plannedArrivalConfirmationWarning.plan_mismatch">
                            Выбранная фактическая дата отличается от периода плана на этот объект.
                        </span>
                        <span v-if="plannedArrivalConfirmationWarning.fact_mismatch">
                            На выбранную дату сотрудник числится в другом месте. Его фактическое назначение будет изменено.
                        </span>
                        <small>
                            Название другого объекта здесь не показывается. Если всё верно, нажмите кнопку ещё раз.
                        </small>
                    </b-alert>
                    <b-alert v-if="hoursArrivalAffected.length" show variant="warning">
                        <strong>В табеле есть отметки раньше даты прибытия {{ shortDate(plannedArrivalEffectiveDate) }}.</strong>
                        <div v-for="entry in hoursArrivalAffected" :key="`${entry.line_id}:${entry.work_date}`">
                            {{ shortDate(entry.work_date) }} — {{ timesheetArrivalLabel(entry) }}
                        </div>
                        Отметки и дату прибытия автоматически не исправляем. Объяснение останется в истории.
                    </b-alert>
                </template>
                <template #modal-footer>
                    <div class="w-100">
                        <b-form-group v-if="hoursArrivalAffected.length" label="Объяснение расхождения (обязательно)">
                            <DiscussionEditor v-model="arrivalDiscrepancyReason" :maxlength="2000"
                                :disabled="plannedMovePendingId !== null" aria-label="Объяснение расхождения"></DiscussionEditor>
                            <b-form-checkbox v-model="arrivalDiscrepancyAccepted" :disabled="plannedMovePendingId !== null">
                                Подтверждаю несмотря на несоответствие
                            </b-form-checkbox>
                        </b-form-group>
                    <b-alert v-if="plannedArrivalModalError" show variant="danger" role="alert">
                        {{ plannedArrivalModalError }}
                        <TimesheetEmployeeCardContacts v-if="plannedArrivalReserveError" :timesheet-id="currentTimesheetId()" reserve-return />
                    </b-alert>
                    <div class="d-flex justify-content-end flex-wrap">
                        <b-button
                            class="mr-2 mb-2"
                            variant="outline-secondary"
                            :disabled="plannedMovePendingId !== null"
                            @click="plannedArrivalModalVisible = false"
                        >
                            Не сейчас
                        </b-button>
                        <b-button
                            class="mb-2"
                            variant="success"
                            :disabled="!plannedArrivalDateIsValid || plannedMovePendingId !== null || hoursArrivalBlocked"
                            @click="submitPlannedArrivalConfirmation"
                        >
                            <b-spinner v-if="plannedMovePendingId !== null" small></b-spinner>
                            <template v-else-if="plannedArrivalConfirmationWarning">
                                Подтвердить с изменениями
                            </template>
                            <template v-else>Подтвердить прибытие</template>
                        </b-button>
                    </div>
                    </div>
                </template>
            </b-modal>

            <timesheet-unresolved-resolver
                v-if="unresolvedResolverLine"
                v-model="unresolvedResolverVisible"
                :timesheet-id="sheet.timesheet.id"
                :line="unresolvedResolverLine"
                :codes="sheet.codes || []"
                :policy="bootstrapData.policy || {}"
                :resolve-with-arrivals="resolveUnresolvedWithArrivals"
                @resolved="handleUnresolvedResolved"
            ></timesheet-unresolved-resolver>

            <footer class="timesheet-actions">
                <div v-if="textQualityIssues.length" class="alert alert-danger w-100 mb-1" role="alert">
                    Отправка заблокирована. Полей с ошибками: {{ textQualityIssues.length }}.
                    <b-button size="sm" variant="outline-danger" :disabled="!canEdit" @click="openTextQualityIssue(textQualityIssues[0])">Показать первое поле</b-button>
                </div>
                <div v-if="emptySubmitAttempt" class="w-100"><TimesheetEmptyLines :key="'empty-bottom-' + sheet.timesheet.id" :sheet="sheet" :lines="emptySubmissionLines" :can-edit="canEdit" :prepare="flushTimesheetEdits" :reload="reloadAfterEmptyLines" @busy="rosterExclusionPending = $event" @checked="setEmptyLines" @locate="locateEmptyLine" /></div>
                <div v-if="submitActionError" role="alert" class="alert alert-danger w-100">{{ submitActionError }}</div>
                <template v-if="sheet.permissions.can_review && ['submitted', 'approved', 'hr_editing'].includes(sheet.timesheet.status)">
                    <b-button variant="outline-danger" :disabled="reviewActionPending" @click="returnTimesheet">
                        Вернуть
                    </b-button>
                    <b-button
                        v-if="['submitted', 'hr_editing'].includes(sheet.timesheet.status)"
                        variant="success"
                        :disabled="reviewActionPending || reportMismatchDays.length > 0 || textQualityIssues.length > 0"
                        @click="approveTimesheet"
                    >
                        Утвердить
                    </b-button>
                </template>
                <b-button
                    v-if="sheet.permissions.can_review && ['submitted', 'approved', 'returned'].includes(sheet.timesheet.status)"
                    variant="outline-primary"
                    :disabled="reviewActionPending"
                    @click="openReviewCorrection"
                >
                    <i class="fas fa-edit mr-1" aria-hidden="true"></i>
                    Исправить самому
                </b-button>
                <b-button
                    v-if="canSubmit && ['draft', 'returned'].includes(sheet.timesheet.status)"
                    variant="success"
                    :disabled="submitting || reportMismatchDays.length > 0 || unresolvedLines.length > 0 || textQualityIssues.length > 0"
                    :title="submitBlockerTitle"
                    @click="submitTimesheet"
                >
                    <b-spinner v-if="submitting" small></b-spinner>
                    <i v-else class="fas fa-paper-plane mr-1" aria-hidden="true"></i>
                    Отправить на проверку
                </b-button>
            </footer>

            <timesheet-activity ref="periodActivity"
                :items="activityItems"
                :loading="activityLoading"
                :pending="activityPending"
                @refresh="loadActivity"
                @create-comment="createActivityComment"
                @edit-comment="editActivityComment"
                @delete-comment="deleteActivityComment"
            ></timesheet-activity>
        </template>

        <b-modal v-model="reportDetailsVisible"
            :title="reportReconciliationRequired ? 'Сверка рабочих со сменными отчётами' : 'Рабочие в табеле'"
            content-class="argus-glass-dialog" scrollable ok-only ok-title="Закрыть">
            <p v-if="reportReconciliationRequired">ИТР в сверку не входят. В каждой ячейке таблицы указано: табель / отчёты.</p>
            <p v-else>Для этого объекта сверка с отчётами не требуется. Здесь указаны рабочие без ИТР.</p>
            <div v-for="day in reportDetailsDays" :key="`report-detail-${day.date}`" class="timesheet-report-detail"
                :class="{'text-danger': reportReconciliationRequired && !reconciliationDay(day.date).matches}">
                <strong>{{ shortDate(day.date) }}</strong>
                <span>В табеле {{ reconciliationDay(day.date).timesheet_workers }}<template v-if="reportReconciliationRequired"> · по отчётам {{ reconciliationDay(day.date).report_workers }}</template></span>
                <TimesheetReportLinks v-if="reportReconciliationRequired" :ids="reconciliationDay(day.date).report_ids" />
            </div>
        </b-modal>
        <b-modal v-model="packageDecisionVisible" modal-class="timesheet-dialog" scrollable
            :title="packageDecisionAction === 'exclude' ? 'Исключить табель из комплекта' : 'Отменить исключение табеля'"
            :no-close-on-backdrop="packageDecisionPending" :no-close-on-esc="packageDecisionPending" :hide-header-close="packageDecisionPending">
            <template v-if="packageDecisionItem">
                <p><strong>{{ packageDecisionItem.object_name }}</strong><br>Месяц: {{ packageDecisionMonth }}</p>
                <p v-if="packageDecisionAction === 'exclude'">Данные этого табеля не попадут в расчёт зарплаты за выбранный месяц. Записи сохранятся в черновике.</p>
                <p v-else>Табель снова будет проверяться при передаче комплекта.</p>
                <p v-if="packageDecisionItem.required_by_settings">Обязательному объекту по-прежнему нужен хотя бы один поданный и утверждённый табель.</p>
                <label class="w-100">Причина решения (обязательно)
                    <DiscussionEditor v-model="packageDecisionReason" maxlength="4000" :disabled="packageDecisionPending" />
                </label>
            </template>
            <template #modal-footer>
                <div class="w-100">
                    <b-alert v-if="packageDecisionError" show variant="danger" role="alert">{{ packageDecisionError }}</b-alert>
                    <div class="d-flex flex-wrap justify-content-end">
                        <b-button variant="secondary" class="mr-2" :disabled="packageDecisionPending" @click="packageDecisionVisible = false">Закрыть</b-button>
                        <b-button variant="primary" :disabled="packageDecisionPending || packageDecisionSaved || !packageDecisionReason.trim()" @click="savePackageDecision">
                            {{ packageDecisionSaved ? 'Решение сохранено' : packageDecisionPending ? 'Сохранение…' : 'Сохранить решение' }}
                        </b-button>
                    </div>
                </div>
            </template>
        </b-modal>

        <b-modal
            modal-class="timesheet-dialog"
            v-model="returnModalVisible"
            title="Вернуть табель"
            ok-title="Вернуть"
            cancel-title="Отмена"
            ok-variant="danger"
            :ok-disabled="!returnReason.trim() || reviewActionPending || Boolean(sheet && sheet.submission_deadline && sheet.submission_deadline.applies && !returnDueDate)"
            :cancel-disabled="reviewActionPending"
            @ok="confirmReturnTimesheet"
        >
            <label v-if="sheet && sheet.submission_deadline && sheet.submission_deadline.applies" class="w-100">
                Подать исправления не позднее (23:59:59, Минск)
                <b-form-input v-model="returnDueDate" type="date" required />
            </label>
            <label class="w-100">
                <span class="d-block mb-2">Что должен исправить автор табеля?</span>
                <DiscussionEditor
                    v-model="returnReason"
                    autofocus
                    placeholder="Причина возврата обязательна"
                ></DiscussionEditor>
            </label>
        </b-modal>

        <b-modal
            modal-class="timesheet-dialog"
            v-model="correctionModalVisible"
            title="Исправить табель самостоятельно"
            ok-title="Открыть исправление"
            cancel-title="Отмена"
            ok-variant="primary"
            :ok-disabled="!correctionReason.trim() || reviewActionPending"
            :cancel-disabled="reviewActionPending"
            @ok="confirmReviewCorrection"
        >
            <p>
                Правки табельщика попадут в историю. После исправлений табель можно утвердить без повторной отправки прорабом.
            </p>
            <label class="w-100">
                <span class="d-block mb-2">Почему табельщик вносит исправления?</span>
                <DiscussionEditor
                    v-model="correctionReason"
                    autofocus
                    placeholder="Причина обязательна"
                ></DiscussionEditor>
            </label>
        </b-modal>

        <employee-inspector
            :employee="inspectorEmployee"
            :loading="inspectorLoading"
            :board-date="inspectorContext ? inspectorContext.date : selectedDate || period.starts_on"
            :timesheet-id="inspectorContext ? inspectorContext.timesheetId : currentTimesheetId()"
            :access-message="inspectorAccessMessage"
            :assignment-notices="inspectorAssignmentNotices"
            :can-move="false"
            :can-manage-characteristics="inspectorCanManage('characteristics')"
            :can-manage-absences="inspectorCanManage('absences')"
            @close="closeEmployeeInspector"
            @characteristic-pin-changed="refreshEmployeeInspector"
            @characteristic-added="refreshEmployeeInspector"
            @characteristic-updated="refreshEmployeeInspector"
            @absence-added="refreshEmployeeInspector"
            @reserve-returned="refreshEmployeeInspector"
        ></employee-inspector>

        <timesheet-assessment-editor v-if="assessmentLine" v-model="assessmentModalVisible"
            :key="assessmentContextKey" :line="assessmentLine" :sheet-id="sheet.timesheet.id"
            :company="$store.getters.currentUser.company"
            :user-id="$store.getters.currentUser.userId || $store.getters.currentUser.user_id || $store.getters.currentUser.id"
            :editable="canEdit" :fields="characteristicFields" :ktu-options="ktuOptions" :ktu-config="ktuConfig"
            :ktu-enabled="ktuEnabled" :initial-ktu="assessmentInitialKtu" :text-quality-enabled="textQualityEnabled"
            :focus-field="assessmentFocusField" @saved="acceptAssessmentResult" />
    </main>
</template>

<script>
import api from '@/utils/api';
import textQuality from '../../../../shared/textQuality';
import TimesheetTextQualityNotice from './TimesheetTextQualityNotice.vue';
import timesheetEditing from './timesheetEditing';
import timesheetCharacteristics from './timesheetCharacteristics';
import TimesheetAssessmentEditor from './TimesheetAssessmentEditor.vue';
import TimesheetCopyNames from './TimesheetCopyNames.vue';
import timesheetEmployeeSearch from './timesheetEmployeeSearch';
import timesheetEmployeeFocus from './timesheetEmployeeFocus';
import TimesheetEmployeeSearch from './TimesheetEmployeeSearch.vue';
import timesheetArrivalEditing from './timesheetArrivalEditing';
import timesheetLayout from './timesheetLayout';
import TimesheetEntryInput from './TimesheetEntryInput.vue';
import TimesheetReadCell from './TimesheetReadCell.vue';
import TimesheetCellPicker from './TimesheetCellPicker.vue';
import TimesheetGridFrame from './TimesheetGridFrame.vue';
import TimesheetEditProblems from './TimesheetEditProblems.vue';
import TimesheetSyncStatus from './TimesheetSyncStatus.vue';
import TimesheetAssessmentStatus from './TimesheetAssessmentStatus.vue';
import TimesheetMonthRows from './TimesheetMonthRows.vue';
import TimesheetRowActions from './TimesheetRowActions.vue';
import TimesheetRosterExclusionDialog from './TimesheetRosterExclusionDialog.vue';
import TimesheetBulkResult from './TimesheetBulkResult.vue';
import TimesheetEmptyLines from './TimesheetEmptyLines.vue';
import timesheetRosterActions from './timesheetRosterActions';
import WorkPeriodBar from '@/components/WorkPeriodBar.vue';
import {activeEditorForUser, parsePresenceCellKey, revealCollaborationCell} from '@/utils/collaborationPresence';
import timesheetPeriodNavigation from './timesheetPeriodNavigation';
import timesheetPersonAdding from './timesheetPersonAdding';
import timesheetManualRemoval from './timesheetManualRemoval';
import timesheetReviewNavigation from './timesheetReviewNavigation';
import TimesheetReviewGrid from './TimesheetReviewGrid.vue';
import TimesheetReviewDraftsHelp from './TimesheetReviewDraftsHelp.vue';
import readiness from '@/utils/timesheetReadiness';
import EmployeeLink from '@/components/EmployeeLink.vue';
import {socket} from '@/plugins/socket';
import timesheetGridIndex from '@/utils/timesheetGridIndex';
import timesheetKeyboardNavigation from '@/utils/timesheetKeyboardNavigation';
import timesheetHours from '@/utils/timesheetHours';
import timesheetReportLinks from '@/utils/timesheetReportLinks';
import TimesheetReportLinks from './TimesheetReportLinks.vue';
import employeeNameFormat from '@/utils/employeeNameFormat';
import assignmentAuthorship from '@/utils/timesheetAssignmentAuthorship';
import conflictSources from '@/utils/timesheetConflictSources';
import TimesheetConflictSources from './TimesheetConflictSources.vue';
import TimesheetConflictPopover from './TimesheetConflictPopover.vue';
import EmployeeInspector from '../EmployeesWorkforce/EmployeeInspector.vue';
import TimesheetActivity from './TimesheetActivity.vue';
import TimesheetSubmissionDeadline from './TimesheetSubmissionDeadline.vue';
import TimesheetStreams from './TimesheetStreams.vue';
import TimesheetHistoryView from './TimesheetHistoryView.vue';
import TimesheetAssessmentLink from './TimesheetAssessmentLink.vue';
import TimesheetUnresolvedResolver from './TimesheetUnresolvedResolver.vue';
import TimesheetEmployeeCardContacts from './TimesheetEmployeeCardContacts.vue';
import timesheetAttention from './timesheetAttention';

function localMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function localDate() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export default {
    provide() { return {discussionContext: () => ({type: 'timesheet', id: this.currentTimesheetId()})}; },
    directives: {timesheetAttention},
    name: 'TimesheetWorkspace',
    mixins: [timesheetLayout, timesheetRosterActions, timesheetEditing, timesheetCharacteristics, timesheetArrivalEditing, timesheetPeriodNavigation, timesheetPersonAdding, timesheetManualRemoval, timesheetReviewNavigation, timesheetEmployeeSearch, timesheetEmployeeFocus],
    components: {
        TimesheetAssessmentEditor, TimesheetCopyNames,
        TimesheetTextQualityNotice,
        TimesheetRowActions, TimesheetRosterExclusionDialog, TimesheetBulkResult,
        EmployeeLink,
        TimesheetEmployeeSearch,
        TimesheetCellPicker,
        TimesheetGridFrame,
        TimesheetEntryInput,
        TimesheetReadCell,
        TimesheetEditProblems,
        TimesheetSyncStatus,
        TimesheetAssessmentStatus,
        TimesheetMonthRows,
        TimesheetConflictSources,
        TimesheetConflictPopover,
        TimesheetReportLinks,
        TimesheetSubmissionDeadline,
        WorkPeriodBar,
        TimesheetReviewGrid,
        TimesheetEmptyLines,
        TimesheetReviewDraftsHelp,
        TimesheetStreams,
        TimesheetHistoryView,
        EmployeeInspector,
        TimesheetActivity,
        TimesheetAssessmentLink,
        TimesheetUnresolvedResolver,
        TimesheetEmployeeCardContacts
    },
    data() {
        return {
            reviewDraftsHelpCounted: false,
            conflictSelection: null,
            sourcePopover: null,
            sourcePopoverKey: 0,
            bootstrapData: {objects: [], codes: [], policy: {}, permissions: {}},
            historicalSheets: [],
            periodHistoricalObjects: [],
            loaded: false,
            loading: false,
            buildingId: null,
            objectQuery: '',
            objectPickerOpen: false,
            periodMonth: localMonth(),
            selectedDate: localDate(),
            sheet: null,
            entryDrafts: {},
            monthCellDrafts: {},
            lineDetailDrafts: {},
            bulkValue: '8',
            bulkError: '',
            assigningIncidentId: null,
            plannedMovePendingId: null,
            plannedArrivalsExpanded: true,
            plannedArrivalModalVisible: false,
            plannedArrivalModalItem: null,
            plannedArrivalEffectiveDate: localDate(),
            plannedArrivalModalError: '',
            plannedArrivalReserveError: false,
            plannedArrivalConfirmationWarning: null,
            pendingPlannedArrivalFromAdd: null,
            mobileMode: 'day',
            mobileMonthLayout: 'weeks',
            mobileNamesPinned: true,
            mobileNamesPreviewing: false,
            mobileNamesHintVisible: false,
            inspectorEmployee: null,
            inspectorContext: null,
            inspectorLoading: false,
            inspectorAccessMessage: '',
            inspectorRequestId: 0,
            mobileWeekKey: '',
            isCompactViewport: false,
            viewportMediaQuery: null,
            collapsedWeekKeys: {},
            weekLayoutPeriod: '',
            showDesktopAssessments: false,
            showMonthlyTotals: false,
            absencePreviewOpen: false,
            absenceApplying: false,
            absenceResolvingKey: null,
            absenceUndo: null,
            errorMessage: '',
            successMessage: '',
            plannedArrivalLinkMessage: '',
            handledArrivalRequest: '',
            sharingTimesheetLink: false,
            addPersonModalVisible: false,
            assessmentModalVisible: false, assessmentInitialKtu: null,
            assessmentLineId: null,
            assessmentFocusField: '',
            assessmentReadinessForced: false,
            assessmentOmissionReasonDraft: '',
            assessmentOmissionReasonTimesheetId: null,
            peopleQuery: '',
            peopleSearching: false,
            peopleSearchCompletedQuery: '',
            peopleSearchTimer: null,
            peopleSearchRequestId: 0,
            peopleSearchError: '',
            peopleResults: {employees: [], unresolved_people: [], existing_unresolved: null},
            addReason: '',
            addPersonError: '',
            showUnresolvedForm: false,
            unresolvedForm: {full_name: '', phone: ''},
            matchCandidates: null,
            addingPerson: false,
            addingEmployeeId: null,
            removingLineId: null,
            unresolvedResolverVisible: false,
            unresolvedResolverLineId: null,
            unresolvedUndoPendingId: null,
            submitting: false, emptySubmitAttempt: false, submitActionError: '',
            reviewMode: false,
            reviewSheets: [],
            reviewObjectSearch: '',
            reviewStatusFilter: '',
            reviewAttentionOnly: false,
            periodSummary: null,
            packageDecisionVisible: false,
            packageDecisionItem: null,
            packageDecisionAction: 'exclude',
            packageDecisionMonth: '',
            packageDecisionReason: '',
            packageDecisionError: '',
            packageDecisionPending: false,
            packageDecisionSaved: false,
            reviewLoading: false,
            handoffPending: false,
            reviewActionPending: false,
            returnModalVisible: false,
            returnReason: '',
            returnDueDate: '',
            correctionModalVisible: false,
            correctionReason: '',
            activityItems: [],
            activityLoading: false,
            activityPending: false,
            editSessionId: '',
            editSessionKind: 'edit',
            editSessionLastActivity: 0,
            sheetRequestId: 0,
            syncStatus: 'connecting',
            syncNotice: '',
            remoteChangePending: false,
            bootstrapRefreshPending: false,
            realtimeEditingActive: false,
            realtimeLastRevision: 0,
            realtimeRefreshTimer: null,
            rosterSyncTimer: null,
            rosterSyncPending: false,
            rosterSyncReloadPending: false,
            rosterSyncInFlight: false,
            realtimePollingTimer: null,
            realtimeNoticeTimer: null,
            realtimeFocusTimer: null,
            realtimeActorName: null,
            watchedTimesheetId: null,
            editingPresenceKey: null,
            presenceHeartbeatTimer: null,
            presenceBlurTimer: null,
            presenceCleanupTimer: null,
            remoteEditors: {},
            seenIssueCueKeys: {}
        };
    },
    computed: {
        gridApi() { return this; },
        keyboardRows() { return this.filteredLines.map(line => String(line.id)); },
        keyboardRowIndex() { return new Map(this.keyboardRows.map((id, index) => [id, index])); },
        keyboardDates() {
            if (this.isCompactViewport && this.mobileMode === 'day') return [this.selectedDate];
            if (this.isCompactViewport && this.mobileMonthLayout !== 'full') return this.selectedMobileWeek.days.map(day => day.date);
            return this.visibleMonthColumns.filter(column => column.type === 'day').map(column => column.date);
        },
        keyboardDateIndex() { return new Map(this.keyboardDates.map((date, index) => [date, index])); },
        remoteEditorsByCell() {
            const index = new Map();
            Object.values(this.remoteEditors).forEach(editor => {
                if (!index.has(editor.cell_key)) index.set(editor.cell_key, []);
                index.get(editor.cell_key).push(editor);
            });
            return index;
        },
        minuteTotalsByDate() {
            const totals = {};
            (this.sheet && this.sheet.lines || []).forEach(line => (line.entries || []).forEach(entry => {
                totals[entry.work_date] = (totals[entry.work_date] || 0) + Number(entry.worked_minutes || 0);
            }));
            return totals;
        },
        conflictDetails() { return this.conflictDetailsFor(this.conflictSelection); },
        sourcePopoverDays() { return this.conflictDetailsFor(this.sourcePopover); },
        reviewStatusOptions() {
            return [{value: '', text: 'Все статусы'}].concat(Array.from(new Set((this.periodSummary && this.periodSummary.items || [])
                .map(readiness.statusKey)))
                .map(status => ({value: status, text: status === 'excluded' ? 'Исключён настройками' : status === 'excluded_by_review' ? 'Исключён проверяющим' : this.statusText(status)})));
        },
        reviewReadinessGroups() { return readiness.groupObjects(this.periodSummary && this.periodSummary.items || []); },
        reviewReadinessCounts() {
            const groups = this.reviewReadinessGroups;
            return {all: groups.length, actionable: groups.filter(group => group.readiness.actionable).length,
                ready: groups.filter(group => group.readiness.key === 'ready').length,
                quiet: groups.filter(group => ['empty', 'excluded'].includes(group.readiness.key)).length};
        },
        reviewReadinessSections() { return readiness.sections(this.filteredReviewObjects, this.isHistoricalReviewPeriod); },
        reviewReadinessFilterCount() { return Number(Boolean(this.reviewObjectSearch.trim())) + Number(Boolean(this.reviewStatusFilter)) + Number(this.reviewAttentionOnly); },
        reviewReadinessStatusLabel() { const option = this.reviewStatusOptions.find(option => option.value === this.reviewStatusFilter); return option ? option.text : this.reviewStatusFilter; },
        filteredReviewObjects() {
            return readiness.groupObjects(this.periodSummary && this.periodSummary.items || [],
                {search: this.reviewObjectSearch, status: this.reviewStatusFilter, onlyActionable: this.reviewAttentionOnly});
        },
        isHistoricalSheet() {
            return Boolean(this.sheet && (this.sheet.timesheet.source === 'legacy_google' || this.sheet.timesheet.status === 'historical_locked' || this.sheet.timesheet.record_kind === 'pilot'));
        },
        isHistoricalReviewPeriod() {
            return Boolean((this.reviewGrid && this.reviewGrid.historical) || (this.periodSummary && this.periodSummary.historical));
        },
        collaborationUserIds() {
            return Object.values(this.remoteEditors).map(editor => Number(editor.actor && editor.actor.user_id));
        },
        selectedObjectMultiple() {
            return (this.bootstrapData.objects || []).find(object => Number(object.building_id) === Number(this.buildingId) && object.multiple_timesheets);
        },
        editableObjects() {
            return (this.bootstrapData.objects || []).filter(object => (
                (object.can_edit || (object.multiple_timesheets && object.can_view)) && object.is_active
            ));
        },
        availableObjects() {
            const objects = new Map((this.bootstrapData.objects || [])
                .filter(object => (object.can_view || object.can_edit) && object.is_active)
                .map(object => [Number(object.building_id), object]));
            this.periodHistoricalObjects.forEach(object => {
                if (!objects.has(Number(object.building_id))) objects.set(Number(object.building_id), object);
            });
            return Array.from(objects.values());
        },
        objectInputOptions() {
            const nameCounts = this.availableObjects.reduce((result, object) => {
                const name = String(object.name || '').trim();
                result[name] = (result[name] || 0) + 1;
                return result;
            }, {});
            return this.availableObjects.map(object => ({
                    value: Number(object.building_id),
                    text: nameCounts[String(object.name || '').trim()] > 1
                        ? `${object.name} · #${object.building_id}`
                        : object.name,
                    hint: object.historical_only ? 'Исторический табель · только чтение' : object.contract_name || object.name
                }));
        },
        filteredObjectInputOptions() {
            const normalized = String(this.objectQuery || '').trim().toLocaleLowerCase('ru-RU');
            const selected = this.objectInputOptions.find(option => (
                Number(option.value) === Number(this.buildingId)
            ));
            if (
                !normalized
                || (selected && String(selected.text).trim().toLocaleLowerCase('ru-RU') === normalized)
            ) {
                return this.objectInputOptions;
            }
            return this.objectInputOptions.filter(option => (
                `${option.text || ''} ${option.hint || ''}`
                    .toLocaleLowerCase('ru-RU')
                    .includes(normalized)
            ));
        },
        codeById() {
            return (this.bootstrapData.codes || []).reduce((result, code) => {
                result[Number(code.id)] = code;
                return result;
            }, {});
        },
        absenceOptions() {
            const categories = ['absence', 'day_off', 'downtime'];
            return [{value: '', text: '—'}].concat(
                (this.bootstrapData.codes || []).filter(code => categories.includes(code.category)).map(code => ({
                    value: code.code,
                    text: `${code.code} — ${code.name}`
                }))
            );
        },
        travelOptions() {
            return [{value: '', text: '—'}].concat(
                (this.bootstrapData.codes || []).filter(code => code.category === 'travel').map(code => ({
                    value: code.code,
                    text: `${code.code} — ${code.name}`
                }))
            );
        },
        legendCodeGroups() {
            const labels = {
                work: 'Работа',
                absence: 'Отсутствие',
                day_off: 'Выходные и отдых',
                downtime: 'Простой',
                travel: 'Дорога и командировка'
            };
            const order = ['work', 'absence', 'day_off', 'downtime', 'travel'];
            const groups = (this.bootstrapData.codes || []).reduce((result, code) => {
                const key = code.category || 'other';
                if (!result[key]) result[key] = [];
                result[key].push(code);
                return result;
            }, {});
            return Object.keys(groups).sort((left, right) => {
                const leftIndex = order.indexOf(left);
                const rightIndex = order.indexOf(right);
                return (leftIndex === -1 ? order.length : leftIndex) -
                    (rightIndex === -1 ? order.length : rightIndex);
            }).map(key => ({
                key,
                label: labels[key] || 'Другие коды',
                codes: groups[key].slice().sort((left, right) => (
                    String(left.code).localeCompare(String(right.code), 'ru', {numeric: true})
                ))
            }));
        },
        characteristicFields() {
            return Array.isArray(this.bootstrapData.policy.characteristic_fields)
                ? this.bootstrapData.policy.characteristic_fields
                : [];
        },
        visibleCharacteristicFields() {
            return this.showDesktopAssessments ? this.characteristicFields : [];
        },
        ktuConfig() {
            return this.bootstrapData.policy.ktu || {
                enabled: true,
                minimum: 0,
                maximum: 5,
                step: 0.5,
                neutral: 4,
                critical_below: 3,
                criteria: []
            };
        },
        ktuEnabled() {
            return this.ktuConfig.enabled !== false;
        },
        ktuOptions() {
            const result = [{value: '', text: 'Выберите КТУ'}];
            for (let value = 5; value >= 0; value -= 0.5) {
                result.push({
                    value: String(value),
                    text: `${String(value).replace('.', ',')} — ${this.ktuScoreLabel(value)}`
                });
            }
            return result;
        },
        assessmentReadinessIssues() {
            return this.sortedLines.reduce((result, line) => (
                result.concat(this.assessmentIssuesForLine(line, false))
            ), []);
        },
        textQualityEnabled() {
            return this.bootstrapData.policy.text_quality_enabled === true && this.sheet
                && ['draft', 'returned', 'hr_editing'].includes(this.sheet.timesheet.status);
        },
        textQualityIssues() {
            return this.assessmentReadinessIssues.filter(issue => issue.type === 'invalid_characteristic_text');
        },
        assessmentMissingCharacteristicLines() {
            return this.sortedLines.filter(line => this.assessmentIssuesForLine(line, true)
                .some(issue => issue.type === 'missing_characteristics'));
        },
        assessmentIssueGroups() {
            const groups = new Map();
            this.assessmentReadinessIssues.forEach(issue => {
                const key = Number(issue.line_id);
                if (!groups.has(key)) {
                    groups.set(key, {
                        line_id: key,
                        employee_name: issue.employee_name,
                        messages: []
                    });
                }
                groups.get(key).messages.push(issue.message);
            });
            return Array.from(groups.values());
        },
        assessmentReadyCount() {
            return Math.max(0, this.sortedLines.length - this.assessmentIssueGroups.length);
        },
        assessmentIsReady() {
            return this.assessmentReadinessIssues.length === 0;
        },
        assessmentReminderDue() {
            if (!this.period.ends_on) return false;
            const reminderStart = new Date(`${this.period.ends_on}T00:00:00.000Z`);
            if (Number.isNaN(reminderStart.getTime())) return false;
            reminderStart.setUTCDate(reminderStart.getUTCDate() - 6);
            return localDate() >= reminderStart.toISOString().slice(0, 10);
        },
        showAssessmentReadiness() {
            return this.assessmentReadinessForced || this.assessmentReminderDue;
        },
        period() {
            return this.sheet ? this.sheet.period : {starts_on: '', ends_on: ''};
        },
        plannedArrivals() {
            return this.sheet && Array.isArray(this.sheet.planned_arrivals)
                ? this.sheet.planned_arrivals
                : [];
        },
        overduePlannedArrivalsCount() {
            return this.plannedArrivals.filter(arrival => (
                this.plannedArrivalIsOverdue(arrival)
            )).length;
        },
        plannedMatchesForAdd() {
            const tokens = String(this.peopleQuery || '')
                .toLocaleLowerCase('ru-RU')
                .trim()
                .split(/\s+/)
                .filter(Boolean);
            if (!tokens.length) return this.plannedArrivals;
            return this.plannedArrivals.filter(arrival => {
                const name = String(arrival.employee_name || '').toLocaleLowerCase('ru-RU');
                return tokens.every(token => name.includes(token));
            });
        },
        regularPeopleResults() {
            const plannedEmployeeIds = new Set(this.plannedArrivals.map(arrival => (
                Number(arrival.employee_id)
            )));
            return (this.peopleResults.employees || []).filter(person => (
                person.excluded_from_timesheet || !plannedEmployeeIds.has(Number(person.employee_id))
            ));
        },
        plannedArrivalDateMax() {
            const today = localDate();
            if (this.hoursArrivalRequirement) return today;
            return this.period.ends_on && this.period.ends_on < today
                ? this.period.ends_on
                : today;
        },
        plannedArrivalDateIsValid() {
            const value = String(this.plannedArrivalEffectiveDate || '');
            return Boolean(
                /^\d{4}-\d{2}-\d{2}$/.test(value)
                && (this.hoursArrivalRequirement || !this.period.starts_on || value >= this.period.starts_on)
                && (!this.plannedArrivalDateMax || value <= this.plannedArrivalDateMax)
            );
        },
        plannedArrivalPreviousPlace() {
            const arrival = this.plannedArrivalModalItem;
            if (!arrival || !arrival.changes_primary_assignment) return '';
            return arrival.previous_assignment_visible && arrival.previous_assignment_name
                ? `Основное место сотрудника «${arrival.previous_assignment_name}» будет изменено.`
                : 'Основное место сотрудника на другом объекте будет изменено.';
        },
        currentObject() {
            return this.sheet ? this.sheet.object : {};
        },
        canEdit() {
            return Boolean(
                this.sheet &&
                this.sheet.permissions.can_edit &&
                ['draft', 'returned', 'hr_editing'].includes(this.sheet.timesheet.status)
            );
        },
        canSubmit() {
            return Boolean(this.sheet && this.sheet.permissions.can_submit);
        },
        canResolveUnresolved() {
            return Boolean(
                this.sheet
                && this.sheet.permissions.can_resolve_unresolved
                && ['draft', 'returned', 'hr_editing'].includes(this.sheet.timesheet.status)
            );
        },
        unresolvedLines() {
            return this.sheet
                ? (this.sheet.lines || []).filter(line => line.unresolved_person_id)
                : [];
        },
        unresolvedResolverLine() {
            return this.unresolvedLines.find(line => (
                Number(line.id) === Number(this.unresolvedResolverLineId)
            )) || null;
        },
        activeUnresolvedMerges() {
            return this.sheet && Array.isArray(this.sheet.unresolved_merges)
                ? this.sheet.unresolved_merges
                : [];
        },
        emptySubmissionLines() {
            const candidates = this.sheet && this.sheet.empty_lines && this.sheet.empty_lines.lines || [];
            return candidates.filter(item => {
                const line = this.sheet.lines.find(l => Number(l.id) === item.line_id);
                return line && !(line.entries || []).some(e => Number(e.worked_minutes) > 0 || e.absence_code_id || e.travel_code_id || e.other_code_id || String(e.note || '').trim())
                    && (line.ktu === null || line.ktu === undefined || line.ktu === '') && !(line.ktu_evidence || []).length
                    && !String(line.ktu_comment || '').trim() && !Object.values(line.characteristic_values || {}).some(v => v != null && String(v).trim());
            });
        },
        submitBlockerTitle() {
            if (this.textQualityIssues.length) return 'Сначала исправьте характеристики: список полей находится над табелем';
            if (this.unresolvedLines.length) {
                return 'Сначала сопоставьте всех людей без карточки сотрудника';
            }
            if (this.reportMismatchDays.length) {
                return 'Сначала выровняйте количество рабочих с ежесменными отчётами';
            }
            return '';
        },
        reportReconciliation() {
            return this.sheet && this.sheet.report_reconciliation
                ? this.sheet.report_reconciliation
                : {days: [], matches: true, blocking: false};
        },
        reportCountRows() {
            const rows = [{field: 'timesheet_workers', label: 'В табеле'}];
            if (this.reportReconciliationRequired) rows.push({field: 'report_workers', label: 'В сменных отчётах'});
            return rows;
        },
        reportMismatchDays() {
            if (!this.reportReconciliationRequired) return [];
            return (this.reportReconciliation.days || []).filter(day => !day.matches);
        },
        reportReconciliationRequired() {
            return this.reportReconciliation.required !== false;
        },
        inspectorAssignmentNotices() {
            const line = this.sheet && (this.sheet.lines || []).find(item => this.inspectorEmployee &&
                Number(item.employee_id) === Number(this.inspectorEmployee.employee_id));
            return assignmentAuthorship.assignmentNotices(line && line.assignment_evidence,
                this.isCompactViewport && this.mobileMode === 'day' ? this.selectedDate : null,
                this.sheet && this.sheet.object && this.sheet.object.name);
        },
        reconciliationDayIndex() {
            return new Map((this.reportReconciliation.days || []).map(day => (
                [day.date, day]
            )));
        },
        monthDayIndex() {
            return new Map(this.monthDays.map((day, index) => [day.date, index]));
        },
        absenceSuggestions() {
            if (!this.sheet) return [];
            return (this.sheet.lines || []).reduce((result, line) => {
                (line.absence_days || []).forEach(day => {
                    if (day.state === 'suggestion' && day.can_apply) result.push({line, day});
                });
                return result;
            }, []);
        },
        absenceDiscrepancies() {
            if (!this.sheet) return [];
            const states = [
                'conflict', 'source_changed', 'source_removed', 'overlap',
                'configuration_error'
            ];
            return (this.sheet.lines || []).reduce((result, line) => {
                (line.absence_days || []).forEach(day => {
                    if (states.includes(day.state)) result.push({line, day});
                });
                return result;
            }, []);
        },
        absencePreviewGroups() {
            const groups = new Map();
            this.absenceSuggestions.forEach(item => {
                const lineId = Number(item.line.id);
                if (!groups.has(lineId)) {
                    groups.set(lineId, {line: item.line, days: []});
                }
                groups.get(lineId).days.push(item.day);
            });
            return Array.from(groups.values());
        },
        absenceWarningGroups() {
            if (!this.sheet) return [];
            return (this.sheet.lines || []).map(line => ({
                line,
                messages: (line.absence_warnings || []).map(item => item.message).filter(Boolean)
            })).filter(group => group.messages.length);
        },
        selectedDayReconciliation() {
            return this.reconciliationDayIndex.get(this.selectedDate) || {
                date: this.selectedDate,
                report_workers: 0,
                timesheet_workers: 0,
                report_ids: [],
                matches: true
            };
        },
        statusLabel() {
            const labels = {
                draft: 'Черновик',
                missing: 'Не подан',
                no_data: 'Нет данных за месяц',
                returned: 'Возвращён',
                hr_editing: 'Исправляет табельщик',
                submitted: 'На проверке',
                approved: 'Утверждён',
                historical_locked: 'Исторический',
                pilot_closed: 'Не используется'
            };
            return labels[this.sheet && this.sheet.timesheet.status] || 'Табель';
        },
        statusVariant() {
            const variants = {
                draft: 'secondary',
                returned: 'warning',
                hr_editing: 'warning',
                submitted: 'primary',
                approved: 'success',
                historical_locked: 'dark',
                pilot_closed: 'dark'
            };
            return variants[this.sheet && this.sheet.timesheet.status] || 'secondary';
        },
        displayMode() {
            return this.isCompactViewport ? this.mobileMode : 'month';
        },
        isMobileFullMonthView() {
            return this.isCompactViewport
                && this.mobileMode === 'month'
                && this.mobileMonthLayout === 'full';
        },
        sortedLines() {
            if (!this.sheet) return [];
            return (this.sheet.lines || []).slice().sort((left, right) => (
                (this.lineGroupKey(left) === this.lineGroupKey(right)
                    ? String(left.employee_name_snapshot || '').localeCompare(
                        String(right.employee_name_snapshot || ''),
                        'ru',
                        {sensitivity: 'base', numeric: true}
                    )
                    : (this.lineGroupKey(left) === 'itr' ? -1 : 1))
            ));
        },
        monthDays() {
            if (!this.period.starts_on || !this.period.ends_on) return [];
            const weekdays = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
            const days = [];
            const cursor = new Date(`${this.period.starts_on}T00:00:00.000Z`);
            const end = new Date(`${this.period.ends_on}T00:00:00.000Z`);
            while (cursor <= end) {
                const date = cursor.toISOString().slice(0, 10);
                const weekday = cursor.getUTCDay();
                days.push({
                    date,
                    day: cursor.getUTCDate(),
                    weekday: weekdays[weekday],
                    is_weekend: weekday === 0 || weekday === 6
                });
                cursor.setUTCDate(cursor.getUTCDate() + 1);
            }
            return days;
        },
        monthWeeks() {
            const groups = [];
            this.monthDays.forEach(day => {
                const cursor = new Date(`${day.date}T00:00:00.000Z`);
                const mondayOffset = (cursor.getUTCDay() + 6) % 7;
                cursor.setUTCDate(cursor.getUTCDate() - mondayOffset);
                const key = cursor.toISOString().slice(0, 10);
                let group = groups.find(item => item.key === key);
                if (!group) {
                    group = {key, days: []};
                    groups.push(group);
                }
                group.days.push(day);
            });
            return groups.map((group, index) => ({
                key: group.key,
                index,
                days: group.days,
                label: `Неделя ${index + 1}`,
                range: `${group.days[0].day}–${group.days[group.days.length - 1].day}`
            }));
        },
        visibleMonthColumns() {
            return this.monthWeeks.reduce((columns, week) => {
                if (this.isWeekCollapsed(week.key)) {
                    columns.push({
                        type: 'week',
                        key: `week-${week.key}`,
                        week_key: week.key,
                        week
                    });
                    return columns;
                }
                week.days.forEach(day => columns.push(Object.assign({
                    type: 'day',
                    key: day.date,
                    week_key: week.key
                }, day)));
                return columns;
            }, []);
        },
        selectedMobileWeek() {
            return this.monthWeeks.find(week => week.key === this.mobileWeekKey) ||
                this.monthWeeks.find(week => week.days.some(day => day.date === this.selectedDate)) ||
                this.monthWeeks[0] || {key: '', days: [], label: '', range: ''};
        },
        monthAddRowColspan() {
            const totalColumns = this.showMonthlyTotals ? 3 : 1;
            return 1 + this.visibleMonthColumns.length + totalColumns + 1 +
                this.visibleCharacteristicFields.length;
        },
        serviceRowTailColspan() {
            return (this.showMonthlyTotals ? 3 : 1) + 1 + this.visibleCharacteristicFields.length;
        },
        monthTableColumnCount() {
            return 1 + (this.ktuEnabled ? 1 : 0) + this.monthAddRowColspan;
        },
        assessmentLine() {
            return this.sortedLines.find(line => Number(line.id) === Number(this.assessmentLineId)) || null;
        },
        cellSuggestionOptions() {
            const suggestions = [];
            const known = new Set();
            const maximum = Number(this.bootstrapData.policy.maximum_daily_minutes || 1440);
            const step = Math.max(1, Number(this.bootstrapData.policy.time_step_minutes) || 15);
            const hoursOptions = Array.from({length: Math.floor(maximum / step)}, (value, index) => (
                this.minutesLabel((index + 1) * step)
            ));
            const add = (value, label) => {
                const text = String(value || '').trim();
                if (!text || known.has(text)) return;
                known.add(text);
                suggestions.push({value: text, label: label || text});
            };
            hoursOptions.forEach(hours => add(hours, `${hours} ч`));
            (this.bootstrapData.codes || []).forEach(code => add(code.code, code.name));
            const travelCodes = (this.bootstrapData.codes || []).filter(code => (
                code.category === 'travel' && !this.isStandaloneCode(code)
            ));
            travelCodes.forEach(code => {
                hoursOptions.forEach(hours => (
                    add(`${hours} ${code.code}`, this.travelSuggestionLabel(hours, code))
                ));
            });
            if (this.sheet) {
                this.sheet.lines.forEach(line => (line.entries || []).forEach(entry => {
                    const minutes = Number(entry.worked_minutes || 0);
                    if (minutes > maximum || minutes % step !== 0) return;
                    const absence = entry.absence_code_id
                        ? this.codeById[Number(entry.absence_code_id)]
                        : null;
                    const travel = entry.travel_code_id
                        ? this.codeById[Number(entry.travel_code_id)]
                        : null;
                    if (
                        Number(entry.worked_minutes) > 0 &&
                        [absence, travel].some(code => this.isStandaloneCode(code))
                    ) {
                        return;
                    }
                    add(this.entryDisplay(entry), 'Уже использовалось в этом табеле');
                }));
            }
            return suggestions;
        },
        monthTotalHours() {
            if (!this.sheet) return '0';
            const minutes = this.sheet.lines.reduce((sum, line) => (
                sum + this.lineMonthMinutes(line)
            ), 0);
            return this.minutesLabel(minutes);
        },
        monthPersonDays() {
            const totals = {};
            ['all', 'itr', 'workers'].forEach(key => {
                totals[key] = {calculated: 0, calendarDays: new Set()};
            });
            (this.sheet && this.sheet.lines || []).forEach(line => {
                const groups = [totals.all, totals[this.lineGroupKey(line)]];
                const calculated = this.lineWorkDays(line);
                groups.forEach(group => { group.calculated += calculated; });
                (line.entries || []).forEach(entry => {
                    if (Number(entry.worked_minutes) > 0 && entry.work_date) {
                        const key = `${line.employee_id || `line:${line.id}`}:${entry.work_date}`;
                        groups.forEach(group => group.calendarDays.add(key));
                    }
                });
            });
            return Object.keys(totals).reduce((result, key) => {
                result[key] = {calculated: Number(totals[key].calculated.toFixed(4)), calendar: totals[key].calendarDays.size};
                return result;
            }, {});
        },
        monthPersonDaysTooltip() {
            const days = this.monthPersonDays;
            return {html: true, title: `<div class="text-left">По расчёту: <strong>${days.all.calculated}</strong> чел.-дн.<br>` +
                `ИТР: ${days.itr.calculated}; остальные: ${days.workers.calculated}.<br><br>` +
                `По календарю: <strong>${days.all.calendar}</strong> чел.-дн.<br>` +
                `ИТР: ${days.itr.calendar}; остальные: ${days.workers.calendar}.<br><br>` +
                'По расчёту — действующие правила табеля, включая дорогу.<br>' +
                'По календарю — один день на сотрудника с любым количеством отработанных часов. ' +
                'Дорога с часами входит, дорога без часов — нет. Только этот табель за выбранный месяц.</div>'};
        },
        openIncidentCount() {
            if (!this.sheet) return 0;
            return this.sheet.lines.reduce((sum, line) => sum + (line.incidents || []).length, 0);
        },
        dayTotalHours() {
            if (!this.sheet) return '0';
            const minutes = this.sheet.lines.reduce((sum, line) => {
                const entry = this.entryForDate(line);
                return sum + Number(entry && entry.worked_minutes || 0);
            }, 0);
            return this.minutesLabel(minutes);
        },
        currentDayIncidentCount() {
            if (!this.sheet) return 0;
            return this.sheet.lines.reduce((sum, line) => (
                sum + this.incidentsForDate(line).length
            ), 0);
        },
        syncStatusLabel() {
            if (this.syncStatus === 'connected') return 'Совместное редактирование включено';
            if (this.syncStatus === 'offline') return 'Нет сети';
            if (this.syncStatus === 'connecting') return 'Подключение…';
            return 'Переподключение…';
        },
        realtimeInteractionActive() {
            return Boolean(
                this.periodGuardPending || this.periodSaveCount || this.periodDraftsDirty || this.periodSaveFailed ||
                this.realtimeEditingActive ||
                this.editingPresenceKey ||
                this.absenceApplying ||
                this.assigningIncidentId ||
                this.plannedMovePendingId !== null ||
                this.addingPerson ||
                this.removingLineId !== null ||
                this.submitting ||
                this.reviewActionPending ||
                this.addPersonModalVisible ||
                this.assessmentModalVisible ||
                this.rosterSyncInFlight ||
                this.loading
            );
        }
    },
    watch: {
        '$route.query.arrival_key'(value) {
            if (!value) this.handledArrivalRequest = '';
        },
        sheet() { this.closeSourcePopover(); },
        peopleQuery(value) {
            this.schedulePeopleSearch(value);
        },
        realtimeInteractionActive(active) {
            if (!active) this.$nextTick(this.flushQueuedRealtimeRefresh);
        },
    },
    async mounted() {
        if (typeof window !== 'undefined' && window.matchMedia) {
            this.viewportMediaQuery = window.matchMedia(
                '(max-width: 720px), '
                + '(hover: none) and (pointer: coarse) '
                + 'and (max-height: 767.98px) and (orientation: landscape)'
            );
            this.syncViewportMode(this.viewportMediaQuery);
            if (this.viewportMediaQuery.addEventListener) {
                this.viewportMediaQuery.addEventListener('change', this.syncViewportMode);
            } else if (this.viewportMediaQuery.addListener) {
                this.viewportMediaQuery.addListener(this.syncViewportMode);
            }
        }
        await this.loadBootstrap();
        if (this.loaded) {
            this.periodRouteReady = true;
            await this.loadPeriodRoute(this.$route);
        }
        this.setupRealtimeSync();
    },
    beforeDestroy() {
        this.inspectorRequestId += 1;
        this.peopleSearchRequestId += 1;
        if (this.peopleSearchTimer) clearTimeout(this.peopleSearchTimer);
        if (this._mobileNamesPreviewTimer) {
            window.clearTimeout(this._mobileNamesPreviewTimer);
            this._mobileNamesPreviewTimer = null;
        }
        if (this._mobileNamesHintTimer) {
            window.clearTimeout(this._mobileNamesHintTimer);
            this._mobileNamesHintTimer = null;
        }
        this.teardownRealtimeSync();
        if (this.viewportMediaQuery) {
            if (this.viewportMediaQuery.removeEventListener) {
                this.viewportMediaQuery.removeEventListener('change', this.syncViewportMode);
            } else if (this.viewportMediaQuery.removeListener) {
                this.viewportMediaQuery.removeListener(this.syncViewportMode);
            }
        }
    },
    methods: {
        onMonthGridScroll() { if (this.$refs.monthRows) this.$refs.monthRows.schedule(); },
        conflictDetailsFor(selection) {
            const line = selection && this.sheet && this.sheet.lines.find(item => Number(item.id) === selection.lineId);
            if (!line) return [];
            return selection.dates.map(date => {
                const incidents = this.dateIncidentsFor(line, date).filter(item => ['cross_object_overlap', 'excessive_daily_time'].includes(item.incident_type));
                const related = incidents.find(item => item.related_entries && item.related_entries.length);
                return {date, name: line.employee_name_snapshot, local: this.entryDisplay(this.entryFor(line, date)) || 'Не заполнено',
                    messages: incidents.map(item => this.incidentText(item)),
                    entries: related ? related.related_entries : this.externalFor(line, date)};
            });
        },
        hasSourceIssue(groups) {
            return (groups || []).some(group => ['cross_object_overlap', 'excessive_daily_time'].includes(group.type));
        },
        showSourcePopover(event, line, groups, focusContent = false) {
            this.markIssueGroupsSeen(groups);
            if (!this.hasSourceIssue(groups)) return;
            const target = event.currentTarget;
            if (this.sourcePopover && this.sourcePopover.target === target) {
                if (focusContent) this.sourcePopover.focusContent = true;
                return;
            }
            const dates = Array.from(new Set(groups.filter(group => this.hasSourceIssue([group]))
                .reduce((all, group) => all.concat(group.items.map(item => item.date).filter(Boolean)), []))).sort();
            this.sourcePopover = {target, key: ++this.sourcePopoverKey, lineId: Number(line.id), dates, focusContent};
        },
        closeSourcePopover(target) {
            if (this.sourcePopover && (!target || this.sourcePopover.target === target)) this.sourcePopover = null;
        },
        applySubmissionDeadline(value) {
            if (!this.sheet || Number(this.sheet.timesheet.id) !== Number(value.timesheet_id)) return;
            this.$set(this.sheet, 'submission_deadline', value);
            this.$set(this.sheet.permissions, 'can_submit', value.can_submit);
        },
        onTimesheetAccessRevoked(event) {
            if (Number(event.timesheet_id) !== Number(this.currentTimesheetId())) return;
            this.clearCurrentTimesheet();
            this.errorMessage = 'Доступ к табелю отозван после замены согласующего.';
        },
        pluralize(value, one, few, many) {
            const number = Math.abs(Number(value)) % 100;
            const last = number % 10;
            if (number > 10 && number < 20) return many;
            if (last > 1 && last < 5) return few;
            if (last === 1) return one;
            return many;
        },
        reconciliationDay(date) {
            return this.reconciliationDayIndex.get(date) || {
                date,
                report_workers: 0,
                timesheet_workers: 0,
                report_ids: [],
                matches: true
            };
        },
        reportColumnDays(column) {
            return column.type === 'week' ? column.week.days : [column];
        },
        reportColumnCount(column, field) {
            return this.reportColumnDays(column).reduce((sum, day) => (
                sum + Number(this.reconciliationDay(day.date)[field] || 0)
            ), 0);
        },
        reportColumnDisplayCount(column, field) {
            const days = this.reportColumnDays(column);
            const emptyFuture = days.every(day => {
                const item = this.reconciliationDay(day.date);
                return day.date > localDate() && !Number(item.timesheet_workers) && !Number(item.report_workers)
                    && !(item.report_ids || []).length;
            });
            return emptyFuture ? '—' : this.reportColumnCount(column, field);
        },
        reportColumnIds(column) {
            return timesheetReportLinks.normalizeReportIds(this.reportColumnDays(column).reduce((ids, day) => (
                ids.concat(this.reconciliationDay(day.date).report_ids || [])
            ), []));
        },
        reportColumnClass(column) {
            const days = this.reportColumnDays(column).map(day => this.reconciliationDay(day.date));
            return {
                'is-weekend': column.type === 'day' && column.is_weekend,
                'is-week-summary': column.type === 'week',
                'is-mismatch': this.reportReconciliationRequired && days.some(day => !day.matches)
            };
        },
        reportColumnTitle(column) {
            const days = this.reportColumnDays(column).map(day => this.reconciliationDay(day.date));
            return days.map(day => {
                if (!this.reportReconciliationRequired) {
                    return `${this.shortDate(day.date)}: в табеле ${day.timesheet_workers}. Сверка с отчётами не требуется`;
                }
                const reports = day.report_ids && day.report_ids.length
                    ? `, отчёты № ${day.report_ids.join(', ')}`
                    : '';
                return `${this.shortDate(day.date)}: по отчётам ${day.report_workers}, в табеле ${day.timesheet_workers}${reports}`;
            }).join('\n');
        },
        reportSummaryLabel(reconciliation) {
            if (reconciliation.required === false) return 'Сверка с отчётами не требуется';
            const mismatches = (reconciliation.days || []).filter(day => !day.matches).length;
            return mismatches
                ? `Не совпадает ${mismatches} ${this.pluralize(mismatches, 'день', 'дня', 'дней')}`
                : 'Количество рабочих совпадает с отчётами';
        },
        lineGroupKey(line) {
            return line && line.employee_category === 'itr' ? 'itr' : 'workers';
        },
        lineGroupLabel(line) {
            return this.lineGroupKey(line) === 'itr' ? 'ИТР' : 'Остальные сотрудники';
        },
        lineGroupStartsAt(index) {
            if (index === 0) return true;
            return this.lineGroupKey(this.filteredLines[index]) !==
                this.lineGroupKey(this.filteredLines[index - 1]);
        },
        browserStorage() {
            return typeof window !== 'undefined' ? window.localStorage : null;
        },
        setupRealtimeSync() {
            socket.on('connect', this.onRealtimeConnect);
            socket.on('disconnect', this.onRealtimeDisconnect);
            socket.on('connect_error', this.onRealtimeConnectError);
            socket.on('timesheet:changed', this.onTimesheetChanged);
            socket.on('timesheet:access-revoked', this.onTimesheetAccessRevoked);
            socket.on('timesheet:presence', this.onTimesheetPresence);
            window.addEventListener('online', this.onBrowserOnline);
            window.addEventListener('offline', this.onBrowserOffline);
            window.addEventListener('focus', this.onBrowserFocus);
            document.addEventListener('visibilitychange', this.onVisibilityChange);
            document.addEventListener('focusin', this.onDocumentFocusChange);
            document.addEventListener('focusout', this.onDocumentFocusChange);
            this.presenceCleanupTimer = window.setInterval(this.cleanupRemoteEditors, 5000);
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                this.syncStatus = 'offline';
                this.startRealtimePolling();
                return;
            }
            this.syncStatus = socket.connected ? 'connecting' : 'reconnecting';
            if (socket.connected) this.subscribeTimesheetSocket();
            else socket.connect();
        },
        teardownRealtimeSync() {
            this.emitEditingPresence(null);
            socket.off('connect', this.onRealtimeConnect);
            socket.off('disconnect', this.onRealtimeDisconnect);
            socket.off('connect_error', this.onRealtimeConnectError);
            socket.off('timesheet:changed', this.onTimesheetChanged);
            socket.off('timesheet:access-revoked', this.onTimesheetAccessRevoked);
            socket.off('timesheet:presence', this.onTimesheetPresence);
            if (socket.connected) {
                socket.emit('timesheet:unwatch');
                socket.emit('timesheet:unsubscribe');
            }
            window.removeEventListener('online', this.onBrowserOnline);
            window.removeEventListener('offline', this.onBrowserOffline);
            window.removeEventListener('focus', this.onBrowserFocus);
            document.removeEventListener('visibilitychange', this.onVisibilityChange);
            document.removeEventListener('focusin', this.onDocumentFocusChange);
            document.removeEventListener('focusout', this.onDocumentFocusChange);
            this.stopRealtimePolling();
            this.stopPresenceHeartbeat();
            [
                'realtimeRefreshTimer',
                'rosterSyncTimer',
                'realtimeNoticeTimer',
                'realtimeFocusTimer',
                'presenceBlurTimer',
                'presenceCleanupTimer'
            ].forEach(field => {
                if (this[field]) window.clearTimeout(this[field]);
                this[field] = null;
            });
            this.remoteEditors = {};
            this.rosterSyncPending = false;
            this.rosterSyncReloadPending = false;
        },
        subscribeTimesheetSocket() {
            const storage = this.browserStorage();
            const token = storage ? storage.getItem('token') : null;
            if (!socket.connected || !token) {
                this.syncStatus = 'offline';
                this.startRealtimePolling();
                return;
            }
            this.syncStatus = 'connecting';
            this.startRealtimePolling();
            socket.emit('timesheet:subscribe', {token}, response => {
                if (response && response.ok === true) {
                    this.syncStatus = 'connected';
                    this.stopRealtimePolling();
                    this.watchCurrentTimesheet();
                    this.scheduleRealtimeRefresh(null, true);
                    this.scheduleAssignedRosterSync(true);
                    return;
                }
                this.syncStatus = 'reconnecting';
                this.startRealtimePolling();
            });
        },
        onRealtimeConnect() {
            this.subscribeTimesheetSocket();
        },
        onRealtimeDisconnect() {
            this.syncStatus = typeof navigator !== 'undefined' && navigator.onLine === false
                ? 'offline'
                : 'reconnecting';
            this.watchedTimesheetId = null;
            this.remoteEditors = {};
            this.startRealtimePolling();
        },
        onRealtimeConnectError() {
            this.onRealtimeDisconnect();
        },
        onBrowserOnline() {
            this.syncStatus = 'reconnecting';
            this.startRealtimePolling();
            if (socket.connected) this.subscribeTimesheetSocket();
            else socket.connect();
            this.scheduleRealtimeRefresh(null, true);
            this.scheduleAssignedRosterSync(true);
        },
        onBrowserOffline() {
            this.syncStatus = 'offline';
            this.remoteEditors = {};
            this.startRealtimePolling();
        },
        onBrowserFocus() {
            this.bootstrapRefreshPending = true;
            if (socket.connected && this.syncStatus !== 'connected') {
                this.subscribeTimesheetSocket();
            }
            this.scheduleRealtimeRefresh(null, true);
            this.scheduleAssignedRosterSync(true);
        },
        onVisibilityChange() {
            if (document.visibilityState === 'visible') {
                this.bootstrapRefreshPending = true;
                this.scheduleRealtimeRefresh(null, true);
                this.scheduleAssignedRosterSync(true);
            }
        },
        onDocumentFocusChange() {
            if (this.realtimeFocusTimer) window.clearTimeout(this.realtimeFocusTimer);
            this.realtimeFocusTimer = window.setTimeout(() => {
                this.realtimeFocusTimer = null;
                const active = document.activeElement;
                this.realtimeEditingActive = Boolean(active && active.matches && active.matches(
                    'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"]'
                ));
            }, 0);
        },
        startRealtimePolling() {
            if (this.realtimePollingTimer) return;
            this.realtimePollingTimer = window.setInterval(() => {
                if (
                    document.visibilityState === 'visible' &&
                    (typeof navigator === 'undefined' || navigator.onLine !== false)
                ) {
                    if (socket.connected && this.syncStatus !== 'connected') {
                        this.subscribeTimesheetSocket();
                    }
                    this.scheduleRealtimeRefresh(null, true);
                    this.scheduleAssignedRosterSync(true);
                }
            }, 10000);
        },
        stopRealtimePolling() {
            if (!this.realtimePollingTimer) return;
            window.clearInterval(this.realtimePollingTimer);
            this.realtimePollingTimer = null;
        },
        onTimesheetChanged(event) {
            if (event && event.mutation_id && this._ownTimesheetMutations.has(event.mutation_id)) return;
            const revision = Number(event && event.revision || 0);
            if (revision && revision <= this.realtimeLastRevision) return;
            if (revision) this.realtimeLastRevision = revision;
            // The deadline panel refreshes its own DTO without rebuilding cell drafts.
            if (event && event.change_type === 'submission_deadline_changed') return;
            if (event && event.change_type === 'settings_changed') this.bootstrapRefreshPending = true;
            if (event && event.change_type === 'workforce_assignment_changed') {
                this.scheduleAssignedRosterSync(false, true);
                return;
            }
            this.remoteChangePending = true;
            this.scheduleRealtimeRefresh(event, false);
        },
        canSyncAssignedRoster() {
            return Boolean(
                this.sheet &&
                this.sheet.permissions &&
                this.sheet.permissions.can_edit &&
                ['draft', 'returned', 'hr_editing'].includes(this.sheet.timesheet.status)
            );
        },
        scheduleAssignedRosterSync(immediate, reload) {
            if (!this.canSyncAssignedRoster()) {
                this.rosterSyncPending = false;
                this.rosterSyncReloadPending = false;
                return;
            }
            this.rosterSyncPending = true;
            this.rosterSyncReloadPending = this.rosterSyncReloadPending || reload === true;
            if (this.realtimeInteractionActive || this.rosterSyncInFlight) return;
            if (this.rosterSyncTimer) window.clearTimeout(this.rosterSyncTimer);
            this.rosterSyncTimer = window.setTimeout(() => {
                this.rosterSyncTimer = null;
                this.syncAssignedRoster();
            }, immediate ? 0 : 300);
        },
        async syncAssignedRoster(options = {}) {
            if (options.reload) this.rosterSyncReloadPending = true;
            if (!this.canSyncAssignedRoster()) {
                this.rosterSyncPending = false;
                this.rosterSyncReloadPending = false;
                return 0;
            }
            if ((this.realtimeInteractionActive && !options.force) || this.rosterSyncInFlight) {
                this.rosterSyncPending = true;
                return 0;
            }
            const timesheetId = Number(this.sheet.timesheet.id);
            const reloadAfterSync = this.rosterSyncReloadPending;
            this.rosterSyncPending = false;
            this.rosterSyncReloadPending = false;
            this.rosterSyncInFlight = true;
            try {
                const response = await api.post(`/api/timesheets/${timesheetId}/roster/sync`);
                const addedCount = Number(response.data && response.data.roster_added_count || 0);
                const removedCount = Number(response.data && response.data.roster_removed_count || 0);
                if (
                    (addedCount > 0 || removedCount > 0 || reloadAfterSync) &&
                    timesheetId === this.currentTimesheetId()
                ) {
                    await this.loadTimesheet(timesheetId, {fromRealtime: true});
                    if (options.notify !== false && (addedCount > 0 || removedCount > 0)) {
                        const changes = [];
                        if (addedCount) changes.push(`Добавлено сотрудников: ${addedCount}`);
                        if (removedCount) changes.push(`Убрано пустых строк без назначения: ${removedCount}. Подробности — в истории табеля`);
                        this.showSyncNotice(
                            changes.join('. ')
                        );
                    }
                }
                return addedCount;
            } catch (error) {
                this.errorMessage = this.responseMessage(
                    error,
                    'Не удалось обновить состав табеля по назначениям с доски.'
                );
                return 0;
            } finally {
                this.rosterSyncInFlight = false;
                if (this.rosterSyncPending) {
                    this.scheduleAssignedRosterSync(true, this.rosterSyncReloadPending);
                }
            }
        },
        scheduleRealtimeRefresh(event, immediate) {
            if (event && event.actor && event.actor.display_name) {
                this.realtimeActorName = event.actor.display_name;
            }
            if (this.realtimeInteractionActive) {
                this.remoteChangePending = true;
                return;
            }
            if (this.realtimeRefreshTimer) window.clearTimeout(this.realtimeRefreshTimer);
            this.realtimeRefreshTimer = window.setTimeout(() => {
                this.realtimeRefreshTimer = null;
                this.refreshTimesheetFromRealtime();
            }, immediate ? 0 : 250);
        },
        flushQueuedRealtimeRefresh() {
            if (this.realtimeInteractionActive) return;
            if (this.rosterSyncPending) this.scheduleAssignedRosterSync(true);
            if (this.remoteChangePending) this.scheduleRealtimeRefresh(null, true);
        },
        async refreshTimesheetFromRealtime() {
            if (this.realtimeInteractionActive) {
                this.remoteChangePending = true;
                return;
            }
            const actorName = this.realtimeActorName;
            const hadPendingSignal = this.remoteChangePending;
            let loaded = false;
            try {
                if (this.bootstrapRefreshPending) {
                    this.bootstrapRefreshPending = false;
                    try {
                        const response = await api.get('/api/timesheets/bootstrap', {ignoreModal: true});
                        const selectedId = this.buildingId;
                        this.bootstrapData = response.data;
                        this.selectObjectById(selectedId);
                        await this.$nextTick();
                        if (this.$refs.namedTimesheets) await this.$refs.namedTimesheets.load();
                        loaded = true;
                    } catch (error) {
                        this.bootstrapRefreshPending = true;
                        throw error;
                    }
                }
                if (this.reviewMode) {
                    loaded = await this.refreshActiveReview();
                } else if (this.sheet && this.sheet.timesheet) {
                    loaded = await this.loadTimesheet(this.sheet.timesheet.id, {fromRealtime: true});
                }
            } catch (error) {
                if (error && error.response && error.response.status === 403) {
                    this.clearCurrentTimesheet();
                }
                this.errorMessage = this.responseMessage(error, 'Не удалось синхронизировать табель.');
            }
            if (!loaded) return;
            this.remoteChangePending = false;
            this.realtimeActorName = null;
            if (actorName || hadPendingSignal) {
                this.showSyncNotice(actorName ? `Табель обновил ${actorName}` : 'Табель синхронизирован');
            }
        },
        showSyncNotice(message) {
            this.syncNotice = message;
            if (this.realtimeNoticeTimer) window.clearTimeout(this.realtimeNoticeTimer);
            this.realtimeNoticeTimer = window.setTimeout(() => {
                this.syncNotice = '';
                this.realtimeNoticeTimer = null;
            }, 3500);
        },
        watchCurrentTimesheet() {
            const timesheetId = Number(this.sheet && this.sheet.timesheet && this.sheet.timesheet.id);
            if (!socket.connected || this.syncStatus !== 'connected' || !timesheetId) return;
            if (Number(this.watchedTimesheetId) === timesheetId) return;
            this.remoteEditors = {};
            socket.emit('timesheet:watch', {timesheet_id: timesheetId}, response => {
                const currentId = Number(this.sheet && this.sheet.timesheet && this.sheet.timesheet.id);
                if (response && response.ok === true && currentId === timesheetId) {
                    this.watchedTimesheetId = timesheetId;
                    if (this.editingPresenceKey) {
                        this.emitEditingPresence(this.editingPresenceKey);
                    }
                }
            });
        },
        entryPresenceKey(lineId, date) {
            return `entry:${lineId}:${date}`;
        },
        async followCollaborator(person) {
            const editor = activeEditorForUser(this.remoteEditors, person.user_id);
            const target = editor && parsePresenceCellKey(editor.cell_key);
            const sheetId = this.currentTimesheetId();
            if (!target || !this.sortedLines.some(line => Number(line.id) === target.lineId)) return;
            if (target.date) {
                const week = this.monthWeeks.find(item => item.days.some(day => day.date === target.date));
                if (!week) return;
                this.$set(this.collapsedWeekKeys, week.key, false);
            } else {
                if (target.field === 'ktu' && !this.ktuEnabled) return;
                if (target.field.startsWith('characteristic:')) {
                    if (!this.characteristicFields.some(field => `characteristic:${field.key}` === target.field)) return;
                    this.showDesktopAssessments = true;
                }
            }
            if (this.isCompactViewport) {
                this.mobileMode = 'month';
                this.mobileMonthLayout = 'full';
            }
            this.revealEmployeeSearchLine(target.lineId);
            await this.$nextTick();
            if (this._isDestroyed || this.reviewMode || this.currentTimesheetId() !== sheetId) return;
            if (this.$refs.monthRows) await this.$refs.monthRows.reveal(target.lineId);
            const cells = Array.from(this.$el.querySelectorAll('[data-presence-key]'));
            const cell = cells.find(element => element.getAttribute('data-presence-key') === editor.cell_key && element.getClientRects().length);
            revealCollaborationCell(cell, person.color);
        },
        detailPresenceKey(lineId, field) {
            return `detail:${lineId}:${field}`;
        },
        handleEntryFocus(line, date, event) {
            this._cellNavigationToken = (this._cellNavigationToken || 0) + 1;
            if (event && event.target && event.target.select) event.target.select();
            this.ensureCellEdit(line, date);
            this.startEditingPresence(this.entryPresenceKey(line.id, date));
        },
        handleEntryBlur(line, date) {
            this.handleMonthCellChange(line, date);
            this.stopEditingPresence(this.entryPresenceKey(line.id, date));
        },
        handleMonthCellChange(line, date) {
            return this.saveMonthCell(line, date);
        },
        revealTimesheetControl(control) {
            const viewport = this.$refs.monthScroll;
            if (!viewport || !viewport.contains(control)) return;
            const view = viewport.getBoundingClientRect();
            const header = viewport.querySelector('thead');
            const footer = viewport.querySelector('tfoot td');
            const row = control.closest('tr');
            const person = row && row.querySelector('.timesheet-month-table__person');
            const number = row && row.querySelector('.timesheet-month-table__number');
            let left = view.left;
            [number, person].filter(Boolean).forEach(cell => {
                if (window.getComputedStyle(cell).position === 'sticky') left = Math.max(left, cell.getBoundingClientRect().right);
            });
            const top = view.top + (header ? header.getBoundingClientRect().height : 0);
            const bottom = footer ? Math.min(view.top + viewport.clientHeight, footer.getBoundingClientRect().top)
                : view.top + viewport.clientHeight;
            const bounds = control.getBoundingClientRect();
            const right = view.left + viewport.clientWidth;
            // Read all geometry before scrolling; native focus does not account
            // for sticky names/header/footer and can leave the editor covered.
            if (bounds.left < left) viewport.scrollLeft -= left - bounds.left;
            else if (bounds.right > right) viewport.scrollLeft += bounds.right - right;
            if (bounds.top < top) viewport.scrollTop -= top - bounds.top;
            else if (bounds.bottom > bottom) viewport.scrollTop += bounds.bottom - bottom;
        },
        selectEmployeeMonth(line, event) {
            if (this._cellPicker) this._cellPicker.selectMonth(line, event.currentTarget);
        },
        async focusTimesheetCell(lineId, date) {
            this.revealEmployeeSearchLine(lineId);
            const sheetId = this.currentTimesheetId();
            const token = this._cellNavigationToken = (this._cellNavigationToken || 0) + 1;
            const selector = `.timesheet-keyboard-cell[data-line-id="${lineId}"][data-work-date="${date}"]:not([disabled])`;
            const focus = control => {
                control.focus();
                this.revealTimesheetControl(control);
                return true;
            };
            const existing = this.$el.querySelector(selector);
            // Neighbouring rows are already in the overscan window; focus synchronously.
            if (existing && existing.getClientRects().length) return focus(existing);
            if (this.$refs.monthRows) await this.$refs.monthRows.reveal(lineId);
            await this.$nextTick();
            if (this._isDestroyed || sheetId !== this.currentTimesheetId() || token !== this._cellNavigationToken) return false;
            const cells = Array.from(this.$el.querySelectorAll('[data-line-id="' + lineId + '"][data-work-date="' + date + '"]'));
            const cell = cells.find(element => element.getClientRects().length) || cells[0];
            const control = cell && (cell.matches('.timesheet-keyboard-cell:not([disabled])') ? cell : cell.querySelector('input:not([disabled]), button:not([disabled]), [tabindex]'));
            if (!control || typeof control.focus !== 'function') return false;
            return focus(control);
        },
        handleCellArrow(line, date, event) {
            if (!event || event.defaultPrevented || event.isComposing || event.altKey || event.ctrlKey || event.metaKey) return;
            const tab = event.key === 'Tab';
            if (event.shiftKey && !tab) return;
            const input = event.currentTarget || event.target;
            if (!tab && (!input || !timesheetKeyboardNavigation.shouldNavigateFromInput(event.key, input.value, input.selectionStart, input.selectionEnd))) return;
            const row = this.keyboardRowIndex.get(String(line.id));
            const column = this.keyboardDateIndex.get(date);
            if (row === undefined || column === undefined) return;
            let nextRow = row;
            let nextColumn = column;
            if (tab) {
                nextColumn += event.shiftKey ? -1 : 1;
                if (nextColumn < 0) { nextRow -= 1; nextColumn = this.keyboardDates.length - 1; }
                if (nextColumn >= this.keyboardDates.length) { nextRow += 1; nextColumn = 0; }
            } else {
                nextRow += event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
                nextColumn += event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
            }
            const targetId = this.keyboardRows[nextRow];
            const targetDate = this.keyboardDates[nextColumn];
            if (!targetId || !targetDate) { if (!tab) event.preventDefault(); return; }
            event.preventDefault();
            this.handleMonthCellChange(line, date);
            this.focusTimesheetCell(targetId, targetDate);
        },
        handleCellEnter(line, date, event) {
            if (event && event.isComposing) return;
            this.handleMonthCellChange(line, date);
            const index = this.keyboardRowIndex.get(String(line.id));
            const nextId = this.keyboardRows[index + 1];
            if (nextId) this.focusTimesheetCell(nextId, date);
        },
        handleDetailFocus(lineId, field, event) {
            const line = this.sheet.lines.find(item => Number(item.id) === Number(lineId));
            if (event && event.target && event.target.select) event.target.select();
            this.startEditingPresence(this.detailPresenceKey(lineId, field));
        },
        handleDetailBlur(lineId, field) {
            const line = this.sheet.lines.find(item => Number(item.id) === Number(lineId));
            this.stopEditingPresence(this.detailPresenceKey(lineId, field));
        },
        startEditingPresence(cellKey) {
            if (this.presenceBlurTimer) window.clearTimeout(this.presenceBlurTimer);
            if (this.editingPresenceKey && this.editingPresenceKey !== cellKey) {
                this.emitEditingPresence(null);
            }
            this.editingPresenceKey = cellKey;
            this.emitEditingPresence(cellKey);
            this.startPresenceHeartbeat();
        },
        stopEditingPresence(cellKey) {
            if (this.presenceBlurTimer) window.clearTimeout(this.presenceBlurTimer);
            this.presenceBlurTimer = window.setTimeout(() => {
                this.presenceBlurTimer = null;
                if (this.editingPresenceKey !== cellKey) return;
                this.emitEditingPresence(null);
                this.editingPresenceKey = null;
                this.stopPresenceHeartbeat();
            }, 80);
        },
        stopAllEditingPresence() {
            if (this.presenceBlurTimer) window.clearTimeout(this.presenceBlurTimer);
            this.presenceBlurTimer = null;
            this.emitEditingPresence(null);
            this.editingPresenceKey = null;
            this.stopPresenceHeartbeat();
        },
        emitEditingPresence(cellKey) {
            const timesheetId = Number(this.sheet && this.sheet.timesheet && this.sheet.timesheet.id);
            if (
                !socket.connected ||
                this.syncStatus !== 'connected' ||
                Number(this.watchedTimesheetId) !== timesheetId
            ) return;
            socket.emit('timesheet:editing', {
                timesheet_id: timesheetId,
                cell_key: cellKey
            });
        },
        startPresenceHeartbeat() {
            if (this.presenceHeartbeatTimer) return;
            this.presenceHeartbeatTimer = window.setInterval(() => {
                if (this.editingPresenceKey) this.emitEditingPresence(this.editingPresenceKey);
            }, 7000);
        },
        stopPresenceHeartbeat() {
            if (!this.presenceHeartbeatTimer) return;
            window.clearInterval(this.presenceHeartbeatTimer);
            this.presenceHeartbeatTimer = null;
        },
        onTimesheetPresence(event) {
            const timesheetId = Number(this.sheet && this.sheet.timesheet && this.sheet.timesheet.id);
            if (!event || Number(event.timesheet_id) !== timesheetId || !event.presence_id) return;
            if (event.cell_key === null) {
                this.$delete(this.remoteEditors, event.presence_id);
                return;
            }
            this.$set(this.remoteEditors, event.presence_id, {
                cell_key: event.cell_key,
                actor: event.actor || {},
                color: this.presenceColor(event),
                seen_at: Date.now()
            });
        },
        cleanupRemoteEditors() {
            const deadline = Date.now() - 20000;
            Object.keys(this.remoteEditors).forEach(id => {
                if (Number(this.remoteEditors[id].seen_at) < deadline) this.$delete(this.remoteEditors, id);
            });
        },
        presenceColor(event) {
            const palette = ['#7c3aed', '#db2777', '#059669', '#d97706', '#2563eb', '#dc2626'];
            const source = String(event && event.actor && event.actor.user_id || event.presence_id || '0');
            let hash = 0;
            for (let index = 0; index < source.length; index += 1) {
                hash = ((hash * 31) + source.charCodeAt(index)) >>> 0;
            }
            return palette[hash % palette.length];
        },
        remoteEditorsFor(cellKey) { return this.remoteEditorsByCell.get(cellKey) || []; },
        remoteEditorFor(cellKey) {
            return this.remoteEditorsFor(cellKey)[0] || null;
        },
        remoteEditorLabel(cellKey) {
            const editors = this.remoteEditorsFor(cellKey);
            if (!editors.length) return '';
            const name = editors[0].actor && editors[0].actor.display_name || 'Коллега';
            return editors.length > 1 ? `${name} +${editors.length - 1}` : name;
        },
        presenceCellClass(cellKey) {
            return {'has-remote-editor': Boolean(this.remoteEditorFor(cellKey))};
        },
        presenceCellStyle(cellKey) {
            const editor = this.remoteEditorFor(cellKey);
            return editor ? {'--timesheet-presence-color': editor.color} : {};
        },
        syncViewportMode(event) {
            this.isCompactViewport = Boolean(event && event.matches);
            if (this.isCompactViewport) this.syncMobileWeekToDate();
        },
        responseMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (
                data
                && data.code === 'TIMESHEET_ASSESSMENT_INCOMPLETE'
                && data.details
                && Array.isArray(data.details.issues)
            ) {
                const reasons = data.details.issues.slice(0, 8).map(issue => (
                    `${issue.employee_name || 'Сотрудник'}: ${issue.message || 'нужно дополнить'}`
                ));
                const tail = data.details.issues.length > reasons.length
                    ? `; и ещё ${data.details.issues.length - reasons.length}`
                    : '';
                return `${data.msg || fallback} ${reasons.join('; ')}${tail}`;
            }
            return data && data.msg ? data.msg : fallback;
        },
        routeTimesheetId(value) {
            const source = value === undefined
                ? this.$route.params && this.$route.params.timesheetId
                : value;
            const timesheetId = Number(source);
            return Number.isInteger(timesheetId) && timesheetId > 0 ? timesheetId : null;
        },
        currentTimesheetId() {
            const timesheetId = Number(this.sheet && this.sheet.timesheet && this.sheet.timesheet.id);
            return Number.isInteger(timesheetId) && timesheetId > 0 ? timesheetId : null;
        },
        lineEmployeeName(line) {
            const fullName = String(line && line.employee_name_snapshot || '').trim();
            if (!this.isCompactViewport || !fullName) return fullName;
            const parts = fullName.split(/\s+/).filter(Boolean);
            const existingInitials = parts.slice(1).join('');
            if (/^(?:[A-ZА-ЯЁ]\.){1,2}$/i.test(existingInitials)) {
                return `${parts[0]} ${existingInitials.toLocaleUpperCase('ru-RU')}`;
            }
            return employeeNameFormat.formatEmployeeName({
                employee_id: line && line.employee_id,
                full_name: fullName,
                last_name: parts[0] || '',
                first_name: parts[1] || '',
                middle_name: parts.slice(2).join(' ')
            }, 'initials');
        },
        inspectorCanManage(section) {
            const manage = this.inspectorEmployee
                && this.inspectorEmployee.card_access
                && this.inspectorEmployee.card_access.manage;
            return Boolean(manage && manage[section] === true);
        },
        employeeInspectorPlaceholder(line) {
            const name = String(line && line.employee_name_snapshot || '').trim();
            return {
                employee_id: Number(line && line.employee_id),
                display_name: name,
                full_name: name,
                can_open_card: false,
                card_access: {can_open: false, view: {}, manage: {}}
            };
        },
        inspectorCharacteristic(item) {
            if (!item || typeof item !== 'object') return item;
            const sourcePayload = item.source_payload && typeof item.source_payload === 'object'
                ? Object.assign({}, item.source_payload)
                : null;
            if (sourcePayload) {
                delete sourcePayload.object_name;
                delete sourcePayload.object_title;
                delete sourcePayload.building_name;
                delete sourcePayload.building_title;
            }
            return Object.assign({}, item, {source_payload: sourcePayload});
        },
        employeeInspectorPayload(details, line) {
            const value = details && typeof details === 'object' ? details : {};
            const permissions = value.permissions || {};
            const cardAccess = permissions.card_access || {
                can_open: false,
                view: {},
                manage: {}
            };
            const currentEmployment = value.as_of && value.as_of.current_employment;
            const department = value.operational_department;
            const absence = value.current_absence || null;
            const presentation = absence && absence.presentation || {};
            const allCharacteristics = Array.isArray(value.characteristics)
                ? value.characteristics.map(this.inspectorCharacteristic)
                : [];
            return Object.assign({}, value, {
                employee_id: Number(value.employee_id || line && line.employee_id),
                display_name: value.full_name
                    || value.display_name
                    || line && line.employee_name_snapshot,
                position: value.operational_position
                    || currentEmployment && currentEmployment.official_position
                    || null,
                department_name: department && department.name || null,
                priority: value.reliability_priority || null,
                contacts: Array.isArray(value.contact_methods) ? value.contact_methods : [],
                characteristics: allCharacteristics.slice(0, 3),
                characteristics_total: allCharacteristics.length,
                card_access: cardAccess,
                can_open_card: cardAccess.can_open === true,
                is_absent: Boolean(absence),
                absence,
                absence_label: presentation.label || presentation.code || null
            });
        },
        async openEmployeeInspector(line, preserveEmployee) {
            const employeeId = Number(line && line.employee_id);
            const context = preserveEmployee && this.inspectorContext || {
                timesheetId: Number(line && line.timesheet_id) || this.currentTimesheetId(),
                date: line && line.context_date || this.selectedDate || this.period.starts_on
            };
            const timesheetId = context.timesheetId;
            if (!Number.isInteger(employeeId) || employeeId <= 0 || !timesheetId) return;
            const requestId = ++this.inspectorRequestId;
            this.inspectorContext = context;
            const fallback = this.employeeInspectorPlaceholder(line);
            if (!preserveEmployee) this.inspectorEmployee = fallback;
            this.inspectorAccessMessage = '';
            this.inspectorLoading = true;
            try {
                const response = await api.get(
                    `/api/employees/${encodeURIComponent(employeeId)}`,
                    {
                        params: {
                            date: context.date,
                            timesheet_id: timesheetId
                        },
                        ignoreModal: true
                    }
                );
                if (requestId !== this.inspectorRequestId) return;
                this.inspectorEmployee = this.employeeInspectorPayload(response.data, line);
            } catch (error) {
                if (requestId !== this.inspectorRequestId) return;
                const status = Number(error && error.response && error.response.status);
                this.inspectorEmployee = fallback;
                this.inspectorAccessMessage = status === 403
                    ? 'Показаны сведения из табеля. Остальные сведения закрыты вашими правами доступа.'
                    : this.responseMessage(error, 'Не удалось безопасно загрузить сведения сотрудника.');
            } finally {
                if (requestId === this.inspectorRequestId) this.inspectorLoading = false;
            }
        },
        refreshEmployeeInspector() {
            if (!this.inspectorEmployee) return;
            const employeeId = Number(this.inspectorEmployee.employee_id);
            const line = this.sortedLines.find(item => Number(item.employee_id) === employeeId) || {
                employee_id: employeeId,
                employee_name_snapshot: this.inspectorEmployee.full_name
                    || this.inspectorEmployee.display_name
                    || ''
            };
            this.openEmployeeInspector(line, true);
        },
        closeEmployeeInspector() {
            this.inspectorRequestId += 1;
            this.inspectorLoading = false;
            this.inspectorAccessMessage = '';
            this.inspectorEmployee = null;
            this.inspectorContext = null;
        },
        clearCurrentTimesheet() {
            this.resetPersonForm();
            this.resetEmployeeSearch();
            this.resetEditQueue();
            this.stopAllEditingPresence();
            if (socket.connected && this.watchedTimesheetId) socket.emit('timesheet:unwatch');
            this.sheetRequestId += 1;
            this.watchedTimesheetId = null;
            this.remoteEditors = {};
            this.sheet = null;
            this.assessmentReadinessForced = false;
            this.entryDrafts = {};
            this.monthCellDrafts = {};
            this.lineDetailDrafts = {};
            this.weekLayoutPeriod = '';
        },
        timesheetShareUrl() {
            const timesheetId = this.currentTimesheetId();
            if (!timesheetId) return '';
            const resolved = this.$router.resolve({
                name: 'timesheet',
                params: {timesheetId: String(timesheetId)}
            });
            return new URL(resolved.href, window.location.href).toString();
        },
        copyTimesheetLinkFallback(value) {
            const textArea = document.createElement('textarea');
            textArea.value = value;
            textArea.setAttribute('readonly', '');
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            try {
                textArea.select();
                textArea.setSelectionRange(0, value.length);
                if (!document.execCommand('copy')) throw new Error('Copy command is unavailable');
            } finally {
                document.body.removeChild(textArea);
            }
        },
        async copyTimesheetLink(value) {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                try {
                    await navigator.clipboard.writeText(value);
                    return;
                } catch (error) {
                    this.copyTimesheetLinkFallback(value);
                    return;
                }
            }
            this.copyTimesheetLinkFallback(value);
        },
        async shareTimesheetLink() {
            const url = this.timesheetShareUrl();
            if (!url || this.sharingTimesheetLink) return;
            this.sharingTimesheetLink = true;
            this.errorMessage = '';
            try {
                const objectName = this.sheet && this.sheet.object && this.sheet.object.name;
                if (navigator.share) {
                    await navigator.share({
                        title: objectName ? `Табель: ${objectName}` : `Табель #${this.currentTimesheetId()}`,
                        text: objectName ? `Табель по объекту «${objectName}»` : 'Табель',
                        url
                    });
                    this.successMessage = 'Ссылка на табель отправлена.';
                } else {
                    await this.copyTimesheetLink(url);
                    this.successMessage = 'Ссылка на табель скопирована.';
                }
            } catch (error) {
                if (!error || error.name !== 'AbortError') {
                    this.errorMessage = 'Не удалось скопировать ссылку на табель.';
                }
            } finally {
                this.sharingTimesheetLink = false;
            }
        },
        syncBuildingFromQuery() {
            const normalized = String(this.objectQuery || '').trim().toLocaleLowerCase('ru-RU');
            const match = this.objectInputOptions.find(option => (
                String(option.text).trim().toLocaleLowerCase('ru-RU') === normalized
            ));
            this.buildingId = match ? Number(match.value) : null;
        },
        handleObjectQueryInput() {
            this.objectPickerOpen = true;
        },
        openObjectPicker(event) {
            if (this.loading) return;
            this.objectPickerOpen = true;
            if (event && event.target && event.target.select) event.target.select();
        },
        toggleObjectPicker() {
            if (this.loading) return;
            this.objectPickerOpen = !this.objectPickerOpen;
            if (!this.objectPickerOpen) return;
            this.$nextTick(() => {
                const component = this.$refs.objectPickerInput;
                const input = component && component.$el ? component.$el : component;
                if (input && input.focus) input.focus();
                if (input && input.select) input.select();
            });
        },
        handleObjectPickerFocusOut(event) {
            const nextTarget = event && event.relatedTarget;
            if (nextTarget && event.currentTarget.contains(nextTarget)) return;
            this.objectPickerOpen = false;
        },
        selectObjectOption(option) {
            this.selectObjectById(option && option.value);
            this.objectPickerOpen = false;
        },
        selectObjectById(buildingId) {
            const option = this.objectInputOptions.find(item => Number(item.value) === Number(buildingId));
            const object = (this.bootstrapData.objects || []).find(item => Number(item.building_id) === Number(buildingId))
                || this.periodHistoricalObjects.find(item => Number(item.building_id) === Number(buildingId))
                || (this.sheet && this.sheet.object && Number(this.sheet.object.building_id) === Number(buildingId) ? this.sheet.object : null);
            this.buildingId = object ? Number(object.building_id) : null;
            this.objectQuery = option ? option.text : object ? object.name : '';
        },
        async loadBootstrap() {
            this.loading = true;
            this.errorMessage = '';
            try {
                const response = await api.get('/api/timesheets/bootstrap');
                this.bootstrapData = response.data;
                const routeObjectId = Number(this.$route.query && this.$route.query.object_id);
                const candidate = (this.bootstrapData.objects || []).find(object => (
                    (object.can_view || object.can_edit) && Number(object.building_id) === routeObjectId
                ));
                this.selectObjectById(candidate ? candidate.building_id : null);
                this.loaded = true;
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось открыть модуль табелей.');
            } finally {
                this.loading = false;
            }
        },
        async openTimesheet() {
            if (!this.periodMissing || this.periodHistoricalObjects.length || !await this.confirmPeriodLeave()) return;
            this.periodCreating = true;
            this.loading = true;
            this.errorMessage = '';
            try {
                const periodStart = `${this.periodMonth}-01`;
                const draft = await api.post('/api/timesheets/drafts', {
                    building_id: this.buildingId,
                    period_start: periodStart
                });
                await this.loadTimesheet(draft.data.id, {syncRoute: false});
                await this.syncTimesheetRoute(draft.data.id, true);
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось открыть табель.');
            } finally {
                this.periodCreating = false;
                this.loading = false;
            }
        },
        packageDecisionTime(value) {
            return value ? new Date(value).toLocaleString('ru-RU', {timeZone: 'Europe/Minsk'}) : '';
        },
        openPackageDecision(item, action) {
            this.packageDecisionItem = item;
            this.packageDecisionAction = action;
            this.packageDecisionMonth = this.periodMonth;
            this.packageDecisionReason = '';
            this.packageDecisionError = '';
            this.packageDecisionSaved = false;
            this.packageDecisionVisible = true;
        },
        async savePackageDecision() {
            if (this.packageDecisionPending || this.packageDecisionSaved || !this.packageDecisionItem || !this.packageDecisionReason.trim()) return;
            const item = this.packageDecisionItem;
            this.packageDecisionPending = true;
            this.packageDecisionError = '';
            try {
                await api.post(`/api/timesheets/${item.timesheet_id}/package-decision`, {
                    action: this.packageDecisionAction, reason: this.packageDecisionReason,
                    expected_version: item.timesheet_version,
                    expected_decision_id: item.package_decision ? item.package_decision.id : null
                });
                this.packageDecisionSaved = true;
                await this.loadReview();
                if (this.errorMessage) {
                    this.packageDecisionError = 'Решение сохранено, но не удалось обновить готовность комплекта. Закройте окно и обновите список.';
                } else {
                    this.packageDecisionVisible = false;
                }
            } catch (error) {
                this.packageDecisionError = this.responseMessage(error, 'Не удалось сохранить решение.');
            } finally {
                this.packageDecisionPending = false;
            }
        },
        async loadReview() {
            const request = ++this.reviewRequestId;
            const month = this.periodMonth;
            this.reviewLoading = true;
            this.errorMessage = '';
            try {
                const periodStart = `${month}-01`;
                const response = await api.get('/api/timesheets/periods/summary', {params: {period_start: periodStart}});
                if (request !== this.reviewRequestId || !this.reviewMode || month !== this.periodMonth) return false;
                this.periodSummary = response.data;
                this._reviewStale.readiness = false;
                return true;
            } catch (error) {
                if (request !== this.reviewRequestId) return false;
                this.periodSummary = null;
                this.reviewSheets = [];
                this.errorMessage = this.responseMessage(error, 'Не удалось загрузить свод табелей.');
                if (error && error.response && error.response.status === 403) this.clearReviewAccess();
                return false;
            } finally {
                if (request === this.reviewRequestId) this.reviewLoading = false;
            }
        },
        async handoffReviewPeriod() {
            if (this.periodNavigationLocked || !this.periodSummary || !this.periodSummary.can_handoff) return;
            const periodStart = this.periodSummary.period.starts_on;
            this.handoffPending = true;
            try {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    'Утверждённые табели будут зафиксированы одним комплектом.'
                    + (this.periodSummary.excluded_object_count
                        ? ` Исключено настройками: ${this.periodSummary.excluded_object_count} ${this.pluralize(this.periodSummary.excluded_object_count, 'объект', 'объекта', 'объектов')}. Их табели не войдут в начисление ЗП.` : '')
                    + ' После передачи обычные правки табельщика закроются.',
                    {title: 'Передать на расчёт зарплаты', okTitle: 'Передать', cancelTitle: 'Отмена'}
                );
                if (!confirmed) return;
                this.errorMessage = '';
                const response = await api.post('/api/timesheets/periods/handoff', {
                    period_start: periodStart
                });
                await this.loadReview();
                this.successMessage = `Комплект версии ${response.data.package_version} передан расчётчику зарплаты.`;
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось передать комплект на расчёт зарплаты.');
            } finally {
                this.handoffPending = false;
            }
        },
        historicalSourceUrl(item) {
            const value = item.source_metadata && item.source_metadata.source_file_url;
            return /^https:\/\/docs\.google\.com\//.test(value || '') ? value : null;
        },
        rememberHistoricalObjects(rows) {
            const objects = new Map();
            rows.filter(row => row.source === 'legacy_google' && row.object && !['annulled', 'pilot_closed'].includes(row.status)).forEach(row => {
                objects.set(Number(row.object.building_id), {...row.object, historical_only: true, can_view: true, can_edit: false});
            });
            this.periodHistoricalObjects = Array.from(objects.values());
        },
        async loadTimesheet(id, options = {}) {
            const editQueue = this._editQueue;
            const editEpoch = editQueue ? editQueue.epoch : 0;
            const requestId = this.sheetRequestId + 1;
            this.sheetRequestId = requestId;
            const isCurrent = () => requestId === this.sheetRequestId && (!options.isCurrent || options.isCurrent());
            const reviewRevisionId = Number(this.$route.query.review_revision) || null;
            const response = await api.get(`/api/timesheets/${id}`, {
                params: reviewRevisionId ? {review_revision: reviewRevisionId} : {}, ignoreModal: options.ignoreModal === true
            });
            if (!isCurrent()) return false;
            const previousSheet = this.sheet;
            const previousId = Number(previousSheet && previousSheet.timesheet && previousSheet.timesheet.id);
            if (previousId === Number(id) && (this.editPendingCount
                || (editQueue && (editQueue !== this._editQueue || editEpoch !== editQueue.epoch))
                || Number(response.data.timesheet.version || 0) < this._confirmedSheetVersion)) {
                this.editSyncPending = true;
                this.scheduleEditRefresh();
                return false;
            }
            if (previousId && previousId !== Number(id)) {
                this.emitEditingPresence(null);
                this.editingPresenceKey = null;
                this.stopPresenceHeartbeat();
                this.remoteEditors = {};
            }
            if (previousId !== Number(id)) this.assessmentReadinessForced = false;
            this.mergeTimesheetDrafts(previousSheet, response.data);
            this.historicalSheets = [];
            if (this.isHistoricalSheet && this.sheet.timesheet.record_kind !== 'pilot') {
                const history = await api.get('/api/timesheets', {params: {period_start: this.sheet.period.starts_on, status: 'all'}, ignoreModal: options.ignoreModal === true});
                if (!isCurrent()) return false;
                this.rememberHistoricalObjects(history.data);
                this.historicalSheets = history.data.filter(row => row.source === 'legacy_google'
                    && row.object && Number(row.object.building_id) === Number(this.sheet.object.building_id));
            }
            this.periodMonth = this.sheet.period.starts_on.slice(0, 7);
            this.selectedPeriodStreamId = Number(this.sheet.timesheet.stream_id) || null;
            this.periodMissing = false;
            this.selectObjectById(this.sheet.object && this.sheet.object.building_id);
            this.rememberTimesheetPeriod();
            if (this.selectedDate < this.period.starts_on || this.selectedDate > this.period.ends_on) {
                this.selectedDate = this.period.starts_on;
            }
            this.initializeWeekLayout();
            this.syncMobileWeekToDate();
            this.watchCurrentTimesheet();
            await this.loadActivity({isCurrent, ignoreModal: options.ignoreModal === true, throwOnError: options.throwActivityErrors === true});
            if (!isCurrent()) return false;
            if (options.syncRoute !== false) await this.syncTimesheetRoute(id);
            if (!isCurrent()) return false;
            if (previousId !== Number(id) && this.$route.query.attention === 'arrivals') {
                this.plannedArrivalsExpanded = true;
                this.$nextTick(() => {
                    if (Number(this.sheet && this.sheet.timesheet.id) !== Number(id) || this.$route.query.attention !== 'arrivals') return;
                    const section = this.$refs.plannedArrivalsSection;
                    if (section) section.scrollIntoView({block: 'start'});
                });
            }
            this.$nextTick(this.openRequestedUnresolvedResolver);
            await this.openRequestedPlannedArrival();
            if (!isCurrent()) return false;
            if (options.fromRealtime && this.sheet.review_revision && this.sheet.review_revision.changed) {
                this.reviewFocusMessage = `Данные табеля отличаются от версии ${this.sheet.review_revision.revision_number}, показанной в своде. Здесь открыты текущие данные.`;
            }
            return true;
        },
        initializeWeekLayout() {
            const periodKey = `${this.period.starts_on || ''}:${this.period.ends_on || ''}`;
            if (!periodKey || this.weekLayoutPeriod === periodKey) return;
            const activeWeek = this.monthWeeks.find(week => (
                week.days.some(day => day.date === this.selectedDate)
            ));
            const collapsed = {};
            this.monthWeeks.forEach(week => {
                collapsed[week.key] = this.isMobileFullMonthView
                    ? false
                    : !activeWeek || week.key !== activeWeek.key;
            });
            this.collapsedWeekKeys = collapsed;
            this.weekLayoutPeriod = periodKey;
        },
        syncMobileWeekToDate() {
            const week = this.monthWeeks.find(item => item.days.some(day => day.date === this.selectedDate));
            if (week) this.mobileWeekKey = week.key;
        },
        handleSelectedDateChange() {
            this.rebuildDrafts();
            this.syncMobileWeekToDate();
        },
        isWeekCollapsed(key) {
            return Boolean(this.collapsedWeekKeys[key]);
        },
        toggleWeek(key) {
            this.$set(this.collapsedWeekKeys, key, !this.isWeekCollapsed(key));
        },
        handleMonthWeekClick(key) {
            if (this.isMobileFullMonthView) return;
            this.toggleWeek(key);
        },
        collapseAllWeeks() {
            const collapsed = {};
            this.monthWeeks.forEach(week => { collapsed[week.key] = true; });
            this.collapsedWeekKeys = collapsed;
        },
        expandAllWeeks() {
            const collapsed = {};
            this.monthWeeks.forEach(week => { collapsed[week.key] = false; });
            this.collapsedWeekKeys = collapsed;
        },
        chooseMobileWeek(key) {
            this.mobileWeekKey = key;
            const week = this.monthWeeks.find(item => item.key === key);
            if (week && week.days.length && !week.days.some(day => day.date === this.selectedDate)) {
                this.selectedDate = week.days[0].date;
                this.rebuildDrafts();
            }
        },
        setMobileMonthLayout(layout) {
            this.mobileMonthLayout = layout === 'full' ? 'full' : 'weeks';
            this.mobileNamesPreviewing = false;
            if (this.mobileMonthLayout !== 'full') {
                this.hideMobileNamesHint(false);
                return;
            }
            this.expandAllWeeks();
            this.$nextTick(() => {
                if (this.$refs.monthScroll) this.$refs.monthScroll.scrollLeft = 0;
                this.showMobileNamesHintIfNeeded();
            });
        },
        startMobileNamesPreview(event) {
            this.hideMobileNamesHint(false);
            if (event && event.pointerType === 'mouse' && event.button !== 0) return;
            if (this._mobileNamesPreviewTimer) {
                window.clearTimeout(this._mobileNamesPreviewTimer);
            }
            this._mobileNamesPreviewSuppressClick = false;
            if (
                event
                && event.currentTarget
                && typeof event.currentTarget.setPointerCapture === 'function'
            ) {
                try {
                    event.currentTarget.setPointerCapture(event.pointerId);
                } catch (error) {
                    // Старые мобильные браузеры продолжат работать без захвата указателя.
                }
            }
            this._mobileNamesPreviewTimer = window.setTimeout(() => {
                this._mobileNamesPreviewTimer = null;
                this._mobileNamesPreviewSuppressClick = true;
                this.mobileNamesPreviewing = true;
            }, 220);
        },
        finishMobileNamesPreview() {
            if (this._mobileNamesPreviewTimer) {
                window.clearTimeout(this._mobileNamesPreviewTimer);
                this._mobileNamesPreviewTimer = null;
            }
            if (!this.mobileNamesPreviewing) return;
            this.mobileNamesPreviewing = false;
            this._mobileNamesPreviewSuppressClick = true;
            window.setTimeout(() => {
                this._mobileNamesPreviewSuppressClick = false;
            }, 0);
        },
        toggleMobileNamesContext(event) {
            if (this._mobileNamesPreviewSuppressClick) {
                if (event && typeof event.preventDefault === 'function') event.preventDefault();
                return;
            }
            this.mobileNamesPinned = !this.mobileNamesPinned;
            this.mobileNamesPreviewing = false;
        },
        showMobileNamesHintIfNeeded() {
            if (!this.isCompactViewport) return;
            const storage = this.browserStorage();
            const key = 'argus:timesheets:mobile-names-hint:v1';
            let views = 0;
            if (storage) {
                try {
                    views = Math.max(0, Number(storage.getItem(key)) || 0);
                    if (views >= 3) return;
                    storage.setItem(key, String(views + 1));
                } catch (error) {
                    // Подсказка продолжит работать и без доступного localStorage.
                }
            }
            this.mobileNamesHintVisible = true;
            if (this._mobileNamesHintTimer) {
                window.clearTimeout(this._mobileNamesHintTimer);
            }
            this._mobileNamesHintTimer = window.setTimeout(() => {
                this._mobileNamesHintTimer = null;
                this.mobileNamesHintVisible = false;
            }, 8000);
        },
        hideMobileNamesHint(permanently) {
            this.mobileNamesHintVisible = false;
            if (this._mobileNamesHintTimer) {
                window.clearTimeout(this._mobileNamesHintTimer);
                this._mobileNamesHintTimer = null;
            }
            if (!permanently) return;
            const storage = this.browserStorage();
            if (!storage) return;
            try {
                storage.setItem('argus:timesheets:mobile-names-hint:v1', '3');
            } catch (error) {
                // Ручное закрытие подсказки не должно мешать работе табеля.
            }
        },
        entryFor(line, date) {
            const index = this.lineGridIndex(line);
            return index && index.entriesByDate.get(date) || null;
        },
        lineGridIndex(line) {
            if (!line) return null;
            // A global computed index makes Vue collect every row dependency on every cell lookup.
            // Reading only this line keeps dependency collection proportional to a single row.
            const cache = this._gridLineCache;
            let item = cache && cache.get(Number(line.id));
            if (!item || item.entries !== line.entries || item.absences !== line.absence_days
                || item.external !== line.external_entries || item.incidents !== line.incidents) {
                item = {entries: line.entries, absences: line.absence_days, external: line.external_entries,
                    incidents: line.incidents, value: timesheetGridIndex.buildTimesheetGridIndex([line]).get(Number(line.id))};
                if (cache) cache.set(Number(line.id), item);
            }
            return item.value;
        },
        lineAssignmentIndex(line) {
            if (!line) return {days: {}};
            const cache = this._assignmentLineCache;
            let item = cache && cache.get(Number(line.id));
            if (!item || item.evidence !== line.assignment_evidence) {
                const days = {};
                (line.assignment_evidence && line.assignment_evidence.days || []).forEach(day => { days[day.date] = day; });
                item = {evidence: line.assignment_evidence, days,
                    presentation: assignmentAuthorship.buildAssignmentPresentation([line])[Number(line.id)]};
                if (cache) cache.set(Number(line.id), item);
            }
            return item;
        },
        lineIssueIndex(line) {
            if (!line) return null;
            const cache = this._lineIssueCache;
            const old = cache && cache.get(Number(line.id));
            const period = this.period.starts_on;
            if (old && old.period === period && old.incidents === line.incidents && old.external === line.external_entries) return old.value;
            const items = this.lineIssueItems(line);
            const byDate = {};
            const byWeek = {};
            this.monthDays.forEach(day => { byDate[day.date] = this.issueGroupsFromItems(items.filter(item => item.date === day.date)); });
            this.monthWeeks.forEach(week => {
                const dates = new Set(week.days.map(day => day.date));
                byWeek[week.key] = this.issueGroupsFromItems(items.filter(item => dates.has(item.date)));
            });
            const value = {all: this.issueGroupsFromItems(items), byDate, byWeek};
            if (cache) cache.set(Number(line.id), {period, incidents: line.incidents, external: line.external_entries, value});
            return value;
        },
        assignmentDayFor(line, date) {
            const days = this.lineAssignmentIndex(line).days;
            return days[date] || null;
        },
        assignmentDayTitle(line, date) {
            return this.assignmentPersonText(line, date);
        },
        assignmentPersonText(line, date) {
            const presentation = this.lineAssignmentIndex(line).presentation;
            return presentation && (date ? presentation.days[date] : presentation.month) || '';
        },
        assignmentPersonTitle(line, date) {
            return [line.employee_name_snapshot, this.assignmentPersonText(line, date)].filter(Boolean).join('\n\n');
        },
        assignmentCellClasses(line, date) {
            const evidence = this.assignmentDayFor(line, date);
            if (!evidence) return {};
            const index = this.monthDayIndex.has(date)
                ? this.monthDayIndex.get(date)
                : -1;
            const sameEvidence = otherDate => {
                const other = otherDate ? this.assignmentDayFor(line, otherDate) : null;
                return Boolean(
                    other
                    && other.kind === evidence.kind
                    && Number(other.source_id) === Number(evidence.source_id)
                );
            };
            const previousDate = index > 0 ? this.monthDays[index - 1].date : null;
            const nextDate = index >= 0 && index < this.monthDays.length - 1
                ? this.monthDays[index + 1].date
                : null;
            return {
                'is-board-assigned': true,
                'is-assignment-start': !sameEvidence(previousDate),
                'is-assignment-end': !sameEvidence(nextDate)
            };
        },
        weekAssignmentCount(line, week) {
            return (week && week.days || []).filter(day => (
                this.assignmentDayFor(line, day.date)
            )).length;
        },
        absenceDayFor(line, date) {
            const index = this.lineGridIndex(line);
            return index && index.absenceDaysByDate.get(date) || null;
        },
        absenceStateLabel(day) {
            if (!day) return '';
            return {
                suggestion: `Кадры предлагают ${day.code}`,
                matched: `Совпадает с кадровой отметкой ${day.code}`,
                accepted: `Проставлено из кадрового отсутствия: ${day.code}`,
                conflict: `Кадры: ${day.code}, в табеле другая отметка`,
                source_changed: 'Основание отсутствия изменено кадровиками',
                source_removed: 'Кадровое отсутствие аннулировано или больше не действует',
                acknowledged: 'Расхождение принято, отметка табеля оставлена',
                overlap: 'Кадры указали несколько пересекающихся отсутствий',
                configuration_error: 'Для отсутствия не настроен код табеля'
            }[day.state] || 'Кадровое отсутствие';
        },
        absenceDayLabel(day) {
            return `${this.shortDate(day.work_date)} — ${day.code}`;
        },
        absenceNeedsAttention(day) {
            return Boolean(day && [
                'conflict', 'source_changed', 'source_removed', 'overlap',
                'configuration_error'
            ].includes(day.state));
        },
        canResolveAbsenceDiscrepancy(day) {
            return Boolean(
                this.canEdit &&
                day &&
                ['source_changed', 'source_removed'].includes(day.state)
            );
        },
        absenceDiscrepancyKey(item) {
            return item && item.line && item.day
                ? `${item.line.id}:${item.day.work_date}`
                : '';
        },
        monthCellPlaceholder(line, date) {
            const absence = this.absenceDayFor(line, date);
            if (absence && absence.state === 'suggestion') return `Кадры: ${absence.code}`;
            return this.hasExternalEntry(line, date) ? 'Дт' : '';
        },
        entryForDate(line) {
            return this.entryFor(line, this.selectedDate);
        },
        rebuildDrafts() {
            const drafts = {};
            if (this.sheet) {
                this.sheet.lines.forEach(line => {
                    const entry = this.entryForDate(line);
                    const absence = entry && entry.absence_code_id
                        ? this.codeById[Number(entry.absence_code_id)]
                        : null;
                    const travel = entry && entry.travel_code_id
                        ? this.codeById[Number(entry.travel_code_id)]
                        : null;
                    drafts[line.id] = {
                        worked_hours: entry ? Number(entry.worked_minutes) / 60 : 0,
                        absence_code: absence ? absence.code : '',
                        travel_code: travel ? travel.code : '',
                        version: entry ? Number(entry.version) : undefined
                    };
                });
            }
            this.entryDrafts = drafts;
        },
        entryDisplay(entry) {
            if (!entry) return '';
            const parts = [];
            if (Number(entry.worked_minutes) > 0) parts.push(this.minutesLabel(entry.worked_minutes));
            const absence = entry.absence_code_id
                ? this.codeById[Number(entry.absence_code_id)]
                : null;
            const travel = entry.travel_code_id
                ? this.codeById[Number(entry.travel_code_id)]
                : null;
            if (absence) parts.push(absence.code);
            if (travel) parts.push(travel.code);
            return parts.join(' ');
        },
        cellKey(lineId, date) {
            return `${lineId}:${date}`;
        },
        rebuildMonthDrafts() {
            const drafts = {};
            if (this.sheet) {
                this.sheet.lines.forEach(line => {
                    this.monthDays.forEach(day => {
                        drafts[this.cellKey(line.id, day.date)] = this.entryDisplay(
                            this.entryFor(line, day.date)
                        );
                    });
                });
            }
            this.monthCellDrafts = drafts;
        },
        rebuildLineDetailDrafts() {
            const drafts = {};
            if (this.sheet) {
                const timesheetId = Number(this.sheet.timesheet.id);
                if (Number(this.assessmentOmissionReasonTimesheetId) !== timesheetId) {
                    this.assessmentOmissionReasonDraft = String(
                        this.sheet.timesheet.characteristic_omission_reason || ''
                    );
                    this.assessmentOmissionReasonTimesheetId = timesheetId;
                }
                this.sheet.lines.forEach(line => {
                    drafts[line.id] = {
                        ktu: line.ktu === null || line.ktu === undefined ? '' : String(Number(line.ktu)),
                        ktu_evidence: (Array.isArray(line.ktu_evidence) ? line.ktu_evidence : [])
                            .map(item => String(item && item.key || ''))
                            .filter(Boolean),
                        characteristic_values: Object.assign({}, line.characteristic_values || {}),
                        ktu_comment: line.ktu_comment || '',
                        version: Number(line.details_version || 1)
                    };
                    this.characteristicFields.forEach(field => {
                        if (drafts[line.id].characteristic_values[field.key] === undefined) {
                            this.$set(drafts[line.id].characteristic_values, field.key, '');
                        }
                    });
                });
            }
            this.lineDetailDrafts = drafts;
        },
        openAddPerson() {
            this.errorMessage = '';
            this.addPersonError = '';
            this.peopleSearchError = '';
            this.addPersonModalVisible = true;
        },
        openAssessment(line, initialKtu = null) {
            this.assessmentFocusField = '';
            this.assessmentInitialKtu = initialKtu;
            this.revealEmployeeSearchLine(line.id);
            this.assessmentLineId = line.id;
            this.assessmentModalVisible = true;
        },
        openAssessmentIssue(issue) {
            const line = this.sortedLines.find(item => Number(item.id) === Number(issue.line_id));
            if (line) this.openAssessment(line);
        },
        openTextQualityIssue(issue) {
            this.openAssessmentIssue(issue);
            this.assessmentFocusField = issue.field_key;
        },
        openFirstIncompleteAssessment() {
            if (!this.assessmentIssueGroups.length) return;
            this.openAssessmentIssue(this.assessmentIssueGroups[0]);
        },
        characteristicValueFilled(value) {
            if (value === true || value === false) return true;
            return value !== null && value !== undefined && String(value).trim() !== '';
        },
        characteristicAuthorLabel(line, field) {
            const saved = (line.characteristic_values || {})[field.key];
            const draft = this.lineDetailDrafts[line.id];
            const normalized = value => value == null ? '' : value;
            if (draft && JSON.stringify(normalized(draft.characteristic_values[field.key])) !== JSON.stringify(normalized(saved))) return 'Изменение ещё не сохранено';
            const author = (line.characteristic_authors || {})[field.key];
            if (!author && !this.characteristicValueFilled(saved)) return '';
            return author && author.name ? `Автор: ${author.name}` : author && author.user_id ? `Автор: пользователь №${author.user_id}` : 'Автор не сохранён';
        },
        ktuScoreLabel(value) {
            const number = Number(value);
            const labels = this.ktuConfig.score_labels || {};
            const configured = labels[String(number)];
            if (configured) return configured;
            if (number === 5) return 'Выдающийся вклад';
            if (number > 4) return 'Выше ожиданий';
            if (number === 4) return 'Работал нормально';
            if (number >= 3.5) return 'Есть замечания';
            if (number >= 3) return 'Серьёзные замечания';
            return 'Критическая оценка';
        },
        lineDetailsDraft(line) {
            return {ktu: line.ktu == null ? '' : String(Number(line.ktu)),
                ktu_evidence: (line.ktu_evidence || []).map(v => String(v.key || v)), ktu_comment: line.ktu_comment || '',
                characteristic_values: {...(line.characteristic_values || {})}, version: Number(line.details_version || 1)};
        },
        async handleKtuChange(line) {
            const draft = line && this.lineDetailDrafts[line.id];
            if (!draft || !this.canEdit) return;
            const selected = draft.ktu;
            if (selected === '' || Number(selected) !== 4) {
                this.$set(this.lineDetailDrafts, line.id, this.lineDetailsDraft(line));
                this.openAssessment(line, selected);
                return;
            }
            draft.ktu_evidence = [];
            await this.saveLineDetails(line);
        },
        acceptAssessmentResult(result) {
            if (!result || !result.line || !this.sheet) return;
            const line = this.sheet.lines.find(item => Number(item.id) === Number(result.line.id));
            if (!line) return;
            Object.keys(result.line).forEach(key => this.$set(line, key, result.line[key]));
            this.$set(this.lineDetailDrafts, line.id, this.lineDetailsDraft(line));
            if (this._editQueue && this._editQueue.records.has('detail:' + line.id))
                this._editQueue.rebase('detail:' + line.id, this.detailEditValue(line), Number(line.details_version || 1), false);
            this.sheet.timesheet.version = Math.max(Number(this.sheet.timesheet.version || 0), Number(result.timesheet_version || 0));
            this._confirmedSheetVersion = Math.max(this._confirmedSheetVersion || 0, this.sheet.timesheet.version);
            this.rowDraftGenerations[line.id] = (this.rowDraftGenerations[line.id] || 0) + 1;
            this.editSyncPending = true;
            this.scheduleEditRefresh();
        },
        assessmentIssuesForLine(line, ignoreOmissionReason) {
            if (!line) return [];
            const cache = this._assessmentLineCache;
            const key = `${line.id}:${Boolean(ignoreOmissionReason)}`;
            const version = this.rowDraftGenerations[line.id] || 0;
            const draft = this.lineDetailDrafts[line.id];
            const fields = this.characteristicFields;
            const config = this.ktuConfig;
            const reason = this.assessmentOmissionReasonDraft;
            const qualityEnabled = this.textQualityEnabled;
            const old = cache && cache.get(key);
            if (old && old.line === line && old.entries === line.entries && old.draft === draft && old.version === version
                && old.fields === fields && old.config === config && old.reason === reason && old.qualityEnabled === qualityEnabled) return old.value;
            const value = this.calculateAssessmentIssuesForLine(line, ignoreOmissionReason);
            if (cache) cache.set(key, {line, entries: line.entries, draft, version, fields, config, reason, qualityEnabled, value});
            return value;
        },
        calculateAssessmentIssuesForLine(line) {
            if (!line) return [];
            const draft = this.lineDetailDrafts[line.id] || {
                ktu: line.ktu === null || line.ktu === undefined ? '' : String(Number(line.ktu)),
                ktu_evidence: (line.ktu_evidence || []).map(item => String(item.key)),
                characteristic_values: line.characteristic_values || {}
            };
            const issues = [];
            const employeeName = line.employee_name_snapshot;
            const ktu = draft.ktu === '' ? null : Number(draft.ktu);
            const hasMarks = (line.entries || []).length > 0 || this.monthDays.some(day =>
                String(this.monthCellDrafts[this.cellKey(line.id, day.date)] || '').trim()
            );
            const hasCharacteristics = Object.values(draft.characteristic_values || {}).some(value =>
                value !== null && value !== undefined && String(value).trim() !== ''
            );
            if (!hasMarks && ktu === null && !(draft.ktu_evidence || []).length && !String(draft.ktu_comment || '').trim()
                && !(line.shared_characteristic ? line.local_characteristic_present : hasCharacteristics)) return [];
            if (this.textQualityEnabled) issues.push(...textQuality.characteristicTextIssues({...line, ...draft}, this.characteristicFields));
            const criteria = Array.isArray(this.ktuConfig.criteria) ? this.ktuConfig.criteria : [];
            const selected = new Set(draft.ktu_evidence || []);
            const selectedCriteria = criteria.filter(item => selected.has(String(item.key)));
            const add = (type, message) => issues.push({
                type,
                message,
                line_id: Number(line.id),
                employee_name: employeeName
            });
            if (line.employee_category !== 'itr' && ktu === null) add('missing_ktu', 'не указан КТУ');
            if (ktu !== null && ktu !== 4) {
                const direction = ktu > 4 ? 'positive' : 'negative';
                const matching = selectedCriteria.filter(item => item.direction === direction);
                if (!matching.length) {
                    add(
                        direction === 'positive' ? 'high_ktu_missing_evidence' : 'low_ktu_missing_evidence',
                        direction === 'positive'
                            ? 'не выбрано основание высокого КТУ'
                            : 'не выбрано основание низкого КТУ'
                    );
                }
                if (ktu < 3 && !matching.some(item => item.critical === true)) {
                    add('critical_ktu_missing_evidence', 'для КТУ ниже 3 нужно критичное основание');
                }
                const commentField = this.characteristicFields.find(field => field.purpose === 'ktu_comment')
                    || this.characteristicFields.find(field => field.key === 'performance_note');
                if ((ktu < 4 || ktu === 5) && (
                    line.shared_characteristic ? !this.characteristicValueFilled(draft.ktu_comment)
                        : !commentField || !this.characteristicValueFilled(draft.characteristic_values[commentField.key])
                )) {
                    add(
                        ktu < 4 ? 'low_ktu_missing_comment' : 'top_ktu_missing_comment',
                        ktu < 4
                            ? 'нет фактического пояснения к низкому КТУ'
                            : 'не описан конкретный вклад для КТУ 5'
                    );
                }
            }
            const missingFields = this.characteristicFields.filter(field => (
                (field.required !== false || field.purpose === 'ktu_comment' || field.key === 'performance_note')
                && !this.characteristicValueFilled(draft.characteristic_values[field.key])
            ));
            if (line.employee_category !== 'itr' && line.shared_characteristic && line.shared_characteristic.requires_choice) {
                add('characteristic_choice', 'выберите основу общей характеристики');
            }
            if (line.employee_category !== 'itr' && missingFields.length) {
                add(
                    'missing_characteristics',
                    `не заполнено: ${missingFields.map(field => field.label).join(', ')}`
                );
            }
            return issues;
        },
        assessmentGuidance(line) {
            const draft = line && this.lineDetailDrafts[line.id];
            const value = draft && draft.ktu;
            if (value === '' || value === undefined) {
                return line && line.employee_category === 'itr'
                    ? 'Для ИТР КТУ и характеристика необязательны.'
                    : 'Выберите оценку. 4 — человек работал нормально.';
            }
            const number = Number(value);
            if (number === 4) return 'Для КТУ 4 дополнительные основания не нужны. Характеристика рабочего заполняется отдельно.';
            if (number > 4) return 'Выберите, чем сотрудник превзошёл ожидания. Для 5 опишите конкретный вклад.';
            return 'Низкий КТУ влияет на средний КТУ сотрудника. Выберите факт и кратко опишите ситуацию.';
        },
        assessmentCompletedCount(line) {
            if (!line) return 0;
            const values = this.lineDetailDrafts[line.id]
                ? this.lineDetailDrafts[line.id].characteristic_values
                : (line.characteristic_values || {});
            return this.characteristicFields.reduce((count, field) => {
                const value = values ? values[field.key] : '';
                return count + (value === true || value === false || String(value || '').trim() ? 1 : 0);
            }, 0);
        },
        mobileAssessmentStatus(line) {
            const issues = this.assessmentIssuesForLine(line, false);
            if (issues.length) {
                const needsKtu = issues.some(issue => issue.type === 'missing_ktu');
                const needsCharacteristic = issues.some(issue => (
                    issue.type === 'missing_characteristics'
                    || issue.type.indexOf('_comment') !== -1
                ));
                if (needsKtu && needsCharacteristic) return 'Нужно заполнить КТУ и характеристику';
                if (needsKtu) return 'Нужно указать КТУ';
                if (needsCharacteristic) return 'Нужно дополнить характеристику';
                return 'Нужно дополнить оценку КТУ';
            }
            const draft = this.lineDetailDrafts[line.id] || {};
            const ktu = draft.ktu === '' || draft.ktu === undefined
                ? null
                : Number(draft.ktu);
            const ktuLabel = ktu === null && line.employee_category === 'itr'
                ? 'КТУ необязателен'
                : `КТУ ${String(ktu).replace('.', ',')}`;
            const characteristicLabel = line.employee_category === 'itr' && this.assessmentCompletedCount(line) < this.characteristicFields.length
                ? 'характеристика необязательна для ИТР'
                : 'характеристика заполнена';
            return `${ktuLabel} · ${characteristicLabel}`;
        },
        employeeInitials(line) {
            return String(line && line.employee_name_snapshot || '')
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map(part => part[0])
                .join('')
                .toLocaleUpperCase('ru-RU');
        },
        configuredCode(value) {
            const normalized = String(value || '').trim().toLocaleLowerCase('ru-RU');
            return (this.bootstrapData.codes || []).find(code => (
                String(code.code).trim().toLocaleLowerCase('ru-RU') === normalized
            )) || null;
        },
        russianHoursWord(hours) {
            if (!Number.isInteger(Number(hours))) return 'часа';
            const value = Math.abs(Number(hours)) % 100;
            const lastDigit = value % 10;
            if (value >= 11 && value <= 14) return 'часов';
            if (lastDigit === 1) return 'час';
            if (lastDigit >= 2 && lastDigit <= 4) return 'часа';
            return 'часов';
        },
        travelSuggestionLabel(hours, code) {
            const codeName = String(code && code.name || '').trim();
            const description = codeName
                ? `${codeName.charAt(0).toLocaleLowerCase('ru-RU')}${codeName.slice(1)}`
                : 'дорога на объект или с объекта';
            return `${hours} ${this.russianHoursWord(hours)} + ${description}`;
        },
        isStandaloneCode(code) {
            return Boolean(code) && (
                code.can_combine_with_work === false ||
                String(code.code || '').trim().toLocaleUpperCase('ru-RU') === 'Л'
            );
        },
        openCellSuggestions(line, date) {
            if (this.canEdit && this._cellPicker) this._cellPicker.openFor(line, date);
        },
        filteredCellSuggestions(value) {
            const query = String(value || '').trim().replace(/,/g, '.').toLocaleLowerCase('ru-RU');
            const preferred = ['8', '9', '10', '11', '12', 'Д', 'Л', 'О', 'Б', 'В'];
            const ordered = this.cellSuggestionOptions.slice().sort((left, right) => {
                const leftIndex = preferred.indexOf(left.value);
                const rightIndex = preferred.indexOf(right.value);
                if (leftIndex !== -1 || rightIndex !== -1) {
                    return (leftIndex === -1 ? preferred.length : leftIndex) -
                        (rightIndex === -1 ? preferred.length : rightIndex);
                }
                return 0;
            });
            if (!query) return ordered;
            return ordered.filter(suggestion => {
                const searchable = `${suggestion.value} ${suggestion.label}`.toLocaleLowerCase('ru-RU');
                return searchable.includes(query);
            });
        },
        parseMonthCell(value) {
            const text = String(value || '').replace(/,/g, '.').replace(/\//g, ' ').trim();
            if (!text) return {worked_minutes: 0, absence_code: null, travel_code: null};
            const tokens = text.split(/\s+/).filter(Boolean);
            let hours = null;
            let absenceCode = null;
            let travelCode = null;
            tokens.forEach(token => {
                if (/^\d+(\.\d+)?$/.test(token)) {
                    if (hours !== null) throw new Error('Укажите часы только один раз.');
                    hours = Number(token);
                    return;
                }
                if (token.toLocaleLowerCase('ru-RU') === 'дт') {
                    throw new Error('Дт рассчитывается системой по другому табелю и вручную не вводится.');
                }
                const code = (this.bootstrapData.codes || []).find(item => (
                    String(item.code).toLocaleLowerCase('ru-RU') === token.toLocaleLowerCase('ru-RU')
                ));
                if (!code) throw new Error(`Код «${token}» не найден.`);
                if (code.category === 'travel') {
                    if (travelCode) throw new Error('В ячейке может быть только один код дороги.');
                    travelCode = code.code;
                } else {
                    if (absenceCode) throw new Error('В ячейке может быть только один код отсутствия.');
                    absenceCode = code.code;
                }
            });
            const minutes = Math.round(Number(hours || 0) * 60);
            const maximum = Number(this.bootstrapData.policy.maximum_daily_minutes || 1440);
            const step = Number(this.bootstrapData.policy.time_step_minutes || 15);
            if (!Number.isInteger(minutes) || minutes < 0 || minutes > maximum || minutes % step !== 0) {
                throw new Error(`Часы должны быть от 0 до ${maximum / 60} с шагом ${step} минут.`);
            }
            const standaloneCode = [absenceCode, travelCode]
                .map(code => this.configuredCode(code))
                .find(code => this.isStandaloneCode(code));
            if (minutes > 0 && standaloneCode) {
                throw new Error(`Код «${standaloneCode.code}» ставится только отдельно, без часов.`);
            }
            return {worked_minutes: minutes, absence_code: absenceCode, travel_code: travelCode};
        },
        editSessionHeaders(forceNew = false, sessionKind = null) {
            const now = Date.now();
            const kind = sessionKind || this.editSessionKind || 'edit';
            if (
                forceNew
                || !this.editSessionId
                || now - Number(this.editSessionLastActivity || 0) > 10 * 60 * 1000
            ) {
                const sheetId = this.sheet && this.sheet.timesheet && this.sheet.timesheet.id || 'unknown';
                this.editSessionId = `timesheet-${kind}:${sheetId}:${now}:${Math.random().toString(36).slice(2, 9)}`;
            }
            this.editSessionLastActivity = now;
            return {'x-timesheet-edit-session': this.editSessionId};
        },
        closeEditSession(resetKind = true) {
            this.editSessionId = '';
            this.editSessionLastActivity = 0;
            if (resetKind) this.editSessionKind = 'edit';
        },
        saveMonthCell(line, date) {
            return this.queueCellSave(line, date);
        },
        saveLineDetails(line) {
            return this.queueDetailsSave(line);
        },
        saveLine(line) {
            const draft = this.entryDrafts[line.id];
            if (!draft) return Promise.resolve(false);
            const text = [Number(draft.worked_hours) ? String(draft.worked_hours) : '', draft.absence_code, draft.travel_code].filter(Boolean).join(' ');
            this.$set(this.monthCellDrafts, this.cellKey(line.id, this.selectedDate), text);
            return this.queueCellSave(line, this.selectedDate);
        },
        handleDayCodeChange(line, slot) {
            const draft = this.entryDrafts[line.id];
            if (!draft) return;
            const value = slot === 'travel' ? draft.travel_code : draft.absence_code;
            const code = this.configuredCode(value);
            if (this.isStandaloneCode(code) && Number(draft.worked_hours) > 0) {
                draft.worked_hours = 0;
            }
            return this.saveLine(line);
        },
        async applyAbsenceSuggestions(line = null, date = null) {
            if (!this.canEdit || this.absenceApplying) return;
            const items = line && date ? [{line_id: Number(line.id), work_date: date}] : [];
            this.absenceApplying = true;
            this.errorMessage = '';
            try {
                const response = await api.post(
                    `/api/timesheets/${this.sheet.timesheet.id}/absence-suggestions`,
                    {items}
                );
                const result = response.data || {};
                if (result.batch_key && Number(result.saved) > 0) {
                    this.absenceUndo = {
                        batch_key: result.batch_key,
                        saved: Number(result.saved)
                    };
                }
                await this.loadTimesheet(this.sheet.timesheet.id);
                const skipped = Number(result.skipped || 0);
                this.successMessage = Number(result.saved)
                    ? `Проставлено ${result.saved} ${this.pluralize(result.saved, 'отметка', 'отметки', 'отметок')}${skipped ? `, пропущено ${skipped}` : ''}.`
                    : 'Подходящих пустых ячеек уже нет.';
            } catch (error) {
                this.errorMessage = this.responseMessage(
                    error,
                    'Не удалось проставить кадровые отсутствия.'
                );
                await this.loadTimesheet(this.sheet.timesheet.id).catch(() => {});
            } finally {
                this.absenceApplying = false;
            }
        },
        async undoAbsenceSuggestions() {
            if (!this.canEdit || !this.absenceUndo || this.absenceApplying) return;
            this.absenceApplying = true;
            this.errorMessage = '';
            try {
                const response = await api.post(
                    `/api/timesheets/${this.sheet.timesheet.id}/absence-suggestions/undo`,
                    {batch_key: this.absenceUndo.batch_key}
                );
                const result = response.data || {};
                this.absenceUndo = null;
                await this.loadTimesheet(this.sheet.timesheet.id);
                this.successMessage = `Отменено ${result.saved || 0} ${this.pluralize(result.saved || 0, 'отметка', 'отметки', 'отметок')}${result.skipped ? `, ${result.skipped} изменённых ячеек не затронуто` : ''}.`;
            } catch (error) {
                this.errorMessage = this.responseMessage(
                    error,
                    'Не удалось отменить массовое проставление.'
                );
            } finally {
                this.absenceApplying = false;
            }
        },
        async resolveAbsenceDiscrepancy(item, action) {
            if (!this.canResolveAbsenceDiscrepancy(item && item.day)) return;
            const key = this.absenceDiscrepancyKey(item);
            if (!key || this.absenceResolvingKey) return;
            this.absenceResolvingKey = key;
            this.errorMessage = '';
            try {
                await api.post(
                    `/api/timesheets/${this.sheet.timesheet.id}/absence-discrepancies`,
                    {
                        line_id: Number(item.line.id),
                        work_date: item.day.work_date,
                        action
                    }
                );
                await this.loadTimesheet(this.sheet.timesheet.id);
                this.successMessage = action === 'sync'
                    ? 'Отметка синхронизирована с кадровым отсутствием.'
                    : 'Текущая отметка оставлена, решение записано в историю.';
            } catch (error) {
                this.errorMessage = this.responseMessage(
                    error,
                    'Не удалось обработать изменение кадрового отсутствия.'
                );
                await this.loadTimesheet(this.sheet.timesheet.id).catch(() => {});
            } finally {
                this.absenceResolvingKey = null;
            }
        },
        async applyBulkValue(action = this.captureTimesheetBulkAction()) {
            this.bulkError = '';
            this.bulkResult = null;
            if (!action.lines.length || action.sheetId !== this.currentTimesheetId() || !this.canEdit) return;
            this.loading = true;
            try {
                const entry = this.parseMonthCell(action.value);
                const response = await api.post(`/api/timesheets/${action.sheetId}/day-fill`, {
                    line_ids: action.lines.map(line => Number(line.id)), work_date: action.date, entry
                }, {ignoreModal: true});
                await this.loadTimesheet(action.sheetId);
                this.bulkResult = {...response.data, date: this.shortDate(action.date)};
            } catch (error) {
                this.bulkError = this.responseMessage(error, error.message || 'Не удалось заполнить день.');
            } finally {
                this.closeEditSession(false);
                this.loading = false;
            }
        },
        moveDay(offset) {
            const date = new Date(`${this.selectedDate}T00:00:00`);
            date.setDate(date.getDate() + offset);
            const next = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            if (next < this.period.starts_on || next > this.period.ends_on) return;
            this.selectedDate = next;
            this.rebuildDrafts();
            this.syncMobileWeekToDate();
        },
        openDay(date) {
            this.selectedDate = date;
            this.rebuildDrafts();
            this.syncMobileWeekToDate();
            if (this.isCompactViewport) this.mobileMode = 'day';
        },
        externalFor(line, date) {
            const index = this.lineGridIndex(line);
            return index && index.externalByDate.get(date) || [];
        },
        externalForDate(line) {
            return this.externalFor(line, this.selectedDate);
        },
        localWorkedMinutes(line, date) {
            const entry = this.entryFor(line, date);
            return Number(entry && entry.worked_minutes || 0);
        },
        externalWorkedMinutes(line, date) {
            return this.externalFor(line, date).reduce((sum, entry) => (
                sum + Number(entry && entry.worked_minutes || 0)
            ), 0);
        },
        combinedWorkedMinutes(line, date) {
            return this.localWorkedMinutes(line, date) + this.externalWorkedMinutes(line, date);
        },
        incidentsFor(line, date) {
            const index = this.lineGridIndex(line);
            if (!index) return [];
            return index.incidentsForDate.get(date) || index.lineIncidents;
        },
        incidentsForDate(line) {
            return this.incidentsFor(line, this.selectedDate);
        },
        lineLevelIncidents(line) {
            const index = this.lineGridIndex(line);
            return index ? index.lineIncidents : [];
        },
        dateIncidentsFor(line, date) {
            const index = this.lineGridIndex(line);
            return index && index.incidentsByDate.get(date) || [];
        },
        incidentText(incident) {
            return [incident && incident.title, incident && incident.action_hint]
                .filter(Boolean)
                .join(' ');
        },
        issueTypeMeta(type) {
            const values = {
                cross_object_overlap: {
                    label: 'Другой табель',
                    icon: 'fas fa-exchange-alt'
                },
                excessive_daily_time: {
                    label: 'Превышение часов',
                    icon: 'fas fa-clock'
                },
                employee_not_in_object_roster: {
                    label: 'Нет назначения на доске',
                    icon: 'fas fa-map-marker-alt'
                },
                employee_not_assigned: {
                    label: 'Нет назначения на доске',
                    icon: 'fas fa-map-marker-alt'
                },
                employee_assignment: {
                    label: 'Нет назначения на доске',
                    icon: 'fas fa-map-marker-alt'
                },
                unresolved_person: {
                    label: 'Нет карточки',
                    icon: 'fas fa-user-times'
                },
                work_during_absence: {
                    label: 'Работа во время отсутствия',
                    icon: 'fas fa-calendar-times'
                },
                assigned_employee_without_marks: {
                    label: 'Нет отметок при назначении',
                    icon: 'fas fa-user-clock'
                },
                validation: {
                    label: 'Ошибка отметки',
                    icon: 'fas fa-exclamation-triangle'
                }
            };
            return values[type] || {
                label: 'Проблема табеля',
                icon: 'fas fa-exclamation-triangle'
            };
        },
        issueTypeKey(type) {
            return ['employee_not_in_object_roster', 'employee_not_assigned'].includes(type)
                ? 'employee_assignment'
                : (type || 'validation');
        },
        issueSeverityRank(severity) {
            return {blocking: 3, warning: 2, info: 1}[severity] || 2;
        },
        externalConflictText: conflictSources.text,
        externalSourceText: conflictSources.text,
        lineIssueItems(line, dates, includeLineLevel) {
            const allowedDates = Array.isArray(dates) ? new Set(dates) : null;
            const includeLine = includeLineLevel !== false;
            const incidents = (line.incidents || []).filter(incident => {
                if (!incident.work_date) return includeLine && !allowedDates;
                return !allowedDates || allowedDates.has(incident.work_date);
            });
            const crossObjectDates = new Set();
            const items = incidents.map((incident, index) => {
                if (incident.incident_type === 'cross_object_overlap' && incident.work_date) {
                    crossObjectDates.add(incident.work_date);
                }
                const relatedExternal = (incident.related_entries || (incident.incident_type === 'cross_object_overlap'
                    ? this.externalFor(line, incident.work_date) : [])).map(this.externalConflictText);
                const text = [this.incidentText(incident), ...relatedExternal]
                    .filter(Boolean)
                    .join(' ');
                const id = incident.id || incident.dedupe_key || `${incident.incident_type}-${index}`;
                return {
                    key: `incident:${id}`,
                    type: this.issueTypeKey(incident.incident_type),
                    severity: incident.severity || 'warning',
                    date: incident.work_date || null,
                    text
                };
            });
            (line.external_entries || []).forEach((conflict, index) => {
                if (allowedDates && !allowedDates.has(conflict.work_date)) return;
                if (crossObjectDates.has(conflict.work_date)) return;
                items.push({
                    key: `external:${conflict.work_date}:${conflict.timesheet_id || conflict.author_name || index}`,
                    type: 'cross_object_overlap',
                    severity: 'info',
                    date: conflict.work_date,
                    text: this.externalConflictText(conflict)
                });
            });
            return items;
        },
        issueGroupsFromItems(items) {
            const grouped = (items || []).reduce((result, item) => {
                const meta = this.issueTypeMeta(item.type);
                if (!result[item.type]) {
                    result[item.type] = {
                        key: item.type,
                        type: item.type,
                        label: meta.label,
                        icon: meta.icon,
                        severity: item.severity,
                        items: []
                    };
                }
                const group = result[item.type];
                group.items.push(item);
                if (this.issueSeverityRank(item.severity) > this.issueSeverityRank(group.severity)) {
                    group.severity = item.severity;
                }
                return result;
            }, {});
            return Object.values(grouped).map(group => Object.assign(group, {
                count: group.items.length,
                first_date: group.items.map(item => item.date).filter(Boolean).sort()[0] || null
            })).sort((left, right) => (
                this.issueSeverityRank(right.severity) - this.issueSeverityRank(left.severity) ||
                String(left.first_date || '').localeCompare(String(right.first_date || '')) ||
                left.label.localeCompare(right.label, 'ru')
            ));
        },
        lineIssueGroups(line) {
            const index = this.lineIssueIndex(line);
            return index
                ? index.all.filter(group => group.type !== 'cross_object_overlap')
                : [];
        },
        visibleLineIssueGroups(line) {
            return this.lineIssueGroups(line).slice(0, 3);
        },
        issueGroupsForDate(line, date) {
            const index = this.lineIssueIndex(line);
            return index && index.byDate[date] ? index.byDate[date] : [];
        },
        issueGroupsForWeek(line, week) {
            const index = this.lineIssueIndex(line);
            return index && week && index.byWeek[week.key] ? index.byWeek[week.key] : [];
        },
        issueGroupTooltip(group) {
            const details = (group.items || []).slice(0, 8).map(item => (
                `${item.date ? `${this.shortDate(item.date)}: ` : ''}${item.text}`
            ));
            if ((group.items || []).length > 8) {
                details.push(`Ещё ${(group.items || []).length - 8}…`);
            }
            return [`${group.label} · ${group.count}`, ...details].join('\n');
        },
        issueGroupsTooltip(groups) {
            return (groups || []).map(this.issueGroupTooltip).join('\n\n');
        },
        lineIssueOverflowTooltip(line) {
            return this.issueGroupsTooltip(this.lineIssueGroups(line).slice(3));
        },
        issueGroupAriaLabel(group) {
            return `${group.label}. ${group.count} ${this.pluralize(group.count, 'случай', 'случая', 'случаев')}.`;
        },
        issueGroupsAriaLabel(groups) {
            return (groups || []).map(this.issueGroupAriaLabel).join(' ');
        },
        issueCueClasses(group) {
            const unseenBlocking = group.severity === 'blocking' && (group.items || []).some(item => (
                !this.seenIssueCueKeys[item.key]
            ));
            return {
                'timesheet-issue-cue': true,
                [`timesheet-issue-cue--${group.severity || 'warning'}`]: true,
                'timesheet-issue-cue--unseen': unseenBlocking
            };
        },
        markIssueGroupSeen(group) {
            (group && group.items || []).forEach(item => {
                if (!this.seenIssueCueKeys[item.key]) this.$set(this.seenIssueCueKeys, item.key, true);
            });
        },
        markIssueGroupsSeen(groups) {
            (groups || []).forEach(this.markIssueGroupSeen);
        },
        openIssueGroup(line, group, weekKey) {
            if (!group) return;
            this.closeSourcePopover();
            this.markIssueGroupSeen(group);
            if (['cross_object_overlap', 'excessive_daily_time'].includes(group.type)) {
                this.conflictSelection = {lineId: Number(line.id), dates: Array.from(new Set(group.items.map(item => item.date).filter(Boolean))).sort()};
                return;
            }
            const date = group.first_date;
            if (!date) return;
            const week = this.monthWeeks.find(item => item.days.some(day => day.date === date));
            const targetWeekKey = weekKey || (week && week.key);
            if (targetWeekKey) this.$set(this.collapsedWeekKeys, targetWeekKey, false);
            this.openDay(date);
            this.$nextTick(() => {
                this.focusTimesheetCell(line.id, date);
                const cells = Array.from(this.$el.querySelectorAll(
                    `[data-line-id="${line.id}"][data-work-date="${date}"]`
                ));
                const cell = cells.find(element => element.getClientRects().length) || cells[0];
                if (!cell) return;
                if (typeof cell.scrollIntoView === 'function') {
                    cell.scrollIntoView({block: 'center', inline: 'center', behavior: 'smooth'});
                }
            });
        },
        openCellIssue(line, date, weekKey) {
            const groups = this.issueGroupsForDate(line, date);
            const conflict = groups.find(group => ['cross_object_overlap', 'excessive_daily_time'].includes(group.type));
            this.openIssueGroup(line, conflict || groups[0], weekKey);
        },
        canAssignFromIncident(incident) {
            return Boolean(
                incident
                && incident.assignment_action
                && incident.assignment_action.can_move === true
                && incident.assignment_action.effective_date
            );
        },
        isPrimaryAssignmentAction(line, incident) {
            if (!this.canAssignFromIncident(incident)) return false;
            const first = this.incidentsForDate(line).find(this.canAssignFromIncident);
            return Boolean(first && Number(first.id) === Number(incident.id));
        },
        assignableLineIncident(line) {
            return this.lineLevelIncidents(line).find(this.canAssignFromIncident) || null;
        },
        shortDate(value) {
            const parts = String(value || '').split('-');
            return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : value;
        },
        assignmentActionLabel(incident) {
            const action = incident && incident.assignment_action || {};
            return `Назначить с ${this.shortDate(action.effective_date)}`;
        },
        plannedArrivalIsOverdue(arrival) {
            return Boolean(arrival && arrival.planned_for < localDate());
        },
        arrivalAlreadyInTimesheet(arrival) {
            if (!arrival || !this.sheet) return false;
            return (this.sheet.lines || []).some(line => (
                Number(line.employee_id) === Number(arrival.employee_id)
            ));
        },
        plannedArrivalInitials(arrival) {
            return String(arrival && arrival.employee_name || '')
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map(part => part[0])
                .join('')
                .toLocaleUpperCase('ru-RU');
        },
        async openRequestedPlannedArrival() {
            const query = this.$route.query;
            const key = query.arrival_key;
            if (query.attention !== 'arrivals' || !key || !this.sheet || this.plannedMovePendingId !== null) return;
            if (this.$route.params.timesheetId && Number(this.$route.params.timesheetId) !== Number(this.sheet.timesheet.id)) return;
            if (query.object_id && Number(query.object_id) !== Number(this.sheet.object.building_id)) return;
            if (query.month !== this.sheet.period.starts_on.slice(0, 7)) return;
            const request = `${this.sheet.timesheet.id}:${key}`;
            if (this.handledArrivalRequest === request) return;
            this.handledArrivalRequest = request;
            this.plannedArrivalLinkMessage = '';
            this.plannedArrivalsExpanded = true;
            const arrival = this.plannedArrivals.find(item => item.key === key);
            if (!arrival) {
                this.plannedArrivalLinkMessage = 'Это прибытие уже подтверждено или план изменён. Проверьте актуальный список прибытий.';
            } else if (!this.canEdit || !arrival.can_confirm) {
                this.plannedArrivalLinkMessage = arrival.confirmation_reason || 'Подтверждение прибытия сейчас недоступно: нет права изменения или табель закрыт для редактирования.';
            } else {
                this.confirmPlannedArrival(arrival);
            }
            // Consume the action so closing the modal or refreshing the page cannot reopen it.
            const nextQuery = {...query};
            delete nextQuery.arrival_key;
            await this.syncPeriodRoute({name: this.$route.name, params: this.$route.params, query: nextQuery});
        },
        confirmPlannedArrival(arrival) {
            if (
                !arrival ||
                !arrival.can_confirm ||
                !this.canEdit ||
                this.plannedMovePendingId !== null || this.editPendingCount > 0
            ) return;
            this.plannedArrivalModalItem = arrival;
            this.hoursArrivalBlocked = false;
            const draft = this._arrivalDrafts.get(`planned:${arrival.key}`) || {};
            this.plannedArrivalEffectiveDate = draft.arrival_date || this.plannedArrivalDateMax || localDate();
            this.arrivalDiscrepancyReason = draft.reason || '';
            this.arrivalDiscrepancyAccepted = false;
            this.plannedArrivalModalError = '';
            this.plannedArrivalModalVisible = true;
            this.plannedArrivalReserveError = false;
        },
        confirmPlannedArrivalFromAdd(arrival) {
            if (!arrival || !arrival.can_confirm || this.plannedMovePendingId !== null || this.editPendingCount > 0) return;
            this.pendingPlannedArrivalFromAdd = arrival;
            this.addPersonModalVisible = false;
        },
        resetPlannedArrivalConfirmation() {
            if (!this.hoursArrivalRequirement && this.plannedArrivalModalItem && !this._standaloneArrivalSaved) {
                this._arrivalDrafts.set(`planned:${this.plannedArrivalModalItem.key}`, {
                    arrival_date: this.plannedArrivalEffectiveDate, reason: this.arrivalDiscrepancyReason
                });
            }
            this._standaloneArrivalSaved = false;
            this.cancelHoursArrival();
            this.hoursArrivalRequirement = null;
            this.hoursArrivalOperation = 'entries';
            this.standaloneArrivalRequirements = [];
            this.plannedArrivalModalItem = null;
            this.plannedArrivalEffectiveDate = localDate();
            this.plannedArrivalModalError = '';
            this.plannedArrivalConfirmationWarning = null;
            this.plannedArrivalReserveError = false;
        },
        async submitPlannedArrivalConfirmation() {
            const arrival = this.plannedArrivalModalItem;
            if (
                !arrival ||
                !this.plannedArrivalDateIsValid ||
                this.plannedMovePendingId !== null
            ) return;
            if (this.hoursArrivalRequirement) { this.acceptHoursArrival(); return; }
            if (this.hoursArrivalAffected.length && (!this.arrivalDiscrepancyReason.trim() || !this.arrivalDiscrepancyAccepted)) {
                this.plannedArrivalModalError = 'Объясните расхождение и явно подтвердите его.';
                return;
            }
            this.plannedMovePendingId = arrival.key || arrival.id;
            this.errorMessage = '';
            this.plannedArrivalModalError = '';
            try {
                await api.post(
                    `/api/timesheets/${this.sheet.timesheet.id}/planned-arrivals/confirm`,
                    {
                        source_type: arrival.source_type,
                        employee_id: arrival.employee_id,
                        interval_id: arrival.interval_id,
                        planned_move_id: arrival.source_type === 'planned_move' ? arrival.id : undefined,
                        arrival_date: this.plannedArrivalEffectiveDate,
                        arrival_discrepancies: this.standaloneArrivalRequirements.map(item => ({warning_token: item.warning_token,
                            reason: this.arrivalDiscrepancyReason, accept_discrepancy: this.arrivalDiscrepancyAccepted})),
                        confirm_plan_mismatch: Boolean(this.plannedArrivalConfirmationWarning),
                        confirm_fact_replacement: Boolean(this.plannedArrivalConfirmationWarning),
                        expected_state_token: arrival.workforce_state_token,
                        expected_state_date: arrival.workforce_state_date
                    },
                    {ignoreModal: true}
                );
                await this.syncAssignedRoster({force: true, notify: false, reload: true});
                this.successMessage = `${arrival.employee_name}: прибытие подтверждено ` +
                    `${this.shortDate(this.plannedArrivalEffectiveDate)}.`;
                this._standaloneArrivalSaved = true;
                this._arrivalDrafts.delete(`planned:${arrival.key}`);
                this.plannedArrivalModalVisible = false;
            } catch (error) {
                const responseData = error && error.response && error.response.data;
                this.plannedArrivalReserveError = Boolean(responseData && responseData.code === 'RESERVE_EMPLOYEE_CANNOT_BE_ASSIGNED');
                if (responseData && responseData.code === 'TIMESHEET_ARRIVAL_CONFIRMATION_REQUIRED') {
                    this.standaloneArrivalRequirements = responseData.details.requirements;
                    return;
                }
                if (
                    responseData
                    && responseData.code === 'WORKFORCE_PLAN_ARRIVAL_CONFIRMATION_REQUIRED'
                ) {
                    const details = responseData.details || {};
                    this.plannedArrivalConfirmationWarning = {
                        plan_mismatch: Boolean(details.plan_mismatch),
                        fact_mismatch: Boolean(details.fact_mismatch)
                    };
                    return;
                }
                this.plannedArrivalModalError = this.responseMessage(
                    error,
                    'Не удалось подтвердить прибытие сотрудника.'
                );
                await this.loadTimesheet(this.sheet.timesheet.id).catch(() => {});
            } finally {
                this.plannedMovePendingId = null;
            }
        },
        async assignEmployeeFromIncident(line, incident) {
            if (!this.canAssignFromIncident(incident) || this.assigningIncidentId !== null) {
                return;
            }
            const action = incident.assignment_action;
            const previousPlace = action.changes_primary_assignment
                && action.previous_assignment_name
                ? ` Сейчас основное место сотрудника — «${action.previous_assignment_name}»; оно будет изменено.`
                : '';
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Назначить ${line.employee_name_snapshot} на объект «${this.sheet.object.name}» ` +
                `с ${this.shortDate(action.effective_date)}?${previousPlace}`,
                {
                    title: 'Назначить сотрудника на объект',
                    okTitle: 'Назначить',
                    cancelTitle: 'Отмена',
                    okVariant: 'warning'
                }
            );
            if (!confirmed) return;
            this.assigningIncidentId = Number(incident.id);
            this.errorMessage = '';
            try {
                await api.post(
                    `/api/timesheets/${this.sheet.timesheet.id}/incidents/${incident.id}/assign-employee`,
                    {effective_date: action.effective_date}
                );
                await this.loadTimesheet(this.sheet.timesheet.id);
                this.successMessage = `Сотрудник назначен на объект с ${
                    this.shortDate(action.effective_date)
                }.`;
            } catch (error) {
                this.errorMessage = this.responseMessage(
                    error,
                    'Не удалось назначить сотрудника на объект.'
                );
                await this.loadTimesheet(this.sheet.timesheet.id).catch(() => {});
            } finally {
                this.assigningIncidentId = null;
            }
        },
        hasExternalEntry(line, date) {
            return this.externalFor(line, date).length > 0;
        },
        monthCellDisplay(line, date) {
            const value = this.entryDisplay(this.entryFor(line, date));
            return value || (this.hasExternalEntry(line, date) ? 'Дт' : '');
        },
        monthCellClasses(line, day) {
            const incidentGroups = this.issueGroupsForDate(line, day.date);
            const absence = this.absenceDayFor(line, day.date);
            return Object.assign({
                'is-weekend': day.is_weekend,
                'is-selected': day.date === this.selectedDate,
                'has-external': this.hasExternalEntry(line, day.date),
                'has-incident': incidentGroups.some(group => group.severity !== 'info'),
                'has-absence-suggestion': absence && absence.state === 'suggestion',
                'has-absence-discrepancy': absence && [
                    'conflict', 'source_changed', 'source_removed', 'overlap',
                    'configuration_error'
                ].includes(absence.state),
                'is-saving': Boolean(this.editStates[this.cellKey(line.id, day.date)] && this.editStates[this.cellKey(line.id, day.date)].pending),
                'is-save-error': Boolean(this.editStates[this.cellKey(line.id, day.date)] && this.editStates[this.cellKey(line.id, day.date)].error)
            }, this.assignmentCellClasses(line, day.date), this.reviewSourceCellClass(line, day.date));
        },
        monthCellTitle(line, date) {
            const parts = [];
            parts.push(...this.issueGroupsForDate(line, date).map(this.issueGroupTooltip));
            const assignmentTitle = this.assignmentDayTitle(line, date);
            if (assignmentTitle) parts.push(assignmentTitle);
            const absence = this.absenceDayFor(line, date);
            if (absence) parts.push(this.absenceStateLabel(absence));
            return parts.join('\n');
        },
        weekLineMinutes(line, week) {
            return (week.days || []).reduce((sum, day) => {
                const entry = this.entryFor(line, day.date);
                return sum + Number(entry && entry.worked_minutes || 0);
            }, 0);
        },
        weekMinutes(week) {
            const totals = this.minuteTotalsByDate;
            return week.days.reduce((sum, day) => sum + (totals[day.date] || 0), 0);
        },
        weekHasIssue(line, week) {
            return this.issueGroupsForWeek(line, week).some(group => group.severity !== 'info');
        },
        weekCellTitle(line, week) {
            const issues = (week.days || []).reduce((result, day) => {
                const title = this.monthCellTitle(line, day.date);
                if (title) result.push(`${day.day}: ${title}`);
                return result;
            }, []);
            const hours = this.minutesLabel(this.weekLineMinutes(line, week));
            return [`${week.label}, ${week.range}: ${hours} ч.`, ...issues].join('\n');
        },
        monthColumnClasses(line, column) {
            if (column.type === 'day') return this.monthCellClasses(line, column);
            const groups = this.issueGroupsForWeek(line, column.week);
            return {
                'is-week-summary': true,
                'has-incident': groups.some(group => group.severity !== 'info'),
                'has-external': groups.some(group => group.type === 'cross_object_overlap')
            };
        },
        monthColumnTitle(line, column) {
            return column.type === 'day'
                ? this.monthCellTitle(line, column.date)
                : this.weekCellTitle(line, column.week);
        },
        lineMonthMinutes(line) {
            if (line.totals && line.totals.worked_minutes !== undefined) {
                return Number(line.totals.worked_minutes);
            }
            return (line.entries || []).reduce((sum, entry) => sum + Number(entry.worked_minutes || 0), 0);
        },
        lineWorkDays(line) {
            if (line.totals && line.totals.equivalent_days !== undefined) {
                return Number(line.totals.equivalent_days);
            }
            const settings = this.bootstrapData.policy.day_counting || {};
            const fullDayMinimum = Number(settings.full_day_minimum_minutes || 300);
            const partialDayValue = Number(settings.partial_day_value === undefined ? 0.5 : settings.partial_day_value);
            const travelOnlyDayValue = Number(settings.travel_only_day_value === undefined ? 0.5 : settings.travel_only_day_value);
            const travelDayCodes = Array.isArray(settings.travel_day_codes)
                ? settings.travel_day_codes.map(String)
                : ['Д'];
            return (line.entries || []).reduce((sum, entry) => {
                const minutes = Number(entry.worked_minutes || 0);
                if (minutes >= fullDayMinimum) return sum + 1;
                if (minutes > 0) return sum + partialDayValue;
                const travel = entry.travel_code_id
                    ? this.codeById[Number(entry.travel_code_id)]
                    : null;
                return sum + (travel && travelDayCodes.includes(String(travel.code))
                    ? travelOnlyDayValue
                    : 0);
            }, 0);
        },
        lineAverageHours(line) {
            if (line.totals && line.totals.average_minutes !== undefined) {
                return this.minutesLabel(line.totals.average_minutes);
            }
            const days = this.lineWorkDays(line);
            return days ? this.minutesLabel(this.lineMonthMinutes(line) / days) : '0';
        },
        dayMinutes(date) {
            return this.minuteTotalsByDate[date] || 0;
        },
        characteristicSelectOptions(field) {
            return [{value: '', text: '—'}].concat((field.options || []).map(option => ({
                value: option,
                text: option
            })));
        },
        characteristicDisplay(line, field) {
            const value = line.characteristic_values && line.characteristic_values[field.key];
            if (value === true) return 'Да';
            if (value === false) return 'Нет';
            return value || '—';
        },
        formatKtu(value) {
            if (value === null || value === undefined || value === '') return '—';
            return String(Number(value)).replace('.', ',');
        },
        ktuClass(value) {
            const number = Number(value);
            if (!Number.isFinite(number) || value === '') return '';
            const minimum = Number(this.ktuConfig.minimum);
            const maximum = Number(this.ktuConfig.maximum);
            const ratio = maximum > minimum ? (number - minimum) / (maximum - minimum) : 1;
            if (ratio < 0.34) return 'is-low';
            if (ratio < 0.67) return 'is-medium';
            return 'is-high';
        },
        minutesLabel(minutes) {
            return timesheetHours.minutesLabel(minutes);
        },
        lifecycleLabel(status) {
            return {
                active: 'Действующий',
                reserve: 'Резерв',
                unverified: 'Не проверен'
            }[status] || '';
        },
        statusText(status) {
            return {
                draft: 'Черновик',
                missing: 'Не подан',
                no_data: 'Нет данных за месяц',
                not_created: 'Табель не создан',
                returned: 'Возвращён',
                hr_editing: 'Исправляет табельщик',
                submitted: 'На проверке',
                approved: 'Утверждён',
                historical_locked: 'Исторический',
                pilot_closed: 'Не используется',
                annulled: 'Аннулирован'
            }[status] || 'Неизвестный статус';
        },
        packageExclusionText(item) {
            const rule = item.package_exclusion || {};
            const month = value => value ? value.split('-').reverse().join('.') : '';
            const duration = rule.through_month
                ? `Временно: ${month(rule.from_month)} — ${month(rule.through_month)} включительно.`
                : `Постоянно с ${month(rule.from_month)}.`;
            return `${duration} Не входит в начисление ЗП.${rule.reason ? ' Причина: ' + rule.reason : ''}`;
        },
        reviewStatusVariant(status) {
            return {
                draft: 'secondary',
                returned: 'warning',
                hr_editing: 'warning',
                submitted: 'primary',
                approved: 'success',
                missing: 'danger',
                annulled: 'danger',
                historical_locked: 'dark',
                pilot_closed: 'dark'
            }[status] || 'secondary';
        },
        schedulePeopleSearch(value) {
            const query = String(value || '').trim();
            if (this.peopleSearchTimer) clearTimeout(this.peopleSearchTimer);
            this.peopleSearchTimer = null;
            this.peopleSearchRequestId += 1;
            this.peopleSearchCompletedQuery = '';
            this.peopleSearchError = '';
            if (query.length < 2) {
                this.peopleSearching = false;
                this.peopleResults = {
                    employees: [],
                    unresolved_people: [],
                    existing_unresolved: null
                };
                return;
            }
            this.peopleSearching = true;
            this.peopleSearchTimer = setTimeout(() => {
                this.peopleSearchTimer = null;
                this.searchPeople(query);
            }, 300);
        },
        searchPeopleNow() {
            const query = String(this.peopleQuery || '').trim();
            if (query.length < 2) return;
            if (this.peopleSearchTimer) clearTimeout(this.peopleSearchTimer);
            this.peopleSearchTimer = null;
            this.searchPeople(query);
        },
        async searchPeople(queryValue) {
            const query = String(queryValue || this.peopleQuery || '').trim();
            if (query.length < 2) return;
            const requestId = ++this.peopleSearchRequestId;
            this.peopleSearching = true;
            this.errorMessage = '';
            this.peopleSearchError = '';
            try {
                const response = await api.get(
                    `/api/timesheets/${this.sheet.timesheet.id}/people/search`,
                    {params: {q: query}}
                );
                if (requestId !== this.peopleSearchRequestId) return;
                this.peopleResults = response.data;
                this.peopleSearchCompletedQuery = query;
                this.unresolvedForm.full_name = query;
            } catch (error) {
                if (requestId !== this.peopleSearchRequestId) return;
                this.peopleSearchCompletedQuery = query;
                this.peopleSearchError = this.responseMessage(
                    error,
                    'Не удалось выполнить поиск. Попробуйте ещё раз.'
                );
            } finally {
                if (requestId === this.peopleSearchRequestId) {
                    this.peopleSearching = false;
                }
            }
        },
        openRequestedUnresolvedResolver() {
            const lineId = Number(this.$route.query && this.$route.query.resolve_unresolved_line);
            if (!lineId) return;
            const line = this.unresolvedLines.find(item => Number(item.id) === lineId);
            if (line && this.canResolveUnresolved) this.openUnresolvedResolver(line);
        },
        openUnresolvedResolver(line) {
            if (!line || !line.unresolved_person_id) return;
            if (!this.canResolveUnresolved) {
                this.errorMessage = 'Сопоставление доступно только пока табель открыт для редактирования.';
                return;
            }
            this.unresolvedResolverLineId = Number(line.id);
            this.unresolvedResolverVisible = true;
        },
        async handleUnresolvedResolved(result) {
            this.unresolvedResolverVisible = false;
            this.unresolvedResolverLineId = null;
            await this.loadTimesheet(this.sheet.timesheet.id);
            this.successMessage = `Строки объединены: ${result.employee_name}. Исходные данные сохранены в истории.`;
        },
        async undoUnresolvedMerge(merge) {
            if (!this.canResolveUnresolved || this.unresolvedUndoPendingId !== null) return;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Вернуть отдельную строку «${merge.unresolved_name}» и восстановить данные до объединения?`,
                {
                    title: 'Отменить объединение строк',
                    okTitle: 'Да, отменить объединение',
                    cancelTitle: 'Не отменять',
                    okVariant: 'danger'
                }
            );
            if (!confirmed) return;
            this.unresolvedUndoPendingId = Number(merge.audit_event_id);
            this.errorMessage = '';
            try {
                await api.post(
                    `/api/timesheets/${this.sheet.timesheet.id}/unresolved-merges/${merge.audit_event_id}/undo`
                );
                await this.loadTimesheet(this.sheet.timesheet.id);
                this.successMessage = 'Объединение отменено. Ручная строка снова требует сопоставления.';
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось отменить объединение.');
            } finally {
                this.unresolvedUndoPendingId = null;
            }
        },
        canRemoveManualLine(line) {
            return Boolean(this.canEdit && line && !line.employee_id && line.can_remove_manual === true
                && !(line.entries || []).some(entry => Number(entry.worked_minutes) > 0 || entry.absence_code_id || entry.travel_code_id || entry.other_code_id || String(entry.note || '').trim()));
        },
        openExistingUnresolvedFromSearch() {
            const existing = this.peopleResults.existing_unresolved;
            if (!existing) return;
            const line = this.unresolvedLines.find(item => (
                Number(item.unresolved_person_id) === Number(existing.id)
            ));
            this.resetPersonForm();
            if (line) this.$nextTick(() => this.openUnresolvedResolver(line));
        },
        resetPersonForm() {
            this.resetPersonAddition();
            if (this.peopleSearchTimer) clearTimeout(this.peopleSearchTimer);
            this.peopleSearchTimer = null;
            this.peopleSearchRequestId += 1;
            this.peopleQuery = '';
            this.peopleSearching = false;
            this.peopleSearchCompletedQuery = '';
            this.peopleSearchError = '';
            this.peopleResults = {employees: [], unresolved_people: [], existing_unresolved: null};
            this.addReason = '';
            this.addPersonError = '';
            this.showUnresolvedForm = false;
            this.unresolvedForm = {full_name: '', phone: ''};
            this.matchCandidates = null;
            this.addPersonModalVisible = false;
        },
        handleAddPersonModalHidden() {
            const arrival = this.pendingPlannedArrivalFromAdd;
            this.pendingPlannedArrivalFromAdd = null;
            this.resetPersonForm();
            if (arrival) this.$nextTick(() => this.confirmPlannedArrival(arrival));
        },
        async loadActivity(options = {}) {
            const timesheetId = this.sheet && this.sheet.timesheet && this.sheet.timesheet.id;
            if (!timesheetId) {
                this.activityItems = [];
                return;
            }
            this.activityLoading = true;
            try {
                const response = await api.get(`/api/timesheets/${timesheetId}/activity`, {
                    params: {limit: 300}, ignoreModal: options.ignoreModal === true
                });
                if (Number(timesheetId) !== this.currentTimesheetId() || options.isCurrent && !options.isCurrent()) return;
                this.activityItems = Array.isArray(response.data) ? response.data : [];
            } catch (error) {
                if (Number(timesheetId) !== this.currentTimesheetId() || options.isCurrent && !options.isCurrent()) return;
                if (options.throwOnError) throw error;
                this.errorMessage = this.responseMessage(error, 'Не удалось загрузить комментарии и историю.');
            } finally {
                if (Number(timesheetId) === this.currentTimesheetId()) this.activityLoading = false;
            }
        },
        async createActivityComment(body) {
            this.activityPending = true;
            try {
                await api.post(`/api/timesheets/${this.sheet.timesheet.id}/comments`, {body});
                await this.loadActivity();
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось добавить комментарий.');
            } finally {
                this.activityPending = false;
            }
        },
        async editActivityComment(payload) {
            this.activityPending = true;
            try {
                await api.patch(
                    `/api/timesheets/${this.sheet.timesheet.id}/comments/${payload.item.comment_id}`,
                    {body: payload.body}
                );
                await this.loadActivity();
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось изменить комментарий.');
            } finally {
                this.activityPending = false;
            }
        },
        async deleteActivityComment(item) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                'Удалить комментарий? В ленте останется отметка об удалении.',
                {title: 'Удалить комментарий', okTitle: 'Удалить', cancelTitle: 'Отмена', okVariant: 'danger'}
            );
            if (!confirmed) return;
            this.activityPending = true;
            try {
                await api.delete(`/api/timesheets/${this.sheet.timesheet.id}/comments/${item.comment_id}`);
                await this.loadActivity();
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось удалить комментарий.');
            } finally {
                this.activityPending = false;
            }
        },
        openReviewCorrection() {
            this.correctionReason = '';
            this.correctionModalVisible = true;
        },
        async confirmReviewCorrection(event) {
            if (event && event.preventDefault) event.preventDefault();
            const reason = this.correctionReason.trim();
            if (!reason) return;
            this.reviewActionPending = true;
            try {
                await api.post(`/api/timesheets/${this.sheet.timesheet.id}/review-correction`, {reason});
                await this.loadTimesheet(this.sheet.timesheet.id);
                this.closeEditSession();
                this.editSessionKind = 'hr-correction';
                this.successMessage = 'Табель открыт для самостоятельного исправления табельщиком.';
                this.correctionModalVisible = false;
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось открыть исправление табеля.');
            } finally {
                this.reviewActionPending = false;
            }
        },
        setEmptyLines(value) { this.$set(this.sheet, 'empty_lines', value); },
        locateEmptyLine(id) { const node = this.$el.querySelector(`[data-review-line-id="${Number(id)}"]`); if (node) node.scrollIntoView({behavior: 'smooth', block: 'center'}); },
        async reloadAfterEmptyLines() { await this.loadTimesheet(this.sheet.timesheet.id, {syncRoute: false, ignoreModal: true}); this.emptySubmitAttempt = false; },
        async showTextQualityError(error, sheetId) {
            const issues = error.response && error.response.data && error.response.data.details && error.response.data.details.issues || [];
            const qualityIssues = issues.filter(issue => issue.type === 'invalid_characteristic_text');
            if (!qualityIssues.length || !this.sheet || this.sheet.timesheet.id !== sheetId) return;
            this.$set(this.bootstrapData.policy, 'text_quality_enabled', true);
            this.submitActionError = qualityIssues.map(issue => `${issue.employee_name}, ${issue.field_label}: ${issue.message} ${issue.help}`).join('\n');
            try { await this.loadTimesheet(sheetId, {syncRoute: false, ignoreModal: true}); } catch (_) { /* Keep field-level feedback and entered values. */ }
        },
        async submitTimesheet() {
            if (this.submitting) return;
            const submitSheetId = this.sheet.timesheet.id;
            this.submitActionError = '';
            if (this.textQualityIssues.length) {
                this.submitActionError = 'Исправьте характеристики и пояснения КТУ. У каждого проблемного поля показаны причина и способ исправления.';
                return;
            }
            this.submitting = true;
            try {
                if (!await this.flushTimesheetEdits()) throw Error('Сначала сохраните изменения и исправьте ошибки сохранения.');
                const response = await api.get(`/api/timesheets/${this.sheet.timesheet.id}/empty-lines-preview`, {ignoreModal: true});
                if (!this.sheet || this.sheet.timesheet.id !== submitSheetId) return;
                this.setEmptyLines(response.data);
                if (response.data.count) { this.emptySubmitAttempt = true; return; }
            } catch (e) { this.submitActionError = this.responseMessage(e, e.message || 'Не удалось проверить пустые строки.'); return; } finally { this.submitting = false; }

            if (this.unresolvedLines.length) {
                this.errorMessage = 'Табель нельзя отправить: сначала сопоставьте всех людей без карточки сотрудника.';
                this.$nextTick(() => {
                    const element = document.querySelector('.timesheet-unresolved-blocker');
                    if (element) element.scrollIntoView({behavior: 'smooth', block: 'center'});
                });
                return;
            }
            if (!this.assessmentIsReady) {
                this.assessmentReadinessForced = true;
                this.errorMessage = 'До отправки заполните КТУ и характеристики. Ниже показано, кого нужно дополнить.';
                this.openFirstIncompleteAssessment();
                this.$nextTick(() => {
                    const element = document.getElementById('timesheet-assessment-readiness');
                    if (element) element.scrollIntoView({behavior: 'smooth', block: 'center'});
                });
                return;
            }
            const confirmed = await this.$bvModal.msgBoxConfirm(
                'После отправки текущая ревизия станет неизменяемой. Продолжить?',
                {title: 'Отправить табель', okTitle: 'Отправить', cancelTitle: 'Отмена'}
            );
            if (!confirmed) return;
            this.submitting = true;
            try {
                await api.post(`/api/timesheets/${this.sheet.timesheet.id}/submit`, {
                    characteristic_omission_reason: null
                });
                this.closeEditSession();
                await this.loadTimesheet(this.sheet.timesheet.id);
                this.successMessage = 'Табель отправлен на проверку.';
            } catch (error) {
                this.submitActionError = this.responseMessage(error, 'Не удалось отправить табель.');
                await this.showTextQualityError(error, submitSheetId);
                if (error.response && error.response.data && error.response.data.code === 'TIMESHEET_EMPTY_LINES') { this.setEmptyLines(error.response.data.details); this.emptySubmitAttempt = true; }
            } finally {
                this.submitting = false;
            }
        },
        returnTimesheet() {
            this.returnReason = '';
            this.returnDueDate = '';
            this.returnModalVisible = true;
        },
        async confirmReturnTimesheet(event) {
            if (event && event.preventDefault) event.preventDefault();
            const reason = this.returnReason.trim();
            if (!reason) return;
            this.reviewActionPending = true;
            try {
                await api.post(`/api/timesheets/${this.sheet.timesheet.id}/return`, {reason, resubmission_due_date: this.returnDueDate});
                await this.loadTimesheet(this.sheet.timesheet.id);
                this.successMessage = 'Табель возвращён автору.';
                this.returnModalVisible = false;
            } catch (error) {
                this.errorMessage = this.responseMessage(error, 'Не удалось вернуть табель.');
            } finally {
                this.reviewActionPending = false;
            }
        },
        async approveTimesheet() {
            const approveSheetId = this.sheet.timesheet.id;
            this.submitActionError = '';
            if (this.textQualityIssues.length) {
                this.submitActionError = 'Исправьте характеристики и пояснения КТУ перед утверждением новой редакции.';
                this.openTextQualityIssue(this.textQualityIssues[0]);
                return;
            }
            if (!this.assessmentIsReady) {
                this.errorMessage = 'Табель нельзя утвердить: не заполнены КТУ или характеристики.';
                this.openFirstIncompleteAssessment();
                return;
            }
            const confirmed = await this.$bvModal.msgBoxConfirm(
                'Утвердить текущую ревизию табеля?',
                {title: 'Утверждение табеля', okTitle: 'Утвердить', cancelTitle: 'Отмена'}
            );
            if (!confirmed) return;
            this.reviewActionPending = true;
            try {
                await api.post(`/api/timesheets/${this.sheet.timesheet.id}/approve`, {
                    characteristic_omission_reason: null
                });
                await this.loadTimesheet(this.sheet.timesheet.id);
                this.successMessage = 'Табель утверждён.';
            } catch (error) {
                this.submitActionError = this.responseMessage(error, 'Не удалось утвердить табель.');
                await this.showTextQualityError(error, approveSheetId);
            } finally {
                this.reviewActionPending = false;
            }
        }
    }
};
</script>

<style src="./timesheet-workspace.css"></style>
<style src="./timesheet-layout.css"></style>
<style src="./timesheet-review-controls.css"></style>

<style scoped src="./timesheet-status-badges.css"></style>
